// PUTTING AN ASSEMBLY ON SCREEN: the three.js half of the step viewer.
//
// The meshes are the real ones — the Parts DB's curated model per catalog item, which is
// what the Blender add-on pulls into a .blend and pushes back. Same bytes, same
// /api/notice/3d/model/<job> the rest of the app draws, so a part looks here exactly as it
// looks in the phase that made it.
//
// Deliberately not mountMesh. That one exists to judge a SINGLE generated mesh: it frames
// the camera off that mesh's own bounding sphere, measures it, and reports which texel the
// pointer is over — all questions about one object. An assembly is the opposite question,
// and the reframing especially is not a feature here but the bug: the camera must hold
// still while parts move through it, or nothing can be seen to move at all.
//
// What the two DO share is everything that is not about how many objects there are, and it
// is shared as code rather than as a resemblance: `loadDeps` so three.js is imported once,
// `makeLighting` for even against studio, `attachDrawModes` for textured against wireframe.
// Those are claims about what is on screen — "even" means a surface renders at its own
// albedo — and two viewers with their own answer would be two answers to one question.
//
// What is still duplicated is the stage: renderer, resize observer, render loop. Forty
// lines, and if this grows past the one fitting it draws today they should be lifted out of
// mountMesh too rather than left as two copies drifting apart.

import { measureEnds, type Ends } from './meshMeasure';
import { IS_SANDBOX } from '../../apiBase';
import { poseAt, type Assembly, type Placement } from './assemblyPose';
import { WIRE_PALETTE, wireColours } from './wireColours';
import {
  clashParts, faceVertex, type ClashMarks, type ClashPart, type ClashProfile, type ClashSource,
  type StandingOverlap,
} from './assemblyClash';
import { clashRemote, type ClashRemote } from './assemblyClashRemote';
import { getMeshView, setMeshView } from './meshView';
import { faceNormal, pointOnFace } from './partFaces';
import type { HoleMark } from './drillPreview';
import { loadCopy, loadFit, placeFit, slide, type Sliding } from './holeFit';
import { sampleProfile } from './partProfileSample';
import { EXPLODE_MS, explodeShifts, smoothstep } from './explodePieces';
import { makeDimensionLines, type DimensionLineSpec } from './dimensionLines';
import type { PartProfile } from './partProfile';
import {
  AXIS_COLOURS, DRAW_ORDER, holdLabels, makeExtentDims, makeWorldGizmo, MM_PER_TRUE_UNIT, mmScale,
  type Dims, type ExtentDims, type FacesTold, type FaceWords,
} from './meshAnnotations';
import { attachDrawModes, type DrawMode } from './meshDrawModes';
import { loadDeps, makeLighting, releaseContext, type LightMode } from './meshScene';
import { tallyRenderer } from './gpuTally';
import { borrowRenderer, returnRenderer } from './sharedRenderer';
import { meshArriving } from './meshPreload';
import { serverLog } from './serverLog';

// THE GIZMO'S ARM, in the scene's millimetres, and it is a LADDER rather than a length.
//
// One centimetre is what a hand knows without converting, and it was the whole of it — which
// worked while the shot was tight on one part and disappeared the moment it was not. A chair
// is 750 mm; framed whole in a wide viewport it draws about 120 px tall, and a centimetre of
// that is a pixel and a half. The gizmo was still there and still correct and could not be
// seen, which is the worst of the three.
//
// So it climbs: a millimetre, two, five, a centimetre, and on up — whichever rung comes out
// closest to a legible size on screen at the distance the camera is standing. Never an
// arbitrary "keep it 80 px" scale factor, because the arm is a MEASUREMENT — something to
// hold a part up against — and a ruler whose divisions are whatever the zoom left them at
// measures nothing. Which rung it is on is written on its arms, so the picture always says
// what its own scale is.
//
// THE ARM IS THE RUNG, AND THAT IS THE WHOLE OF IT. It spans exactly the length written on
// it, in the same world the parts are in, so it grows and shrinks with them as you zoom — and
// steps down a rung when it outgrows its corner. What is fixed is the DIVISION, not the
// drawing: the number on the arm is chosen from this ladder and the arm is then drawn that
// long, rather than the arm being drawn one length and the ladder asked what it is worth.
const GIZMO_STEPS = [
  // A MILLIMETRE, which the ladder used to start a decade above. The bottom rung is drawn
  // whatever happens — there being nothing shorter to fall back to — so a centimetre at the
  // bottom meant a barrel nut 12 mm across wore an arm as long as itself, with the words on
  // it wider than the part. A grub screw, a washer and a dowel are all read at this end, and
  // for those a millimetre is what a hand knows.
  //
  // 1, 2, 5 AND NOT DECADES ALONE, which is what this was. The rungs are what the arm's
  // length JUMPS BY when it changes rung, and decades jump by ten: the arm grew tenfold
  // across a rung and then fell to a tenth of itself in one frame, which is a lurch rather
  // than a legend. Every ruler ever printed uses 1-2-5 for the same reason, and every one of
  // these is still a number a hand knows — the objection the decades were chosen against was
  // to an arbitrary "keep it 80 px" factor, and 1-2-5 is not one.
  // AND BELOW IT, WHERE NOBODY IS MEASURING FURNITURE. These three exist so the ladder has a
  // FLOOR low enough never to be stood on: a rung with nothing under it cannot fall back, so
  // the arm just grows, and the corner inset — which is sized for the longest arm the ladder
  // allows — would be overrun. `minDistance` lets the camera to within about 3 mm of a dowel
  // (see the framing, where it is the part's own half-extent plus a near plane), and a 1 mm
  // arm at 3 mm is five times the size it is meant to be, straight off the edge of the
  // picture. A tenth of a millimetre is not a division anybody wants; it is the guarantee that
  // the divisions above it are always the ones drawn.
  { mm: 0.1, label: '0.1 mm' },
  { mm: 0.2, label: '0.2 mm' },
  { mm: 0.5, label: '0.5 mm' },
  { mm: 1, label: '1 mm' },
  { mm: 2, label: '2 mm' },
  { mm: 5, label: '5 mm' },
  { mm: 10, label: '1 cm' },
  { mm: 20, label: '2 cm' },
  { mm: 50, label: '5 cm' },
  { mm: 100, label: '10 cm' },
  { mm: 200, label: '20 cm' },
  { mm: 500, label: '50 cm' },
  { mm: 1000, label: '1 m' },
];

// THE WIDGET'S OWN NUMBERS, now that the arms are pinned to a corner rather than standing in
// the picture.
//
// There used to be a ceiling here — the arm could grow to a quarter of the viewport's short
// side and no further — and a hysteresis band to stop it flickering between rungs. Both were
// answers to a question that no longer exists: an arm that stands in the scene competes with
// the thing it measures, and one that grew past its welcome had to be caught. A box in the
// corner cannot overflow anything, so what is left is a length and a distance.
//
// THE ARM IS THE MEASUREMENT AGAIN, which is what these numbers are for now.
//
// It was not, for a while, and the comment that stood here said so without noticing the cost:
// the arm was a FIXED length on screen and the rung was chosen to say what that length
// happened to be worth. Which is only true at a rung boundary. Nearest-in-log over a decade
// ladder puts the rung within half a decade of the truth, and the hysteresis below widened
// that to three quarters — so an arm reading "1 cm" was drawn at every camera distance from
// 36 mm to 364 mm, and across that range it was really worth anywhere from 3.2 mm to 32 mm.
// A ruler wrong by a factor of three is not a ruler, and it was the reason the arms could not
// change size as you zoomed: they were pinned, and the only thing left free to move was the
// number written on them.
//
// So it is the other way round again, as it was when the arms stood at the origin. The arm
// spans exactly its rung — the group is scaled by (rung / AXIS_LEN) in a holder at world
// scale, so an arm labelled 1 cm is one centimetre of the same world the parts are in. Zoom
// in and it grows with them, continuously, because it is one of them; it snaps back a rung
// when it outgrows the corner, which is a step exactly where a step means something.
//
// HUD_ARM and HUD_REF now define the arm's TARGET size on screen rather than its actual one:
// the rung chosen is the one landing nearest (HUD_ARM/HUD_REF) of the frustum, which is the
// size the arms have always been drawn at, so the widget looks as it did at rest and only
// breathes between rungs.
//
// HUD_EDGE is gone. The arm no longer has one size, so an inset measured as a share of the
// viewport would crop the long end. The origin is pinned at a fixed number of pixels from the
// corner instead — exactly enough for the longest arm the ladder can produce (see
// HUD_RUNG_MAX) — so the reference point holds still, the arrows grow and shrink around it,
// and the longest of them very nearly touches the edge rather than stopping short of it.
// AND THE COLOUR OF BEING WRONG, which is not one of them. The same red the clearance graph
// draws an overlap in, so the picture and the plot are saying one thing.
const CLASH_COLOUR = 0xef4444;

// How many triangles of red one part may carry — the same bound the measurement stops at (see
// MAX_MARKS), reserved once so a frame of playback rewrites a buffer instead of building one.
const CLASH_FACES = 4000;

const HUD_ARM = 1;
// THE TWO OF THEM ARE ONE KNOB: their ratio is the size the arms aim for, and nothing else
// reads either. 16.5 puts the target at about 8% of the viewport's height — a 512-pixel panel
// gets a 43-pixel arm where it used to get 61.
//
// SMALLER THAN IT WAS, because the widget has to fit its own longest arm now. The inset below
// is sized for that worst case, so every pixel the arms grow pushes the origin a pixel further
// from the corner — and at the old size the origin ended up two thirds of the way to the
// middle of the picture, which is not a corner legend. Shrinking here pulls it back to where
// it has always sat.
//
// The WORDS shrink with it, since their size is set against this too (see placeHud). That is
// the right coupling: they name the arms, and a legend whose text outgrows its own arrows is
// what this looked like at 11.5.
const HUD_REF = 16.5;
// The gap between the arms' furthest possible tip and the corner they are pinned to.
//
// THE TIP, AND NOT THE LABEL. The words are anchored a third of an arm past it and run about
// 110 px wide on a 512-pixel panel — the widest thing in this widget by a long way — so
// holding all of them in frame would push the origin well into the picture. Worse, it puts a
// third of the widget's own room permanently out of use: with the inset sized for the labels
// the longest arm the ladder can draw still stopped short of the edge, which reads as a
// legend that has been shoved inward for no reason.
//
// So the ARM is what must be wholly in frame, and the words overhang. Which is what they have
// always done and what the plate under them was dropped for: they are drawn over the parts,
// through them (see the arm builder in meshAnnotations), and read perfectly well there.
const HUD_PAD_PX = 16;

// AND THE LONGEST THE ARM CAN EVER BE, as a multiple of the size it aims for — which is what
// lets the origin be placed once and left alone.
//
// It was placed against the arm's ACTUAL length for a day, so the widget's outer edge stayed
// pinned and its origin crept inward as the arm grew, snapping back at every rung. Which is
// the wrong end to hold still: the origin is width 0, depth 0, height 0 — the one point every
// number on this page is measured from — and a reference that slides while you zoom is not
// one. The far end of an arrow is allowed to move; that is what makes it a ruler.
//
// So the inset is computed for the WORST arm instead, and the origin never moves. The bound:
// picking the nearest rung in log leaves the arm within half the widest gap in the ladder
// (2 -> 5) and the hysteresis may hold it a little past that. Where it switches is where one
// rung stops being HUD_RUNG_HOLD better than the other, which puts the worst arm at
// 10^((0.398 + HUD_RUNG_HOLD) / 2) = 1.73 of its target. 1.8 covers it with a margin.
//
// TRUE ONLY BECAUSE THE LADDER HAS A FLOOR UNDER EVERY USABLE ZOOM — see the rungs below a
// millimetre. A rung with nothing beneath it has no worst case at all.
const HUD_RUNG_MAX = 1.8;
// HOW MUCH BETTER A RUNG MUST BE before it takes over, in decades. The rung is REBUILT and not
// scaled — three arrows and three label textures — so a camera resting on a threshold would
// rebuild every frame. It was a quarter of a decade, which was a fifth of the way to the next
// rung when the rungs were decades apart; on a 1-2-5 ladder the smallest gap is 0.30 decades,
// so a quarter would be almost the whole step and the widget would stick a rung behind for
// most of its range.
const HUD_RUNG_HOLD = 0.08;

// HOW THIN EVERY ORIGIN MARK IS, as a share of its own arm — the world's cross and the parts'
// alike. Each is sized off the thing it belongs to (the whole assembly, or one part), so they
// cannot share a thickness in millimetres without a mark on a screw coming out as wide as it
// is long; what they share is this proportion, which is what makes them read as the same kind
// of mark at every size. One number because two drifted: the parts' crosses stayed fat after
// the world's was thinned, and a fat bar reads as a solid built at the origin rather than a
// mark on it.
const CROSS_THICK = 0.07;


// THE SHOT OPENS AT HOME, and there is no second opening view to configure.
//
// It used to open on a three-quarter — from the front, above and to the right, the way every
// exploded diagram in a manual is drawn — with the origin held down and to the left, out of
// the way of the fitting it is a legend for. The argument was that a first look wants depth
// and that a fitting seen dead-on hides the very gap you are checking.
//
// What that cost is the reference. Every number in this phase is read against the square-on
// view: the angles are measured from it, the zoom is a multiple of its distance, and the
// position is where it is looking. Opening anywhere else meant the readouts started at
// −33, 33, 20 and ×1.07 — three numbers off nothing, on a picture nobody had touched — and
// the way to make them mean something was to press a button first. A refresh now IS the
// reference, and the depth is one drag away.

// One part to load: which instance it is, where its mesh lives, and what the manual says
// it measures.
export type ScenePart = {
  instanceId: string;
  // /api/notice/3d/model/<jobId> — see meshSrc.
  url: string;
  // WHAT IT IS MADE OF, where it is a piece an earlier sub-step left: the meshes it is put
  // together from, each at its own address and standing where it was signed off (`m`, the
  // assembly's frame in millimetres — see PiecePart). Loaded in place of `url`, which is the
  // same geometry in one file: a screw already downloaded for the sub-step that prepared it is
  // not downloaded again inside the frame it was driven into.
  pieces?: { url: string; m: number[]; name: string }[];
  // The printed size, for putting the mesh in millimetres. Null when the manual measured
  // nothing — which only matters if the file cannot answer for itself; see `trueScale`.
  dims: Dims;
  // THE FILE IS ALREADY THE PART, at true scale: a CAD tessellation or a turned mesh, which
  // glTF puts in metres. Then nothing needs measuring and no printed dimension has to anchor
  // it — which for some parts is the only way they can be scaled at all. A spring washer's
  // one printed spec is "M6", and that is the screw it FITS rather than a size of its own.
  trueScale: boolean;
  // AND WHAT THE BUILD MEASURED, for a run that measured itself. A written mesh is built in
  // metres out of millimetres the model chose and read back by Blender the moment it had run —
  // the real size of this very file, and the only one a part with no printed dimension has. See
  // builtBoxOf, and mmScale, where it comes after the drawing and never before it.
  builtMm?: { x: number; y: number; z: number } | null;
  label: string;
  // DRAWN AS ITS OWN MIRROR IMAGE — the other hand of a left/right pair.
  //
  // A pair is ONE file. The two units differ by a reflection and by nothing else, so storing
  // two meshes would be storing the same part twice and asking every edit after it to be made
  // twice as well: drill the left rail, and the right one has a rail-shaped hole missing.
  // Reflecting at draw time keeps one mesh, one set of holes and one scale, and makes the
  // handedness what it actually is — a way of looking at the part, not a second part.
  //
  // ACROSS X, the width axis, which is the axis this app's own six face words are named
  // against: mirroring it swaps LEFT and RIGHT and leaves front/back and top/bottom where they
  // were, which is exactly what distinguishes the two units of a pair.
  mirror?: boolean;
};

// WHICH PART — what a click answers. Nothing is ever dragged: a drag moves the camera, in this
// viewer as in every other, and a part's place is the caller's to write.
export type PartPick = { instanceId: string };

// A DRAG ON THE PARTS TURNS THEM, A DRAG BESIDE THEM SLIDES THE CAMERA — in every viewer, the way
// the published player does it (see the turning drag in mountAssembly). The wheel zooms toward the
// cursor.

// A SOLID LAID OVER THE FIRST PART — a GLB in that part's own file frame, drawn in one colour. See
// AssemblyView.overlays. `on`: the part's own meshes it belongs to, by name — a collision is
// between two, and follows each of them when the part is exploded.
export type ViewerOverlay = { url: string; colour: string; on?: readonly string[] };

// One part of what is on screen and where it stands: `m` is its transform under the assembly's
// root, sixteen numbers column-major. `piece` is which of its part's own parts this is, in the
// order of ScenePart.pieces — null for a part that is one mesh.
export type Placed = { instanceId: string; piece: number | null; m: number[] };

type Vec3T = [number, number, number];
export type CamPose = { eye: Vec3T; target: Vec3T; up: Vec3T };

export type AssemblyView = {
  // Everything on screen is a function of these three.
  show: (asm: Assembly, step: number, t: number) => void;
  // WHETHER THE PARTS PASS THROUGH EACH OTHER, over the whole of the step — measured on the
  // loaded meshes, which is why it is asked of the scene rather than computed beside it.
  // Nothing about the picture changes; see assemblyClash.
  // `moments` is which points of the travel to measure, all of them by default — the seating
  // pass asks for one at a time (see assemblySnap).
  clash: (asm: Assembly, step: number, moments?: readonly number[]) => Promise<ClashProfile>;
  // AND WHICH FACES ARE INSIDE THE OTHER PART AT ONE MOMENT OF IT — what `clashMarks` is then
  // handed. Its own question, asked for the moment on screen: finding them walks every face of
  // both parts, which a profile of sixty-one moments does not (see clashMarksAt).
  clashFaces: (asm: Assembly, step: number, t: number) => Promise<ClashMarks | null>;
  // HOW MUCH OF WHAT MOVES IS HIDDEN BY WHAT STANDS, seen from each of these sides (directions in
  // the assembly's frame, toward the camera): 0 all of it seen, 1 none. See hiddenFrom.
  hiddenFrom: (asm: Assembly, step: number, eyes: readonly (readonly number[])[], cams?: readonly (readonly number[])[]) => Promise<number[]>;
  // AND THE SAME QUESTION OF A PICTURE STANDING STILL, pair by pair: which of these pairs of
  // instances are inside each other, how far, and which faces. See standingOverlaps.
  overlapsAmong: (asm: Assembly, pairs: readonly (readonly [string, string])[]) => Promise<StandingOverlap[]>;
  // HOW MUCH WOOD A BORE WOULD CROSS, at each of the places asked about — measured on the
  // loaded mesh, which is why it is asked of the scene rather than computed beside it, exactly
  // as `clash` is. Millimetres, or null where the ray met nothing to cross.
  //
  // It answers the one thing a THROUGH hole's depth is: a bore that does not stop has no depth
  // of its own, it has the thickness of what it goes through, and that thickness is a fact
  // about the shape rather than about the hole. The bounding box cannot give it — a rail
  // proud at one end has a box thicker than the board everywhere else — and the profile cannot
  // either, since a bore runs along the third axis and the profile is a section over two.
  //
  // THE ONE-PART VIEWER'S QUESTION. It measures the first part in the scene, which in the
  // viewer this is asked of is the only one there is.
  thickness: (probes: readonly HoleMark[]) => (number | null)[];
  // AND THE PART OF THE PICTURE THAT IS WRONG, PAINTED RED: the triangles one measurement
  // found inside the other part, by instance. Null takes the paint off.
  //
  // A whole frame's worth at a time, because that is what it is — the caller asks `clashFaces`
  // for the moment on screen and hands the answer over, and this owns nothing but the drawing.
  clashMarks: (marks: ClashMarks | null) => void;
  // Outline this part and no other, or none at all. Answering the click is the caller's
  // business — it owns what is selected, and the scene draws what it is told.
  select: (instanceId: string | null) => void;
  // AND THESE TOO, in the same orange: parts a table row points at (a joint's covers), beside the
  // one selected. Empty is none.
  highlight: (instanceIds: string[]) => void;
  // WHICH PARTS TO LEAVE OUT OF THE PICTURE, by instance id, and it is the whole list every
  // time rather than one part toggled: the caller holds what is hidden, and a scene that
  // kept its own idea of it would be a second answer to the same question.
  //
  // Left out, not made invisible in some other sense: they are not drawn, not picked by a
  // click, and not measured by the lines around the assembly — which is the point of hiding
  // one, since the size of what you are looking at is the size of what is there.
  hide: (instanceIds: string[]) => void;
  // AND WHICH OF ONE PART'S OWN MESHES ARE OUT OF THE PICTURE, by name — see onPieces. Only
  // drawn differently: the box around the part still measures all of it.
  hidePieces: (instanceId: string, names: string[]) => void;
  // AND WHICH OF THEM WEAR A MEASURED BOX OF THEIR OWN, by name — the part's box, drawn around
  // one mesh. The whole list every time, like `hide`. Face words are left off: one set per
  // mesh would bury the model in labels.
  boxPieces: (instanceId: string, names: string[]) => void;
  // AND WHERE A WRITTEN MODEL'S DIMENSIONS ARE MEASURED, as thin red lines over it — see
  // dimensionLines. Null takes them away.
  dimensionLines: (instanceId: string, spec: DimensionLineSpec | null) => void;
  // AND WHETHER ONE PART'S OWN MESHES STAND SPREAD APART — the Exploded view — or together,
  // sliding between the two unless `animate` is false. Nothing for a part of one mesh. See
  // explodePieces; the part's own box is not drawn while they are apart.
  explode: (instanceId: string, on: boolean, animate?: boolean) => void;
  // How the parts are drawn — the mesh viewer's own question, answered by the same code.
  // See meshDrawModes. How they are LIT is not one of these: the phase settled it at mount
  // and offers nobody the switch, so a hook to change it would be a capability with no
  // caller and a second answer to a question that has one.
  draw: (mode: DrawMode) => void;
  // RED SOLIDS OVER THE FIRST PART — a product's overlaps, each a GLB in the same file frame as the
  // model (see ProductOverlaps). Hung on the model's own node, so they turn, scale and centre with
  // it; drawn through everything, so a collision inside the wood reads in every draw mode. The
  // whole list each time: what is not in it is taken off.
  //
  // ONE COPY PER MESH IT NAMES (`on`), hung on that mesh: exploded, each of the two parts of a
  // collision carries it away with it; assembled, the copies stand in one place and read as one.
  overlays: (list: readonly ViewerOverlay[]) => void;
  // HOW BRIGHT THE PICTURE IS, as a multiple of the set's own — see makeLighting's `brighten`.
  brightness: (by: number) => void;
  // A SHADOW ON THE FLOOR UNDER WHAT STANDS NOW, cast from above and in front — the published
  // tutorial's cover, which reads as a product photograph with one (Louis 2026-10-23). Measured
  // on the pose on screen, so it is asked for once the clip has drawn; a still clip never moves.
  floorShadow: () => void;
  // THE DRAG AND THE WHEEL ANSWER FROM HERE ON, for a scene mounted `heldUntilReleased`.
  release: () => void;
  // HOW IT IS LIT — even against studio, see makeLighting, which argues the two answers.
  //
  // The assembly phase does not call this and does not offer the switch: nothing there is
  // being colour-matched, and under even's flat white sphere a zinc screw is a grey
  // silhouette with no shank. The bench is where the other question gets asked — "is this the
  // colour it was matched to" — and it is asked of one part, on its own, which is the viewer
  // this scene now also draws.
  light: (mode: LightMode) => void;
  // WHERE EVERY PART ON SCREEN STANDS, RIGHT NOW — at whatever moment of the movement is
  // showing.
  //
  // Which is what a validated sub-step's OUTPUT is: two parts and a final position somebody
  // has watched and accepted. Phase 5 signs that off; this is how the thing it signed off
  // becomes addressable — a piece with an id, loadable by every later step and by the parts
  // strip, instead of a fitting that has to be re-assembled from its parts to be seen again.
  //
  // ASKED OF THE SCENE because the scene is the only place those parts stand together: the
  // poses live in the store, the meshes are scaled and centred here, and what puts one inside
  // the other is this file. A part that is itself a piece answers once per part IT is made of,
  // so what is kept is always a list of meshes and never a list of lists.
  //
  // IN THE ASSEMBLY'S OWN FRAME, Z up, which is the frame every mesh in this app is stored in
  // and the one the fitting's numbers are written in. The root's quarter turn into three's
  // Y-up is display, and it is divided back out.
  //
  // Only what is DRAWN: a part somebody hid is not in the output, because it is not in the
  // picture that was validated. Wireframes, outlines, red clash patches and the gizmo are not
  // either — they hang beside the part rather than inside it.
  placed: () => Placed[];
  // A TURN THAT ARRIVES rather than being dragged: half a turn about an axis is the other
  // side of the fitting, which by hand is a wrist's worth of orbiting that lands near it.
  //
  // About the ASSEMBLY's axes, not three's. The letters on the buttons are the letters on
  // the gizmo, and the gizmo is labelled in the frame every field on the page is written in
  // — a Z button that turned about three's Z would disagree with the arrow beside it.
  turn: (axis: 'x' | 'y' | 'z', degrees: number) => void;
  // WHERE THE CAMERA IS STANDING, and how to put another one in the same place. The same
  // pair meshScene grew and for the same reason: a viewer is mounted per mesh, so swapping
  // one for another frames afresh — right for a different object, wrong for the SAME object
  // at two stages. Comparing a written run's draft against its correction means looking at
  // both from one angle.
  //
  // The target goes with the position, or the next drag orbits a point nobody is looking at.
  viewpoint: () => { at: [number, number, number]; to: [number, number, number] };
  aimAt: (shot: { at: [number, number, number]; to: [number, number, number] }) => void;
  // A step along one of those arms was here — the corner's position readout used to open a
  // menu of them. It is typed now, and an absolute aim goes through `look` with the rest of
  // the shot; a relative pan had no caller left. The arrow keys are untouched: they nudge the
  // picture across the SCREEN, which is a different question from "move 10 cm along red".
  // Re-fit the camera. Called when the FITTING changes, never while scrubbing: a camera
  // that re-framed per frame would push in as the part approached, and a part cannot be
  // seen to travel if the world travels with it.
  frame: (asm: Assembly, step: number) => void;
  // THIS SCENE AS A PICTURE — a PNG data URL of whatever is on the canvas.
  //
  // What it is FOR: the candidate shots of ShotCandidates, each a picture rather than a live
  // WebGL context.
  //
  // NO SETUP NEEDED. It renders and reads inside one task, which is before the browser
  // composites and throws the colour buffer away, so the picture is still there — no
  // `preserveDrawingBuffer`, and no per-frame copy on every viewer in the app to buy it.
  shot: () => string | null;
  // AND THE PICTURE AS IT STANDS, drawn into a 2D canvas: rendered and copied in one task, as `shot`
  // reads it — for recording a video of what plays (see AllClips).
  drawInto: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  // RENDERED THIS MANY PIXELS WIDE, whatever the box is on screen (null: the screen's own density):
  // a video recorded at 1920 wide is drawn at 1920, not scaled up from the box.
  renderWidth: (px: number | null) => void;
  home: () => void;
  // PUT THE CAMERA EXACTLY THERE — the inverse of what `onView` reports, in the same three
  // terms: the point the shot is centred on (assembly frame, millimetres), how far it is
  // turned off square (XYZ Euler degrees), and how far in it is as a multiple of home.
  //
  // Which is the whole reason those three were chosen for the readout: a shot has to be
  // recordable and re-enterable, and a reading you cannot hand back is a reading you can only
  // compare by eye. See the camera row of the movement bar, which stores exactly this.
  look: (view: { centre: [number, number, number]; deg: [number, number, number]; zoom: number; auto?: number }) => void;
  // HOW WIDE THE PICTURE IS AT ZOOM 1, in millimetres across where it is aimed: this assembly's
  // own outline, seen from `eye` (towards the camera, the assembly's frame) and aimed at `aim`
  // (its millimetres) — the fit `look` will stand a shot at. What a rule's zoom is a share of (see
  // movementShot's zoomOf). Null where none of its parts is loaded.
  widthAt: (asm: Assembly, step: number, eye: readonly number[], aim: readonly number[]) => number | null;
  // THE CAMERA AS IT STANDS, in the assembly's own frame (mm): where it is, what it looks at, which
  // way is up — and put back exactly there (`place`). For carrying a view from one clip's scene
  // into the next one's (see AllClips), where the three readout numbers would not mean the same.
  cameraPose: () => CamPose;
  placeCamera: (pose: CamPose) => void;
  // WHERE THE MESH LOADED FROM `url` STANDS, in the assembly's frame, as a 4x4 (column-major):
  // the first of the given instances holding it. Null where none does.
  meshFrame: (url: string, among: string[]) => number[] | null;
  // AND EVERY MESH AT ONCE, as it stands now: the file it came from, the instance it is drawn for,
  // its frame as meshFrame gives one, and whether it is shown (it and all above it). For the
  // assembly as a GLB of its own (see assemblyGlb).
  meshFrames: () => { url: string; inst: string; frame: number[]; shown: boolean }[];
  // A CAMERA CARRIED FROM ONE FRAME TO ANOTHER: `pose` as seen where a mesh stood at `from`, put
  // where that mesh stands at `to` (both from meshFrame).
  carryPose: (pose: CamPose, from: number[], to: number[]) => CamPose;
  // HOW FAR BACK, ALONG THE LINE THE CAMERA IS ALREADY ON — and in the units the view is
  // REPORTED in, which is the whole reason it exists beside `look`.
  //
  // `look` fits its zoom to the body AS SEEN FROM THE ANGLE IT IS GIVEN, because a shot that
  // says ×1 has to actually contain the part it is a shot of, whatever corner it stands at.
  // `reportView` measures against the square-on distance, which does not move as you orbit —
  // so ×2 means the same thing all the way round. Both are right for what they are for, and
  // they are not the same number: a long part seen corner-first needs about 1.6× the room.
  //
  // Putting a remembered zoom back through `look` therefore returned a DIFFERENT number than
  // went in, and the viewer put that back next time: 2.55, 1.59, 0.99, 0.62 — the same factor
  // compounding on every swap, which is what pressing between a run's two passes looked like.
  //
  // This one changes the distance and nothing else, against the reference the report uses. Put
  // ×2.55 in, read ×2.55 out.
  zoomTo: (zoom: number) => void;
  // WHICH PARTS ARE MEASURED, by instance id, the whole list every time — like `hide` and
  // `origins`, and for the same reason: the caller holds what is switched off.
  //
  // One switch per part now, where it used to be one switch for all of them. That was not a
  // simplification but the shape of the thing being switched: there was ONE box, around
  // everything visible, so "this part's measures" named nothing and the cards' buttons could
  // only vote. Each part carries its own box now, so each button is about its own part.
  measures: (instanceIds: string[]) => void;
  // AND WHOSE SIX FACES ARE NAMED, the same way — see ExtentDims.showFaces, which argues why
  // this is a second button and not the same one. The box the words hang on is drawn while
  // either list has the part in it — unless the scene was mounted with `boxSwitch`, see `boxes`.
  faces: (instanceIds: string[]) => void;
  // AND WHOSE BOX IS DRAWN AT ALL, where the scene was mounted with `boxSwitch`: the edges on a
  // switch of their own, so a box can stand with no numbers and no face words on it. The whole
  // list every time, like `measures`.
  boxes: (instanceIds: string[]) => void;
  // THE GIZMO, ON OR OFF. Held as a flag in the scene rather than applied to the group once,
  // because the gizmo is REBUILT whenever it changes rung (see setGizmoStep) — a visibility
  // set on the old group would be thrown away with it, and the arms would come back on their
  // own the first time the shot crossed a decade.
  gizmo: (on: boolean) => void;
  // WHAT A MODEL SAID ABOUT ONE PART'S SIX FACES, written under the app's own name for each —
  // see FacesSaid, and makeExtentDims, which draws it. Null takes the second rows away again;
  // ASKING hides that part's plates entirely, for the length of a call.
  //
  // Per part, because the answer is about one mesh: the assembly draws two or three, and the
  // orientation question was asked of exactly one of them.
  said: (instanceId: string, faces: FacesTold) => void;
};

export type AssemblyOpts = {
  onReady?: (view: AssemblyView) => void;
  // THE SHAPE A SHOT'S ZOOM IS MEASURED IN, width over height (see shotShape): zoom 1 fits the parts
  // in a box this shape, or the box itself where it is narrower. A shot is a zoom of that fit, so in a
  // wider box (the desktop's, see formFactor) it stands where it stands in the tall one, the same
  // height of picture and more room either side. Fitted to the box itself, zoom 1 came closer as the
  // box widened and the shot cropped top and bottom (Louis 2026-11-02). Absent: the box.
  framedAt?: number;
  // THE HOLE BEING TYPED, READ EVERY FRAME rather than pushed in when it changes.
  //
  // A preview has to follow the fields keystroke by keystroke, and pushing it here went
  // through a store write, a subscription, a re-render and an effect — four links, any one of
  // which failing looks the same from the outside: a mark that stands still while the numbers
  // under it change. This loop already reads live state sixty times a second (see the tick);
  // one more read of a value that is nearly always null costs a comparison and cannot go
  // quiet. The scene calls it and knows nothing else about where it comes from.
  //
  // A LIST, because a reading of a part is compared: four holes drawn together say whether
  // they line up and how far apart they are, which is what somebody is checking, and asking
  // it one hole at a time is not the same question. Empty for none.
  holeMarks?: () => HoleMark[];
  // HOW THE MESHES OF ONE PART COME APART when exploded: out from the centre, or along one axis
  // for hardware built together — see explodeShifts.
  explodeAlong?: 'z';
  // WHICH PARTS STAY SOLID when the scene is seen through ('xray') — the hardware in a joint.
  keepSolid?: (instanceId: string) => boolean;
  // WHAT EACH AXIS MEASURES, written on the gizmo's arrows. Translated by the caller, which
  // is the only place with a language. In the ASSEMBLY's frame, so Z is the height and Y is
  // the depth — the opposite pairing to the mesh viewer's, whose gizmo stands in three's
  // Y-up world. The same three words either way; the rotation is what swaps two of them.
  axisWords?: { x: string; y: string; z: string };
  // WHERE THE VIEWPOINT IS POINTING, as XYZ Euler degrees, whenever it changes.
  //
  // Measured FROM THE SQUARE-ON VIEW and not from three's own identity camera, so the number
  // is the one the picture is asking about: how far this shot is turned off dead-on. Home
  // therefore reads (0, 0, 0), which is what makes it a reference rather than a place the
  // camera happens to have been. Degrees, like the panel's orientation boxes, and the same
  // 'XYZ' order.
  // AND HOW FAR IN THE SHOT IS, as a multiple of the home distance: 1 at home, 2 at half of
  // it, 0.5 at twice. A factor and not a distance in millimetres, because "how close am I"
  // has no natural unit and every answer to it is really a comparison with somewhere — and
  // there is now a somewhere, one click away, which is what makes the number readable.
  // AND WHERE IT IS LOOKING, in the assembly's own frame and in centimetres — the origin at
  // home, and drifting from it as the shot is panned. In the ASSEMBLY's coordinates and not
  // the screen's, deliberately: "the gizmo has gone off to the right" is a fact about the
  // picture that changes meaning the moment you orbit, while "the camera is looking 12 cm
  // along X" is the same statement from every angle, and is the frame every other number in
  // this phase is written in.
  // WHETHER TURNING THIS VIEWER TURNS EVERY MESH IN THE APP — the shared angle the
  // thumbnails follow. True where the scene holds ONE part and is a way of looking at it
  // (the phase's viewer, the modal over it); false for the assembly phase, where the camera
  // is about a fitting rather than about a part, and re-aiming every thumbnail in the
  // database because somebody watched a screw go in would be an answer to a question nobody
  // asked. The twin of mountMesh's own `sharedView`.
  sharedView?: boolean;
  // WHETHER THE CAMERA MAY GO INSIDE THE PART. Off by default: the assembly phase holds
  // several parts with air between them, and flying through that gap is how a joint is
  // looked at. On for a viewer showing one solid part, where there is no inside to visit.
  keepOut?: boolean;
  onView?: (view: {
    deg: [number, number, number];
    zoom: number;
    centre: [number, number, number];
  }) => void;
  // AND THE THREE THE MEASUREMENTS ARE WRITTEN WITH — "3D width", "3D depth", "3D height",
  // the mesh viewer's own wording for what a mesh actually measures, translated by the
  // caller like everything else here. Absent, the assembly is drawn without them.
  extentWords?: { width: string; depth: string; height: string; unknown: string };
  // WHETHER A BOX'S EDGES HAVE A SWITCH OF THEIR OWN — see `boxes`. Without it they are drawn
  // while its size labels or its face words are, which is what the assembly phase relies on.
  boxSwitch?: boolean;
  // WHAT EACH OF A PART'S OWN MESHES IS SUPPOSED TO MEASURE, asked by the mesh's name — the second
  // row on its box, as the part's `dims` are on the part's. Null where nothing says.
  pieceWanted?: (name: string) => { x: number | null; y: number | null; z: number | null } | null;
  // AND THE SIX THE FACES ARE NAMED WITH — top, bottom, front, back, left, right, translated
  // in the same place. Needed alongside `extentWords` and not instead of it: both are drawn on
  // one box, so the box is built only when both are given.
  faceWords?: FaceWords;
  // "wanted width", "wanted depth", "wanted height", and the word for an axis nothing was
  // measured on. Given, every box carries a second row saying what its part is SUPPOSED to
  // measure, from that part's own `dims`.
  //
  // PHASE 2 GIVES THESE AND PHASE 5 DOES NOT, which is the difference between the two
  // questions and not a setting. On the bench a mesh is being judged against the size the
  // manual printed, and the gap between the two rows IS the judgement. In an assembly there
  // is nothing to judge: a washer halfway down a screw is where the fitting says it is, and a
  // second row under every number would be six lines of noise around a movement.
  wantedWords?: { width: string; depth: string; height: string; unknown: string };
  // Parts whose mesh loaded but whose real size is unknown, so they are drawn at whatever
  // scale the file happened to use. Worth saying out loud: a screw and a washer at two
  // unrelated scales make a correct animation look broken.
  onUnscaled?: (labels: string[]) => void;
  // WHAT IT IS LIT WITH at mount. The assembly never passes one and gets studio, for the
  // reason written where it is applied; the bench passes whatever its switch is on.
  light?: LightMode;
  // HOW BIG THE MESH IS, counted once as it loads: what the decimation target is aiming at.
  // The setting says what was asked for and this says what came back, and they are not always
  // the same. Summed over every part on screen, which for the bench's one part is that part.
  onStats?: (stats: { vertices: number; triangles: number }) => void;
  // WHAT A PART'S MESH ACTUALLY MEASURES, in the FILE's own units, reported once per part as
  // it loads. Not millimetres: the caller that wants this wants it to offer a rescale — "this
  // axis should be 70 mm" — and the factor it computes is against the file's numbers.
  //
  // Phase 2's, like the words above. An assembly has nothing to do with it.
  onMeasured?: (instanceId: string, size: { x: number; y: number; z: number }) => void;
  // AND ITS SHAPE INSIDE THAT BOX, for the caller that places holes on it. Absent everywhere
  // else, and the sampling does not happen without it.
  onProfiled?: (instanceId: string, profile: PartProfile) => void;
  // THE SEPARATE MESHES A PART'S FILE HOLDS, by the names the file gives them — reported once it
  // has loaded, and only where there are two or more. A written model is one mesh per component,
  // and nothing else on the page can name them.
  onPieces?: (instanceId: string, names: string[]) => void;
  onState?: (state: 'loading' | 'ready' | 'failed') => void;
  // WHETHER THE GIZMO STANDS IN THE SCENE FROM THE START (it does by default). A clip player
  // says no: it used to switch it off once the meshes were in, so a scene still loading, or one
  // that never finished, showed a published page a set of coloured arrows and nothing else.
  gizmo?: boolean;
  // ROOM KEPT CLEAR AT THE TOP OF THE PICTURE, in pixels, for what the caller draws over it (a part
  // card's name, Louis 2026-10-23). The shot is fitted to the picture below it, and the picture
  // itself still runs to the top edge: turned or zoomed, the model may pass under the name, and is
  // never cut off short of the frame. Done by the projection alone (see resize), so every fit
  // and every drag reads the frame exactly as without it.
  headroom?: number;
  // A PICTURE IN A LIST THAT SCROLLS (the published page's parts, Louis 2026-10-23): a swipe up
  // or down scrolls the page and never reaches the scene, a swipe across turns the part round
  // its upright and does not tip it, and the wheel and the pinch go to the page too. Every
  // finger movement was the picture's, and the list could hardly be scrolled.
  scrollsUpDown?: boolean;
  // NO DRAG AND NO WHEEL UNTIL `release` IS CALLED, instead of from the moment the meshes are in:
  // for a caller that moves the camera itself once they are (the cover re-aims at the product's
  // middle), which threw away a drag already under way — the view jumped.
  heldUntilReleased?: boolean;
  // THE BROWSER TOOK THE WEBGL CONTEXT BACK while this scene was live: past its allowance of
  // contexts (one shared with the app's other tabs) it drops the oldest, and the scene is a
  // blank canvas from then on. The caller mounts it again.
  onContextLost?: () => void;
  // A PART WAS CLICKED, or the background was. The scene does not decide what a selection
  // means — it reports the click and outlines whatever `select` is then told to.
  onPick?: (pick: PartPick | null) => void;
  // ONE OF THE SIX WORDS ON A PART'S BOX WAS PRESSED. `face` is that word's own name — "top",
  // "front" — in the frame the box is drawn in, which is the caller's to interpret.
  //
  // Its own callback rather than a kind of `onPick`, because it is a press on an ANNOTATION
  // and not on the object: a label is a claim the app is making about the part, and pressing
  // one asks about the claim. Given, the plates become pressable and a press on one no longer
  // selects the part behind it — see the click handler, which tries the words first.
  onPickFace?: (instanceId: string, face: string) => void;
  // SOMEBODY HAS TAKEN THE CAMERA — turned it, slid it, or zoomed. Said once per gesture, for a
  // caller that is otherwise driving the camera itself and has to stop (the clip player's shot).
  onCameraTaken?: () => void;
  // ONE GPU CONTEXT FOR ALL THE VIEWS GIVEN THIS NAME (see sharedRenderer): each draws into a plain
  // canvas of its own, copied from the shared one after every frame. The published tutorial's four
  // colourway tiles, whose one chair was otherwise uploaded four times.
  sharedGl?: string;
};

// Draw `parts` inside `el`, and answer the function that takes it down again. Synchronous
// to call and asynchronous inside, the shape mountMesh uses: an effect can return this
// directly, and a component unmounted while three.js is still arriving tears down nothing
// because nothing was built.
export function mountAssembly(el: HTMLElement, parts: ScenePart[], opts: AssemblyOpts = {}): () => void {
  let alive = true;
  let cleanup: (() => void) | undefined;
  opts.onState?.('loading');
  // HOW LONG EACH STEP OF THE LOAD TOOK, for the server's log ("[load]", see serverLog): named by
  // the colourway its meshes are in, where they are in one.
  const t0 = performance.now();
  const firstUrl = parts[0]?.url || parts[0]?.pieces?.[0]?.url || '';
  const load = serverLog('load', `${/colourway=([^&]{4})/.exec(firstUrl)?.[1] ?? 'app'}${Math.random().toString(36).slice(2, 4)}`);
  const since = () => `+${Math.round(performance.now() - t0)} ms`;
  load?.say(`mount, ${parts.length} parts`);
  let drawnOnce = false;

  void loadDeps().then(async ({ THREE, GLTFLoader, OrbitControls, RoomEnvironment }) => {
    if (!alive) return;
    load?.say(`three.js in ${since()}`);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 5000);
    // `stencil` because the selection rim needs one — three leaves it off by default, and
    // without it the mask below writes nothing and the rim fills the part solid. See
    // outlineMat.
    // AND THE DRAWING BUFFER IS KEPT ON THE SANDBOX, where it is the difference between
    // a save that has the 3D in it and one with a grey rectangle where the viewport was.
    // A WebGL canvas is read back with toDataURL, and a browser is free to throw the
    // colour buffer away the moment it has composited — which it does, unless asked not
    // to. The Saves tab photographs a whole phase some time after it was drawn (see
    // sandboxSaves), so it cannot use the trick the clip poster uses of rendering and
    // reading inside one task. It costs a copy per frame and it is paid on the sandbox
    // alone, which is the one build that photographs itself.
    const make = () => {
      const made = new THREE.WebGLRenderer({
        antialias: true, alpha: true, stencil: true, preserveDrawingBuffer: IS_SANDBOX,
      });
      tallyRenderer(made);
      return made;
    };
    const shared = opts.sharedGl;
    const renderer = shared ? borrowRenderer(shared, make) : make();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // WHAT THIS VIEW SHOWS AND IS PRESSED ON: the renderer's own canvas, or where the renderer is
    // shared, a plain one the picture is copied into (see `render`).
    const surface = shared ? document.createElement('canvas') : renderer.domElement;
    const copyTo = shared ? surface.getContext('2d') : null;
    // This view's size in CSS pixels, which a shared renderer's own is not.
    const size = { w: 0, h: 0 };
    const viewHeight = () => size.h;
    const render = () => {
      if (!copyTo) {
        renderer.render(scene, camera);
        return;
      }
      // Sized to this view, and only when another left it otherwise: resizing clears the buffer.
      const at = renderer.getSize(sizeScratch);
      if (at.x !== size.w || at.y !== size.h) renderer.setSize(size.w, size.h, false);
      // And this scene's exposure and tone mapping, which another view's lighting may have set.
      lighting.restore();
      renderer.render(scene, camera);
      copyTo.clearRect(0, 0, surface.width, surface.height);
      copyTo.drawImage(renderer.domElement, 0, 0, surface.width, surface.height);
    };
    // The tonemapper comes with the lighting below — see makeLighting.
    el.appendChild(surface);
    surface.style.width = '100%';
    surface.style.height = '100%';
    surface.style.display = 'block';

    // A TEARDOWN THAT EXISTS FROM THE MOMENT THERE IS ANYTHING TO TEAR DOWN.
    //
    // The canvas goes into the page here, and the meshes are awaited below — so between
    // those two lines this mount owns a canvas and a GL context while the caller's teardown
    // has nothing to call. It only set `alive = false`, the awaited body returned on the
    // next line, and the canvas stayed in the host for ever.
    //
    // That is not a leak you have to look for. The orphan is 300 pixels tall and styled to
    // fill its parent, the live canvas is appended under it, and the host clips at 460 — so
    // the viewport shows a blank dead canvas with the real scene rendering below the fold.
    // One remount during a load is enough, and the parts list settling right after mount is
    // exactly such a remount.
    const giveBack = () => (shared ? returnRenderer(shared, renderer) : releaseContext(renderer));
    cleanup = () => {
      giveBack();
      surface.remove();
    };
    // Not our own teardown's release, which loses it on purpose: `alive` is false by then.
    renderer.domElement.addEventListener('webglcontextlost', () => {
      // In the load log too: a view the browser dropped shows as a blank picture and nothing
      // else (Louis 2026-10-23).
      load?.say(`webgl context lost ${since()}${alive ? '' : ' (already unmounted)'}`);
      if (alive) opts.onContextLost?.();
    });
    // It may already have been cancelled while three.js was importing.
    if (!alive) { cleanup(); return; }

    // STUDIO, AND ONLY STUDIO — from the mesh viewer's own builder, see makeLighting, which
    // argues the two answers. Not a default here but the whole choice: nothing in this phase
    // is being colour-matched, and a metallic surface has no diffuse term at all, so under
    // even's flat white sphere a zinc screw is a grey silhouette with no shank and no
    // readable axis. A room to reflect is what makes a fastener look like one, and this
    // phase is about which way it points. The switch that used to sit over the viewport is
    // gone with the question it asked.
    const lighting = makeLighting(THREE, renderer, scene, RoomEnvironment);
    lighting.apply(opts.light ?? 'studio');

    // pivot centres the assembly; root turns Blender's Z-up into three's Y-up. One
    // rotation, at the top, so every number in assemblyPose and in the fitting stays in the
    // frame it was written in.
    const pivot = new THREE.Group();
    const root = new THREE.Group();
    root.rotation.x = -Math.PI / 2;
    pivot.add(root);
    scene.add(pivot);

    // WHAT IS DRAWN ABOUT THE PARTS RATHER THAN BEING ONE OF THEM: the measurement lines and
    // the gizmo. It carries the root's own quarter turn, so everything inside it is written
    // in the ASSEMBLY's axes — the ones the fitting's fields name — while sitting outside
    // the root itself, which matters twice. `frame` fits the camera to everything beneath
    // the root, and these are drawn FROM that fit: measuring them in turn would be a shot
    // fitted to its own annotations, growing by a gap and a label every time. And
    // `attachDrawModes` walks that same root, where an arrow turning into a wireframe cone
    // with the parts would be an annotation drawn as if it were one of the things annotated.
    const annot = new THREE.Group();
    annot.rotation.copy(root.rotation);
    pivot.add(annot);

    // HOW BIG EACH PART IS, AND WHICH WAY ROUND — ONE BOX PER PART, and it is the part's own.
    //
    // It was one box around everything visible, rebuilt every frame from the parts' current
    // poses. Which measured the right thing only while nothing turned: an axis-aligned box
    // around a rotated part is not that part's box, so laying the seat flat moved its 733 mm
    // from the height to the depth and swung "Top" onto whichever face was pointing up at
    // that instant. Three numbers and six words that changed while the part they describe did
    // not — the one thing a measurement must never do.
    //
    // A part's size is a fact about the PART. So each one carries its own box, measured once
    // in its own frame (see `bounds`) and drawn through its pose: the box tumbles with the
    // part, the numbers never move between axes, and a face keeps its name.
    //
    // Which also settles a question the union could not answer. "Which way up is this?" is
    // asked OF SOMETHING; with a rail halfway into a leg there was no answer, and the words
    // named the world's directions with a part's vocabulary. Per part there is always one.
    //
    // Under `annot` rather than parented to the holders, for the reason annot exists: the
    // camera is framed on `root` and the draw modes walk it, and an annotation inside either
    // is a picture measuring itself. `root` carries only the Z-up quarter turn, and `annot`
    // copies it, so a holder's matrix means the same thing under both — which is what lets a
    // box wear its part's pose without composing anything by hand.
    const extents = new Map<string, ExtentDims>();
    // A written model's dimension lines, by part — see dimensionLines — and the mesh inside each
    // holder, whose own scale and centring are what a point in the file is written through.
    const dimLines = new Map<string, ReturnType<typeof makeDimensionLines>>();
    const meshOf = new Map<string, import('three').Object3D>();
    // WHOSE BOX IS DRAWN, by instance id — the whole list every time, like `hide` and
    // `origins`. The cards' ruler switch was one button per part voting on a single set of
    // lines, which is what a shared box left it able to mean; now each button is about the
    // part it sits on.
    const ruled = new Set<string>();
    // And whose plates are on — the same shape as `ruled`, filled at load for the same reason:
    // a part arrives named unless somebody says otherwise.
    const labelled = new Set<string>();
    // And whose box is drawn at all — read only where the scene has `boxSwitch`. On at load, as
    // the other two are.
    const boxed = new Set<string>();
    // AND WHAT EACH PART IS SUPPOSED TO MEASURE, in the frame this box is drawn in: x the
    // width, y the depth, z the height — which is exactly how a part's `size` record is
    // written, so nothing is remapped on the way in. Only read where `wantedWords` was given.
    const intended = new Map<string, NonNullable<Dims>>();

    // AND WHICH WAY IS WHICH, AT THE ORIGIN — width 0, depth 0, height 0, which is the one
    // place its arrows are not just a direction but the frame itself. Every number on the
    // page is measured from here: a seat of [0,0,0] is a part standing exactly on this
    // point, and the +Z the axis buttons name is this blue arm.
    //
    // Ten millimetres of it, which is the second thing it says — a centimetre held up
    // against the part, foreshortened and shrunk by the same camera the part is drawn
    // through. See makeWorldGizmo.
    //
    // DRAWN OVER THE PARTS. A frame you cannot see is not a frame: the arms are drawn through
    // whatever is in front of them (see the `arm` builder in meshAnnotations, where the case
    // is argued), while still being the length of the world they claim.
    //
    // REBUILT WHEN IT CHANGES RUNG, and not scaled. The words on the arms carry the length,
    // so a rung is a different set of labels and not a different transform — and the labels
    // are baked into their textures when the gizmo is built. Crossing a decade is a rare
    // event (three of them exist), so building three arrows and three sprites at the moment
    // it happens costs nothing anybody can see; scaling the old group would leave "1 cm"
    // written on an arm a metre long, which is the one failure that matters here.
    // PINNED TO THE BOTTOM LEFT, AND IN THE SAME PICTURE AS EVERYTHING ELSE.
    //
    // The arms used to stand at the origin, in the middle of whatever was being looked at, and
    // a shot leaning into a joint pushed them off screen — so the one legend saying how big
    // anything is went missing exactly when it was needed. They were then drawn into their own
    // scissored corner, over the top of the frame, which fixed that and bought a new problem:
    // a legend nothing can ever be in front of is a legend that covers the thing it is a
    // legend for.
    //
    // So: a child of the CAMERA, which is what pins it — it inherits where the camera is and
    // which way it is facing, and holding its own place in camera space puts it at the same
    // corner of the picture whatever the shot does. It keeps the world's ORIENTATION (its
    // quaternion is the camera's, undone) so the arms go on pointing where the axes point, and
    // it is scaled by the viewing distance so its size on screen never changes.
    //
    // AND IT SITS AT THE DEPTH OF WHAT IS BEING LOOKED AT, not against the lens — which is
    // what makes its arms the same size as the parts' own millimetres, and therefore a ruler
    // rather than a badge. It does not TEST against that depth: the arms and their words are
    // drawn through whatever covers them, for the reason given with the arm builder. Where it
    // sits and what it is occluded by are two questions, and only the first one is answered
    // by putting it out there in the scene.
    const hudHolder = new THREE.Group();
    camera.add(hudHolder);
    // A camera draws its own children only if it is in the graph; nothing else needs it there.
    scene.add(camera);

    let gizmo: ReturnType<typeof makeWorldGizmo> | null = null;
    let gizmoStep = -1;
    // WHETHER THE WORLD IS IN MILLIMETRES AT ALL. It is whenever one part was anchored; when
    // none was, the arms are file units and a "10 mm" on them is the same lie the box's edges
    // would tell. Written once the parts have loaded, and declared here because the render
    // loop builds the widget before they have.
    let worldInUnits = false;
    let gizmoShown = opts.gizmo ?? true;
    const setGizmoStep = (index: number) => {
      if (index === gizmoStep) return;
      gizmoStep = index;
      const rung = GIZMO_STEPS[index];
      if (gizmo) {
        hudHolder.remove(gizmo.group);
        gizmo?.dispose();
      }
      // The arm's own length, written after the name of the axis it measures: "width=0" says
      // where the arrow starts, and this says how far it goes — and now it does go that far.
      const w = opts.axisWords;
      // THE ARM IS THE RUNG. `makeWorldGizmo`'s length is in the scene's units and the scene's
      // unit is a millimetre (every mesh is scaled by mmScale on the way in), so an arm built
      // at `rung.mm` is that many millimetres of the world the parts are in — foreshortened by
      // the same camera, growing and shrinking with them. Which is what the arms were when
      // they stood at the origin, and what they stopped being when they moved to the corner.
      gizmo = makeWorldGizmo(
        THREE,
        w && !worldInUnits
          ? { x: `${w.x}, ${rung.label}`, y: `${w.y}, ${rung.label}`, z: `${w.z}, ${rung.label}` }
          : w,
        rung.mm,
      );
      gizmo.group.visible = gizmoShown;
      // The assembly is Z-up and three is Y-up; the arms are drawn in the assembly's frame, so
      // the widget carries the same quarter turn `root` does.
      gizmo.group.rotation.copy(root.rotation);
      hudHolder.add(gizmo.group);
    };

    // THE ARM'S LENGTH ON SCREEN, in pixels, at the rung given. Everything below is placed
    // against this — which rung to be on, and how far in from the corner to sit.
    const armPixels = (mm: number, h: number, dist: number) => {
      const halfWorld = dist * Math.tan((camera.fov * Math.PI) / 360);
      return halfWorld > 0 ? (mm * (h / 2)) / halfWorld : 0;
    };
    // AND THE SIZE THE ARMS AIM FOR, which is the size they used to be fixed at: HUD_ARM of
    // the world at HUD_REF, in a frustum of this height. So the widget at rest looks exactly
    // as it did, and what has changed is that it now moves between rungs instead of holding
    // still and relabelling.
    const armTarget = (h: number) =>
      (HUD_ARM * (h / 2)) / (HUD_REF * Math.tan((camera.fov * Math.PI) / 360));

    // Reused: the two below run every frame, and a Vector2 per frame is a Vector2 per frame.
    const sizeScratch = new THREE.Vector2();

    // WHERE THE WIDGET SITS THIS FRAME, in the camera's own space: pinned to the bottom left
    // by its outer extent, at the distance the shot is focused on, turned back into the
    // world's frame.
    //
    // AT WORLD SCALE, which is the change. The holder used to be scaled by (distance /
    // HUD_REF) so that whatever hung off it held a constant size on screen; the arms are a
    // measurement of the world now, so the holder is left at 1 and they are simply the length
    // they claim. What still wants a constant size is the WORDS, and they get one below.
    const hudSpin = new THREE.Quaternion();
    const placeHud = () => {
      const dist = camera.position.distanceTo(controls.target) || 1;
      const h = viewHeight();
      const tanV = Math.tan((camera.fov * Math.PI) / 360);
      const tanH = tanV * (camera.aspect || 1);
      const rung = GIZMO_STEPS[gizmoStep] ?? GIZMO_STEPS[0];
      // HOW FAR THE ARMS COULD EVER REACH from the origin, in pixels: the longest one the
      // ladder allows. Isotropic, because the arms point where the world points and any of the
      // three may be the one aimed at the corner.
      //
      // The LONGEST and not this frame's, so the answer depends on the viewport alone. See
      // HUD_RUNG_MAX: the origin is the reference every number is measured from, and one that
      // creeps across the picture as you zoom is not a reference.
      const reachPx = armTarget(h) * HUD_RUNG_MAX;
      // Back into the world at the depth it is drawn: a pixel is this much of it.
      const perPx = h > 0 ? (2 * tanV * dist) / h : 0;
      const inset = (reachPx + HUD_PAD_PX) * perPx;
      // Both terms scale with the distance, so what this fixes is a place on the SCREEN: the
      // origin sits the same number of pixels up and in from the bottom-left corner at every
      // zoom, and the arms grow and shrink around it.
      hudHolder.position.set(-tanH * dist + inset, -tanV * dist + inset, -dist);
      // The camera's own turn, undone: a child of the camera inherits its rotation, and the
      // arms have to point where the world points rather than where the lens does.
      camera.getWorldQuaternion(hudSpin);
      hudHolder.quaternion.copy(hudSpin).invert();
      // AND THE WORDS HELD AT A CONSTANT SIZE, which is the half of this the group scale can no
      // longer do for them. The group is scaled by (rung / AXIS_LEN), so a sprite's size on
      // screen would follow the rung: the same label a hundred times bigger on the metre rung
      // than on the centimetre one. Divided back out, and multiplied by the distance, it is the
      // size it was when the whole widget was pinned — the text beside a scale bar, which does
      // not grow with the bar.
      const words = dist / (HUD_REF * rung.mm);
      for (const { sprite, base } of gizmo?.labels ?? []) {
        sprite.scale.set(base.x * words, base.y * words, base.z);
      }
    };

    // WHICH RUNG THE SHOT IS ON: the ladder step whose arm comes out nearest the size the arms
    // are meant to be. The camera looks at the world's centre and the widget is drawn at that
    // distance, so the distance to the target is the distance to the arms.
    const pickGizmoStep = () => {
      const h = viewHeight();
      const dist = camera.position.distanceTo(controls.target) || 1;
      const target = armTarget(h);
      if (!(target > 0) || !h) return;
      let want = 0;
      let best = Infinity;
      for (let i = 0; i < GIZMO_STEPS.length; i += 1) {
        const px = armPixels(GIZMO_STEPS[i].mm, h, dist);
        if (!(px > 0)) continue;
        const off = Math.abs(Math.log(px / target));
        if (off < best) { best = off; want = i; }
      }
      if (!Number.isFinite(best)) return;
      // HYSTERESIS, because the rung is REBUILT and not scaled: three arrows and three label
      // textures, and a camera resting exactly on a threshold — a slow zoom, a glide easing to
      // a stop — would rebuild them every frame. A rung has to be clearly better than the one
      // in hand before it takes over. See HUD_RUNG_HOLD for how much "clearly" is, and why it
      // is a tenth of what it was.
      if (gizmoStep < 0) setGizmoStep(want);
      else if (want !== gizmoStep) {
        const here = Math.abs(
          Math.log(armPixels(GIZMO_STEPS[gizmoStep].mm, h, dist) / target),
        );
        if (here - best > HUD_RUNG_HOLD * Math.LN10) setGizmoStep(want);
      }
    };

    const loader = new GLTFLoader();
    const objects = new Map<string, import('three').Object3D>();
    // The cover's floor shadow, once asked for (see floorShadow), and released with the scene.
    let shadowFloor: {
      sun: import('three').DirectionalLight; floor: import('three').Mesh;
    } | null = null;
    // The loaded .glb per part, beside the holder it hangs in — what the clash check reads.
    const bodies: ClashSource = new Map();
    // And the red overlay each part carries, with the mesh whose faces it indexes.
    const painted = new Map<string, import('three').LineSegments>();
    // WHAT EACH PART OCCUPIES, in its own holder's frame, measured once — see the loop below.
    const bounds = new Map<string, import('three').Box3>();
    // The selection outlines, one per part, hidden until a part is picked.
    // The RIM group of each part — what fitShell grows. Its parent holds the stencil mask
    // beside it and is what gets shown and hidden; see shellVisible.
    const shells = new Map<string, import('three').Object3D>();
    const shellVisible = new Map<string, import('three').Object3D>();
    // And what each part measures in its holder's frame, which the rim is re-fitted against
    // every frame — see fitShell.
    const shellSize = new Map<string, import('three').Vector3>();
    const outlinable: {
      instanceId: string; holder: import('three').Object3D;
      mesh: import('three').Object3D; size: import('three').Vector3;
    }[] = [];
    // Blender's selection orange, and one material for every shell: they are never outlined
    // two at a time, and a colour is not a fact about a part.
    //
    // THROUGH EVERYTHING, and that is the point of it. The hull was depth-tested, so a part
    // INSIDE another one — which is where a fastener lives, and a barrel nut is only ever
    // selected because it is about to go into a rail — had its rim fail that test across the
    // whole of the part covering it, and clicking it lit nothing up. Two pixels of rim (see
    // RIM_PX) need only be covered by a hair to disappear entirely.
    //
    // AND STILL ONLY THE RIM. Turning the depth test off alone paints the hull's back faces
    // wherever the part is — which is the part filled solid orange, visible or buried, since
    // the parts are drawn before it and nothing then covers it back up. The rim is by
    // definition the hull MINUS the part's own footprint, so that footprint has to be taken
    // out, and it cannot be taken out by depth: the whole point is to ignore depth.
    //
    // So it is taken out by STENCIL. A second copy of the part, at its true size, draws first
    // with no colour and no depth and marks every pixel it covers; the hull then draws only
    // where that mark is absent. What is left is the rim and nothing else — around a part in
    // plain sight exactly as before, and around one buried inside another where before there
    // was nothing at all.
    //
    // `depthTest: false` needs a renderOrder to go with it — with nothing to sort by, the
    // shell would be drawn in whatever order the scene graph happens to hold it. Above the
    // parts and below the annotations: it is a fact about the geometry, and the crosses and
    // labels are marks ON it. See DRAW_ORDER, whose numbering this joins.
    const outlineMat = new THREE.MeshBasicMaterial({
      color: 0xf97316, side: THREE.BackSide, depthWrite: false, depthTest: false,
      stencilWrite: true, stencilRef: 1, stencilFunc: THREE.NotEqualStencilFunc,
    });
    // The part's own footprint, written to the stencil and to nothing else. Ignores depth for
    // the same reason the rim does: a buried part covers those pixels just as much as a
    // visible one, and a mask that stopped at the rail would let the rim fill the half of the
    // part the rail is in front of.
    //
    // AND BOTH SIDES OF IT, WHICH IS WHAT MAKES THE RIM DISAPPEAR FROM INSIDE THE PART.
    //
    // Single-sided, the mask is drawn only where a face points at the camera — so with the
    // camera inside a part it marks nothing at all, every face there pointing away. The hull
    // is BackSide and ignores depth, so its back faces then cover the entire viewport with
    // nothing masked out of them: the whole screen goes orange the moment you zoom in far
    // enough to enter the part. Double-sided, the mask marks the part's footprint whichever
    // way its faces happen to point, the hull is excluded from all of it, and the rim is
    // visible from outside and nowhere else — which is the only place a rim means anything.
    //
    // From outside it is the same picture as before: a closed surface's back faces project
    // onto exactly the silhouette its front faces do, and the stencil op is a replace, so
    // marking those pixels twice is marking them once.
    const maskMat = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      colorWrite: false, depthWrite: false, depthTest: false,
      stencilWrite: true, stencilRef: 1, stencilFunc: THREE.AlwaysStencilFunc,
      stencilZPass: THREE.ReplaceStencilOp,
    });
    const unscaled: string[] = [];
    // The same parts by id, which is what the per-frame redraw of their boxes is keyed by.
    const inUnits = new Set<string>();
    // AND WHAT EACH PART'S MESH MEASURES AT ITS BOTTOM AND ITS TOP, for the parts whose record
    // has ends to hold them against. See measureEnds, and the box's `ends`.
    const endsOf = new Map<string, Ends>();
    // AND EACH PART'S OWN MESHES, where its file holds several — see onPieces and hidePieces.
    // EVERY MESH BY THE FILE IT CAME FROM, and the instance it is drawn for (see meshFrame).
    const meshByUrl: { inst: string; url: string; obj: import('three').Object3D }[] = [];
    const piecesOf = new Map<string, {
      name: string; obj: import('three').Object3D; box?: import('three').Box3;
      // Where it stands assembled, the move its own node makes exploded, that move in the frame
      // its box is drawn in, and the box it is drawn at on the way.
      home?: import('three').Vector3; local?: import('three').Vector3;
      shift?: import('three').Vector3; at?: import('three').Box3;
      // ITS COPIES IN THE SELECTION OUTLINE — the mask and the grown rim are clones of the whole
      // part (see the shells below), so each holds this mesh again, and exploding has to carry
      // those along or the orange outline stays where the part was assembled.
      twins?: { mask: import('three').Object3D; rim: import('three').Object3D };
    }[]>();
    // WHICH OF A PART'S MESHES ARE ASKED HIDDEN, kept until its file is in (Louis 2026-11-02): asked
    // before it loaded — a part's earlier states, beside it until its screws are in (see showStates) —
    // the ask found no meshes, was never made again, and the state drew over the part it stands in
    // for: two rails in one place, striped where each wins the depth test.
    const piecesOff = new Map<string, Set<string>>();
    // HOW FAR EACH PART'S MESHES ARE SPREAD, 0 together to 1 apart, and the slide between the two
    // it is on — see explode.
    const apart = new Map<string, { e: number; from: number; to: number; t0: number }>();
    const spread = (id: string, e: number) => {
      const grown = shells.get(id)?.scale;
      for (const piece of piecesOf.get(id) ?? []) {
        if (!piece.home || !piece.local) continue;
        piece.obj.position.copy(piece.home).addScaledVector(piece.local, e);
        if (!piece.twins) continue;
        piece.twins.mask.position.copy(piece.obj.position);
        // THE RIM IS GROWN ABOUT THE PART'S CENTRE (see fitShell), so a move inside it comes out
        // multiplied by that growth. Divided back out, the rim's copy travels exactly as far as
        // the mesh does and surrounds it where it lands, as it surrounds it assembled.
        piece.twins.rim.position.copy(piece.local).multiplyScalar(e);
        if (grown) piece.twins.rim.position.divide(grown);
        piece.twins.rim.position.add(piece.home);
      }
    };
    // Their boxes, built the first time one is asked for (see boxPieces) rather than for every
    // mesh at load — a file of thirty-six meshes would be thirty-six sets of label textures
    // nobody looked at. Keyed by part and mesh name; `boxedPieces` is which are switched on.
    const pieceExtents = new Map<string, ExtentDims>();
    const boxedPieces = new Map<string, Set<string>>();
    const pieceKey = (id: string, name: string) => `${id}\u001f${name}`;
    // A size nobody printed, on every axis — what a mesh's second row reads where no part says.
    const UNKNOWN_SIZE: { x: number | null; y: number | null; z: number | null } = {
      x: null, y: null, z: null,
    };

    // ONE FILE, ASKED FOR AFTER ANY DOWNLOAD OF IT ALREADY UNDER WAY — the public page's, which
    // asks for its meshes ahead of the screen (see meshPreload). Then READ ONCE FOR THE PAGE
    // (loadCopy), each use a copy of its own to stand somewhere else: a piece stands the same
    // part's file four times, and the next sub-step stands most of them again. Read per use, a
    // sub-step opened five seconds after its files were in.
    const fetched = (url: string) => {
      const asked = performance.now();
      return Promise.resolve(meshArriving(url)).then(() => {
        const preloaded = performance.now();
        return (loadCopy(loader, url) as Promise<import('three').Group | null>).then((got) => {
          if (load) {
            // The browser's own timing of the download: when it started, how long until the first
            // byte, how long the body took, and how big it was (0 bytes is the browser's cache).
            const net = performance.getEntriesByName(new URL(url, window.location.href).href)
              .at(-1) as PerformanceResourceTiming | undefined;
            const ms = (n: number) => Math.round(n);
            load.say(`mesh ${/mesh\/(\d+)/.exec(url)?.[1] ?? url.slice(-24)} done ${since()}`
              + ` (waited on preload ${ms(preloaded - asked)} ms, read ${ms(performance.now() - preloaded)} ms)`
              + (net ? ` | net from +${ms(net.startTime - t0)} ms, first byte ${ms(net.responseStart - net.startTime)} ms,`
                + ` body ${ms(net.responseEnd - net.responseStart)} ms, ${net.transferSize} B` : ' | no net entry')
              + (got ? '' : ' FAILED'));
          }
          return got;
        });
      });
    };
    // The parts each piece was put together from, in its list's order — what `placed` reads
    // back when the piece goes into the next one.
    const madeOf = new Map<string, import('three').Object3D[]>();
    const loaded = await Promise.all(parts.map(async (p) => {
      if (!p.pieces?.length) {
        const scene = await fetched(p.url);
        if (scene) meshByUrl.push({ inst: p.instanceId, url: p.url, obj: scene });
        return scene && { part: p, scene };
      }
      // A PIECE, STOOD UP FROM ITS PARTS: each part's own scene where the sign-off left it,
      // under one node taking the scene's millimetres back to the metres a file is in — which
      // is the file a bake of it used to be, so everything below reads it as one.
      const made = await Promise.all(p.pieces.map((q) => fetched(q.url)));
      if (made.some((m) => !m)) return null;
      const out = new THREE.Group();
      out.scale.setScalar(1 / MM_PER_TRUE_UNIT);
      (made as import('three').Group[]).forEach((m, i) => {
        m.matrix.fromArray(p.pieces![i].m);
        m.matrix.decompose(m.position, m.quaternion, m.scale);
        m.name = m.userData.name = p.pieces![i].name;
        out.add(m);
        meshByUrl.push({ inst: p.instanceId, url: p.pieces![i].url, obj: m });
      });
      madeOf.set(p.instanceId, out.children.slice());
      const scene = new THREE.Group();
      scene.add(out);
      return { part: p, scene };
    }));
    // Cancelled while the meshes were downloading. `cleanup` above has already been called
    // by the caller's teardown, so the canvas is gone — this only stops the rest of the
    // build from running against a scene nobody will ever see.
    if (!alive) return;
    load?.say(`all meshes in ${since()}`);
    // EACH STRETCH OF THE BUILD AFTER THE MESHES ARE IN, timed: it is seconds a tile, on the one
    // thread every tile shares (exploration, Louis 2026-10-23).
    let stepFrom = performance.now();
    const step = (name: string) => {
      const now = performance.now();
      load?.say(`  ${name}: ${Math.round(now - stepFrom)} ms`);
      stepFrom = now;
    };
    if (loaded.some((m) => m === null)) opts.onState?.('failed');

    for (const entry of loaded) {
      if (!entry) continue;
      const { part, scene: mesh } = entry;

      // ITS OWN MESHES, where it has several: the file's top-level nodes that hold geometry,
      // under the names the file gives them. The loader keeps the original in userData — its
      // `name` is sanitised, "Side rail Left" arriving as "Side_rail_Left".
      //
      // THROUGH A WRAPPER, where the file has one: a node holding nothing but the nodes under
      // it. The written runs turned upright after the fact (see the orient one-off) had every
      // mesh put under one `notice_orient` node, and reading the top level alone found one
      // piece on each of them.
      const holdsGeometry = (node: import('three').Object3D) => {
        let holds = false;
        node.traverse((o) => { if ((o as import('three').Mesh).isMesh) holds = true; });
        return holds;
      };
      // NOT INTO ONE MESH'S MATERIALS: a glTF mesh with two materials loads as a node holding
      // `name`, `name_1`… — a drilled part's wood and its bores (see _cut_material). One piece.
      const isPrimitives = (node: import('three').Object3D) => node.children.length > 1
        && node.children.every((c, i) => (c as import('three').Mesh).isMesh && !c.children.length
          && c.name === (i ? `${node.children[0].name}_${i}` : node.children[0].name));
      let level: import('three').Object3D = mesh;
      for (;;) {
        const inner = level.children.filter(holdsGeometry);
        const only = inner.length === 1 ? inner[0] : null;
        if (!only || (only as import('three').Mesh).isMesh || !only.children.length || isPrimitives(only)) break;
        level = only;
      }
      // A NAME FOR EVERY ONE, and a different one: they are what a row says and what hiding is
      // keyed on. A file that names nothing — a converted CAD part arrives as two unnamed nodes —
      // is numbered in its own order rather than listed as blank rows that hide each other.
      const seen = new Set<string>();
      const pieces = level.children
        .filter(holdsGeometry)
        .map((child, i) => {
          let name = String(child.userData.name ?? child.name ?? '').trim() || `#${i + 1}`;
          if (seen.has(name)) name = `${name} (${i + 1})`;
          seen.add(name);
          return { name, obj: child };
        });
      if (pieces.length > 1) {
        piecesOf.set(part.instanceId, pieces);
        const off = piecesOff.get(part.instanceId);
        if (off) for (const piece of pieces) piece.obj.visible = !off.has(piece.name);
        opts.onPieces?.(part.instanceId, pieces.map((p) => p.name));
      }

      // INTO MILLIMETRES, through the same helper the mesh viewer and the scale buttons ask,
      // and asked the SAME WAY — with the true-scale flag. A generated mesh arrives
      // normalised to about a unit across, which is a proportion and not a size; a CAD or
      // turned one is already the part and needs no drawing to anchor it. Dropping that
      // second argument left a 12 mm washer at 0.012 units beside a screw scaled to
      // millimetres, which is the one way to make correct arithmetic look like a broken
      // animation.
      const rawBox = new THREE.Box3().setFromObject(mesh);
      const raw = rawBox.getSize(new THREE.Vector3());
      opts.onMeasured?.(part.instanceId, { x: raw.x, y: raw.y, z: raw.z });
      // AND WHAT SHAPE IT IS INSIDE THAT BOX — see partProfile. Sampled HERE, beside the
      // measurement, because this is the last moment the mesh stands in the file's own axes:
      // the scene turns its root a quarter turn to put Z up, and every triangle with it.
      //
      // ONLY WHEN SOMEBODY ASKS FOR IT. It is a pass over every triangle of the part, which is
      // nothing beside the download that just finished and is not nothing fourteen times over
      // on an assembly where no hole will ever be placed.
      if (opts.onProfiled) {
        const profile = sampleProfile(mesh, {
          min: [rawBox.min.x, rawBox.min.y, rawBox.min.z],
          size: [raw.x, raw.y, raw.z],
        });
        if (profile) opts.onProfiled(part.instanceId, profile);
      }
      const scale = mmScale({ x: raw.x, y: raw.y, z: raw.z }, part.dims, part.trueScale,
                            part.builtMm);
      if (scale) mesh.scale.setScalar(scale);
      else {
        unscaled.push(part.label);
        inUnits.add(part.instanceId);
      }

      // RE-ORIGINED TO ITS OWN CENTRE, which is what makes the seat offsets usable. A
      // reconstruction's origin is wherever the generator left it and means nothing, so
      // "concentric" would otherwise be a different offset for every part. Centred, a seat
      // of [0,0,0] is a washer sitting on a screw's axis, which is nearly right before any
      // nudging — and the nudge is then a small number with an obvious sign.
      const box = new THREE.Box3().setFromObject(mesh);
      mesh.position.sub(box.getCenter(new THREE.Vector3()));

      // AND EACH OF ITS MESHES' OWN BOX, in the frame the part's box is drawn in — now, while
      // the mesh is scaled and centred and has no holder yet, so its world is that frame. Kept
      // for the same reason `bounds` is: a box redrawn every frame must not walk the vertices
      // every frame. The other hand of a pair is the holder reflected in x, and so is the box.
      const ownPieces = piecesOf.get(part.instanceId);
      if (ownPieces) {
        mesh.updateMatrixWorld(true);
        for (const piece of ownPieces) piece.box = new THREE.Box3().setFromObject(piece.obj);
        // AND WHERE EACH GOES WHEN THE MODEL IS EXPLODED — see explodePieces. Worked out in the same
        // unreflected frame, and turned into the move its own node makes: that node's parent is
        // scaled and turned, and a shift in the part's frame is not a shift in its.
        const shifts = explodeShifts(ownPieces.map((p) => p.box!), opts.explodeAlong);
        ownPieces.forEach((piece, i) => {
          const shift = new THREE.Vector3(shifts[i].x, shifts[i].y, shifts[i].z);
          const toParent = new THREE.Matrix3().setFromMatrix4(piece.obj.parent!.matrixWorld).invert();
          piece.home = piece.obj.position.clone();
          piece.local = shift.clone().applyMatrix3(toParent);
          piece.shift = shift;
          piece.at = new THREE.Box3();
          if (part.mirror) {
            const { min, max } = piece.box!;
            [min.x, max.x] = [-max.x, -min.x];
            shift.x = -shift.x;
          }
        });
      }

      // AND KEPT, because the measurement lines are redrawn on every frame of a movement and
      // this is the number they are drawn from. Box3.setFromObject walks every vertex of
      // every mesh under it: once per part is nothing, sixty times a second on a tessellated
      // screw is the frame budget. Centred on the line above, so what it occupies is its own
      // size either side of the holder's origin and there is nothing left to measure.
      const half = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
      bounds.set(part.instanceId, new THREE.Box3(half.clone().negate(), half.clone()));

      // AND THIS PART'S OWN MEASURED BOX, built from those same bounds. On by default, like
      // the origin crosses: what is on screen is measured unless somebody says otherwise.
      if (opts.extentWords && opts.faceWords) {
        const dims = makeExtentDims(THREE, opts.extentWords, opts.faceWords, opts.wantedWords);
        annot.add(dims.group);
        extents.set(part.instanceId, dims);
        ruled.add(part.instanceId);
        labelled.add(part.instanceId);
        boxed.add(part.instanceId);
        // What the manual says this part measures, kept beside its box: the second row is
        // drawn from it, and only where a caller asked for one.
        if (part.dims) intended.set(part.instanceId, part.dims);
      }

      // The wrapper carries the assembly pose; the mesh inside carries its own centring, so
      // the two never have to be composed by hand.
      const holder = new THREE.Group();
      holder.add(mesh);
      // AND REFLECTED, for the other hand of a pair — see ScenePart.mirror.
      //
      // ON THE HOLDER AND NOT ON THE MESH, which is the whole care needed here. Everything
      // that is drawn ON the part hangs off this holder and not off the mesh inside it: the
      // hole marks a drill form types, the origin cross, the selection shell, the red clash
      // patch. Reflect the mesh alone and the part turns over while its marks stay where they
      // were — a bore drawn on the wrong end of a reflected rail, which is worse than no
      // reflection at all. Reflect the holder and the part and everything said about it turn
      // together.
      //
      // NOT the measured box, which hangs off `annot` and is deliberately left alone: its
      // extents are centred on the origin and a reflection leaves them exactly where they
      // were, and its six face words then land on the faces of the hand being LOOKED at —
      // the face on the screen's left is the left face of whichever unit is drawn. Mirroring
      // the labels too would write them backwards for nothing.
      //
      // A NEGATIVE DETERMINANT REVERSES THE WINDING of every triangle, so a back-face cull
      // would throw away exactly the faces pointing at you and keep the ones behind them —
      // the part comes back hollow, lit from the inside. Nothing here has to say so:
      // attachDrawModes sets every material in this scene to DoubleSide, for its own reason
      // (a camera that goes inside a part), and that answer covers this one. Lighting needs no
      // help either, three building its normal matrix from the inverse transpose, which
      // reflects along with the rest.
      if (part.mirror) holder.scale.x = -1;
      holder.visible = false;
      root.add(holder);
      objects.set(part.instanceId, holder);
      meshOf.set(part.instanceId, mesh);
      // THE MESH'S OWN BOTTOM AND TOP, measured once it hangs where the box is measured from —
      // only where somebody will read them: a wanted row to stand under, and a record with a
      // pair of ends to be compared with.
      const wantsEnds = part.dims && ((part.dims.xBottom != null && part.dims.xTop != null)
        || (part.dims.yBottom != null && part.dims.yTop != null));
      const partBox = bounds.get(part.instanceId);
      if (opts.wantedWords && wantsEnds && partBox) {
        const ends = measureEnds(THREE, holder, mesh, partBox);
        if (ends) endsOf.set(part.instanceId, ends);
      }
      // AND THE PART ON ITS OWN, kept apart from the holder it hangs in. By the time anything
      // asks, that holder also carries the part's wireframe and a hidden copy of it for the
      // selection outline — see clashParts, which measures surfaces and must be handed the
      // surfaces rather than left to recognise them.
      bodies.set(part.instanceId, { holder, body: mesh });

      // THE RED PATCH THIS PART MAY HAVE TO WEAR, built empty and kept empty until a
      // measurement says otherwise.
      //
      // ITS OWN OBJECT, and not a second colour on the part's material or on its wireframe.
      // The wireframe is a WireframeGeometry, which dedupes shared edges and so has no
      // triangle left to point at; the material is one colour for the whole part by
      // construction. A separate LineSegments can be told about any subset of faces, drawn
      // over both draw modes, and emptied without touching either.
      //
      // A CHILD OF THE HOLDER, AND DRAWN FROM THE MEASUREMENT'S OWN COPY OF THE MESH.
      //
      // Not from the mesh on screen, and that is the bug this shape exists to make
      // impossible. A face index only means something against a particular ordering of the
      // faces, and three-mesh-bvh REORDERS the index buffer of whatever geometry it builds a
      // tree over — sorting the triangles is how the tree is built. The measurement works on
      // a baked copy, so the copy is reordered and the mesh on screen is not, and the same
      // number names two different triangles in the two of them.
      //
      // Drawn from the copy, both sides speak the same order — and since the measuring is a
      // worker's, with a copy of that copy, only because its trees are built INDIRECT and sort
      // nothing (see treeOf): the same bug came back the day the worker did. The copy is already in the
      // holder's frame in millimetres (see clashParts), which is why this hangs off the
      // holder rather than off the mesh, and needs no transform of its own.
      //
      // What that cost when it was wrong is worth writing down, because it did not look like
      // a bug: the patch appeared on the right part, in the right numbers, scattered over the
      // whole of it. Against a textured surface it read as a strange result; in wireframe,
      // one red edge among a hundred and thirty thousand teal ones, it read as nothing at all.
      //
      // Its buffer is allocated once at its full size and rewritten per frame — a clip
      // playing shows this patch move, and sixty geometries a second is sixty allocations.
      const marked = new THREE.BufferGeometry();
      marked.setAttribute('position', new THREE.BufferAttribute(new Float32Array(CLASH_FACES * 18), 3));
      marked.setDrawRange(0, 0);
      // DRAWN LAST AND THROUGH EVERYTHING, which is not decoration on either count.
      //
      // Its lines are the edges of triangles the part's own wireframe is ALSO drawing, at
      // exactly the same depth — and three's depth test passes on equal, so of two coincident
      // lines the one drawn second wins. Left to the default order the teal wireframe covered
      // every red line on the mesh and the patch was invisible with nothing wrong anywhere:
      // the marks were computed, written into the buffer, and shown, and the picture had no
      // red in it.
      //
      // Depth off for a plainer reason: an overlap is INSIDE the parts by definition. A patch
      // that can only be seen when nothing hides it is a patch that can never be seen.
      const paint = new THREE.LineSegments(
        marked,
        new THREE.LineBasicMaterial({ color: CLASH_COLOUR, toneMapped: false, depthTest: false }),
      );
      paint.renderOrder = 2;
      paint.frustumCulled = false;
      paint.raycast = () => {};
      paint.visible = false;
      holder.add(paint);
      painted.set(part.instanceId, paint);

      // Everything the outline below needs, kept until the draw modes have been attached.
      outlinable.push({ instanceId: part.instanceId, holder, mesh, size: box.getSize(new THREE.Vector3()) });
    }
    step('meshes placed in the scene');
    if (unscaled.length) opts.onUnscaled?.(unscaled);
    // Every part that loaded is in file units, so the arms are too — rebuilt without their
    // lengths if the loop already drew them with some.
    worldInUnits = inUnits.size > 0 && inUnits.size === objects.size;
    if (worldInUnits && gizmoStep >= 0) {
      const at = gizmoStep;
      gizmoStep = -1;
      setGizmoStep(at);
    }

    step('gizmo scale');
    if (opts.onStats) {
      let vertices = 0;
      let triangles = 0;
      root.traverse((o) => {
        const g = (o as import('three').Mesh).geometry;
        if (!g?.attributes?.position) return;
        vertices += g.attributes.position.count;
        triangles += (g.index ? g.index.count : g.attributes.position.count) / 3;
      });
      opts.onStats({ vertices, triangles: Math.round(triangles) });
    }

    // Textured / wireframe / both, off the same builder the mesh viewer uses. Built once
    // over the whole root, so both parts answer one press — which is the point here rather
    // than a convenience: the two are being compared, and one drawn textured beside one
    // drawn as edges would be two objects, not two views of one fitting.
    // A COLOUR PER PART, and in the order the parts were given: the fixed part first, then
    // the one being fitted to it, then the tool. Wireframes do not hide what is behind them,
    // so a washer sliding down a screw drawn in one colour is a single thicket of lines with
    // no edge between the two things being compared — which is the whole of what the mode is
    // for. The mesh's holder is the part; whichever holder a mesh sits under decides it.
    const order = [...objects.values()];
    const partOf = (mesh: import('three').Object3D) => {
      let node: import('three').Object3D | null = mesh;
      while (node && !order.includes(node)) node = node.parent;
      return node;
    };
    // NO TWO PARTS IN CONTACT IN ONE COLOUR (see wireColours) — fixed once for this scene, on the
    // first pose it shows (assembled), and never again: switching the wireframe or exploding the
    // parts does not recolour them.
    let wirePalette: Map<string, number> | null = null;
    const paletteNow = () => {
      if (wirePalette) return wirePalette;
      const from = performance.now();
      wirePalette = wireColours(THREE, bodies, () => null);
      load?.say(`    of which the parts' wire colours (which touch which): ${Math.round(performance.now() - from)} ms`);
      return wirePalette;
    };
    step('stats');
    const draw = attachDrawModes(THREE, root, (mesh) => {
      const node = partOf(mesh);
      const id = [...objects.entries()].find(([, o]) => o === node)?.[0];
      return (id != null ? paletteNow().get(id) : undefined) ?? WIRE_PALETTE[0];
    }, opts.keepSolid ? (mesh) => {
      const node = partOf(mesh);
      const id = [...objects.entries()].find(([, o]) => o === node)?.[0];
      return id != null && opts.keepSolid!(id);
    } : undefined);
    step('draw modes');
    draw.apply('textured');

    // WHICH PART WEARS THE OUTLINE, and whether an outline is a thing that can be worn at
    // all just now.
    //
    // The shell is a rim only because the real part is drawn over the middle of it. Switch to
    // wireframe and the part stops being drawn, leaving the shell's inside faces to read as a
    // flat orange silhouette of the thing you were trying to look through. So the outline is
    // a textured-mode answer, and in wireframe the selection is what the panel says it is.
    let selected: string | null = null;
    let lit = new Set<string>();
    let drawn: DrawMode = 'textured';
    const showShells = () => {
      for (const [id, shell] of shellVisible) {
        shell.visible = (id === selected || lit.has(id)) && drawn === 'textured';
      }
    };

    // HOW THICK THE RIM IS: A FEW PIXELS, and that is the whole of this function's argument.
    //
    // It was a fixed fraction of the part — 1.2% of its longest extent — which is stable in the
    // world and therefore not stable on screen. A 400 mm side rail got a 4.8 mm border, three
    // or four pixels of orange; a 20 mm barrel nut standing beside it got 0.24 mm, which at the
    // zoom that fits the rail is a quarter of a pixel. The nut WAS selected, its panel was open,
    // and the picture said nothing — so the outline read as "small parts cannot be picked".
    //
    // A border is a thing on the SCREEN. So it is measured there: what a pixel is worth in the
    // world at that part's own distance, times a few. Capped against the part's own size, since
    // a rim wider than the thing it surrounds is a blob rather than an outline — which is what
    // a fastener would become when the shot is pulled right back.
    //
    // Every frame, for the one visible shell: it is a distance, a tangent and three divisions,
    // and it has to follow the wheel as well as the selection.
    // ABOVE THE PARTS, BELOW THE MARKS ON THEM. The clash paint sits at 2 and the origin
    // crosses at 6, so the selection goes between: a rim that hid a clash would be hiding the
    // one thing the phase is being read for.
    const SHELL_ORDER = 3;
    const RIM_PX = 2.5;
    const rimAt = new THREE.Vector3();
    // Every outline on show: the selected part's and the highlighted ones'.
    const fitShell = () => {
      for (const id of new Set([...(selected ? [selected] : []), ...lit])) fitOne(id);
    };
    const fitOne = (id: string) => {
      const shell = shells.get(id);
      const size = shellSize.get(id);
      if (!shell || !shellVisible.get(id)?.visible || !size) return;
      const h = viewHeight();
      shell.getWorldPosition(rimAt);
      const dist = camera.position.distanceTo(rimAt) || 1;
      const halfWorld = dist * Math.tan((camera.fov * Math.PI) / 360);
      if (!(halfWorld > 0) || !h) return;
      const thick = Math.min(
        (RIM_PX * 2 * halfWorld) / h,
        0.15 * Math.max(size.x, size.y, size.z),
      );
      shell.scale.set(
        1 + (2 * thick) / Math.max(size.x, 1e-6),
        1 + (2 * thick) / Math.max(size.y, 1e-6),
        1 + (2 * thick) / Math.max(size.z, 1e-6),
      );
      // An exploded part's rim pieces are placed against this growth (see spread): re-placed
      // whenever it changes, which is whenever the camera moves.
      const e = apart.get(id)?.e ?? 0;
      if (e) spread(id, e);
    };

    // THE ORANGE OUTLINE, ONE PER PART, BUILT HIDDEN. Blender's, and by Blender's trick: a
    // copy of the part drawn INSIDE OUT and a hair larger, so all the camera sees of it is
    // the rim standing out past the real part. There is no post-processing pass anywhere in
    // this app, and one selection border does not earn a second render target.
    //
    // AFTER attachDrawModes ON PURPOSE. That walks the root and takes every mesh under it as
    // a part to be drawn textured or as edges, and a shell caught by it would come out as a
    // second wireframe of a part that is only being pointed at.
    //
    // The copy shares its geometry — clone() does — because a border must not double the
    // memory of a tessellated screw. It does not share the material: that one is flat orange,
    // unlit, and drawn back-face only.
    //
    // GROWN ABOUT THE PART'S CENTRE, per axis. Inside its own group, so the scaling happens
    // around the holder's origin — which the centring above made the middle of the part —
    // rather than around whatever origin the .glb happened to be authored at. Per axis
    // because a screw is 78 mm by 13: one flat percentage is a millimetre and a half of
    // border along it and a quarter across it, which reads as two different outlines.
    step('textured draw mode');
    for (const { instanceId, holder, mesh, size } of outlinable) {
      const copy = mesh.clone(true);
      copy.traverse((child) => {
        const solid = child as import('three').Mesh;
        if (solid.isMesh) solid.material = outlineMat;
      });
      // THE MASK, at the part's true size — the footprint the rim is cut out of. Its own copy
      // and not the enlarged one, since the hole has to be the shape of the PART.
      const mask = mesh.clone(true);
      mask.traverse((child) => {
        const solid = child as import('three').Mesh;
        if (solid.isMesh) solid.material = maskMat;
      });
      // The rim lives in its own group so the growing happens to it alone: the mask is beside
      // it, at 1, and must stay there however far the camera pulls back.
      const rim = new THREE.Group();
      rim.add(copy);
      const shell = new THREE.Group();
      shell.add(mask);
      shell.add(rim);
      // A first thickness only, and the frame loop takes it over the moment the shell is shown
      // — see fitShell, which measures the rim in pixels. Kept because a shell scaled 1:1 with
      // the part it copies is a z-fight rather than a border, and this is what stands in the
      // frame between a selection and the first fit.
      const thick = Math.max(size.x, size.y, size.z) * 0.012;
      rim.scale.set(
        1 + (2 * thick) / Math.max(size.x, 1e-6),
        1 + (2 * thick) / Math.max(size.y, 1e-6),
        1 + (2 * thick) / Math.max(size.z, 1e-6),
      );
      shell.visible = false;
      // The order the materials' disabled depth test leaves undecided — on the groups AND on
      // every mesh in them, because three compares a group's own order BEFORE its children's
      // and a shell whose group said nothing would sort against the parts by position. Same
      // trap the origin crosses hit; see the note by DRAW_ORDER.cross below.
      //
      // The mask FIRST and the rim after it: the rim is drawn where the mask has not written,
      // so a rim that went first would be tested against a stencil nobody had marked yet.
      shell.renderOrder = SHELL_ORDER;
      mask.renderOrder = SHELL_ORDER;
      rim.renderOrder = SHELL_ORDER + 1;
      for (const [group, order] of [[mask, SHELL_ORDER], [rim, SHELL_ORDER + 1]] as const) {
        group.traverse((child) => {
          child.renderOrder = order;
          // NEVER THE THING A CLICK FINDS. These are copies of the part sitting on and just
          // outside it, so a ray reaches them first — and picking would then answer with an
          // outline rather than with a part. Silenced rather than filtered at the call site,
          // so it stays true however the picking is later written.
          child.raycast = () => {};
        });
      }
      holder.add(shell);
      shells.set(instanceId, rim);
      // Each of the part's meshes found again in both copies — by its path from the part's
      // root, which clone() keeps — so exploding moves the outline with them (see spread).
      const pathOf = (obj: import('three').Object3D) => {
        const at: number[] = [];
        for (let o = obj; o !== mesh && o.parent; o = o.parent) at.unshift(o.parent.children.indexOf(o));
        return at;
      };
      const follow = (from: import('three').Object3D, at: number[]) =>
        at.reduce<import('three').Object3D | undefined>((o, i) => o?.children[i], from);
      for (const piece of piecesOf.get(instanceId) ?? []) {
        const at = pathOf(piece.obj);
        const inMask = follow(mask, at);
        const inRim = follow(copy, at);
        if (inMask && inRim) {
          piece.twins = { mask: inMask, rim: inRim };
          inMask.visible = inRim.visible = piece.obj.visible;
        }
      }
      shellVisible.set(instanceId, shell);
      shellSize.set(instanceId, size.clone());
    }


    // THE HOLE PREVIEW'S OWN RED, which is the red this app uses for "this is wrong or this is
    // about to change" and is the one colour no part is ever painted. Unlit and drawn over the
    // part like every other mark here: a ring shaded by the room would go dark exactly where a
    // face turns away, which is where a hole in it is hardest to see.
    //
    // DOUBLE-SIDED, because a ring lies IN a face and half the ways of looking at that face
    // put its back to you.
    const drillMat = new THREE.MeshBasicMaterial({
      color: 0xef4444, depthTest: false, depthWrite: false, transparent: true, opacity: 0.95,
      side: THREE.DoubleSide,
    });
    // AND AMBER WHERE IT IS NOT ON THE PART. The mark is put where a drill coming in along the
    // face's normal would first touch material; where the ray finds none — past a taper, in a
    // notch, beyond the end — it falls back to the bounding box's own plane, which is a place
    // in the air. Two colours because those are two different pictures and they look identical
    // otherwise: red is "the drill touches here", amber is "there is nothing under this spot".
    const drillAirMat = new THREE.MeshBasicMaterial({
      color: 0xf59e0b, depthTest: false, depthWrite: false, transparent: true, opacity: 0.95,
      side: THREE.DoubleSide,
    });
    // AND THE WALL BETWEEN THE TWO ENDS, in the same two colours and much fainter. It is the
    // only part of this mark that has a length, so it is also the only part that would hide
    // the part it is drawn on: at the mouth's own opacity a 30 mm bore is a red rod laid over
    // the picture, and what somebody is looking at is the timber around it. Faint, the two
    // ends stay the thing the eye lands on and the wall is what joins them.
    const bore = (of: import('three').MeshBasicMaterial) => new THREE.MeshBasicMaterial({
      color: of.color, depthTest: false, depthWrite: false, transparent: true, opacity: 0.3,
      side: THREE.DoubleSide,
    });
    const drillBoreMat = bore(drillMat);
    const drillAirBoreMat = bore(drillAirMat);
    // THE WOUND BORDERS A CUT HAS ALREADY LEFT, a hue apart each.
    //
    // PURPLE AND NOT RED, because red means "about to change" everywhere else in this app and
    // these have already changed: they are what a cut LEFT, not what a press would take.
    //
    // A HUE PER RING, and that is the whole reason they are drawn. A through hole leaves two
    // borders and a shell with an inner layer leaves two more, and they are closed one at a
    // time — so the question being asked of this picture is never "are there borders" but
    // "is the one I am looking at among them". One colour for all of them cannot answer it.
    // Four, because four is what a through hole in a two-layer shell leaves; beyond that they
    // repeat, and a fifth ring on one bore is a thing to be surprised by anyway.
    const ringColours = [0xa855f7, 0xec4899, 0x6366f1, 0x14b8a6];
    // TUBES AND NOT LINES, which is why these are mesh materials. A border edge is one pixel
    // wide drawn as a line — WebGL ignores `linewidth`, so there is no wider line to ask for —
    // and one pixel of purple laid over a wireframe of half a million triangles is a rumour.
    // Each segment is drawn as a rod instead, with a real thickness in the part's own units,
    // so it stands off the mesh and reads at a glance from wherever the camera is.
    //
    // Unlit, so purple stays purple wherever the lights are and whichever of the two lighting
    // modes is on: this is a marking on a picture, not a thing in it.
    const ringMats = ringColours.map((color) => new THREE.MeshBasicMaterial({ color }));
    // How fat, as a fraction of the widest border the mark left. Off the borders themselves
    // rather than a fixed number of millimetres, because the same rod has to read on a 4 mm
    // dowel hole and on a 30 mm mortice.
    const RING_FAT = 0.04;

    // AND THE TRIANGLES STEP TWO WOULD ACTUALLY DELETE, drawn as their own EDGES in red.
    //
    // The capsule beside this is the RULE; these are the faces the rule picks, and they are
    // not the same picture. A face goes if its CENTRE is inside the capsule, so one straddling
    // the skin survives with a corner hanging into the hole — which is invisible in a drawing
    // of the capsule and is exactly what somebody finds afterwards, as a fringe round the rim.
    //
    // EDGES AND NOT A FILL, which is the whole point of drawing it at all. The part is being
    // looked at as a wireframe, and a solid red area laid over it hides the one thing being
    // judged: WHICH triangles, and how raggedly the selection follows the smooth skin. Filled,
    // it is a red blob roughly the shape of the capsule — a picture of the rule again, when
    // the question is what the rule picked.
    //
    // The same shape as the wireframe it recolours: LineSegments over the selected faces,
    // one-pixel edges that do not thicken with the camera (see meshDrawModes, where the
    // part's own wires are built the same way).
    //
    // TRANSPARENT, though it is fully opaque, and ordered above the wires. These edges lie
    // exactly on the part's own, so whichever is drawn LAST wins the depth test — and the
    // part's wires are transparent, which puts them in a later pass than any opaque material
    // could reach. Same pass, higher renderOrder, and the red is on top.
    const drillCutMat = new THREE.LineBasicMaterial({
      color: 0xef4444, transparent: true, opacity: 1,
    });
    // AND A PAIR PER ROW COLOUR, built the first time a row wears one and kept.
    //
    // The red above is the app's "this is about to change", which is right for the one hole
    // being typed into the drill form and wrong for a table of four: marked in one colour they
    // say where the holes are and not which row is which, and on this rail — two pairs, both
    // 10 mm, differing only in where they sit — which is which is the whole question.
    //
    // THE AMBER IS NOT REPLACED. It does not mean "hole", it means "there is nothing under
    // this spot": the mark fell back to the bounding box's plane because no ray found
    // material. That is a warning about the reading itself and it has to stay the one colour
    // it has always been, whatever row it belongs to.
    const rowMats = new Map<number, [import('three').MeshBasicMaterial,
      import('three').MeshBasicMaterial]>();
    const rowMat = (colour: number) => {
      let pair = rowMats.get(colour);
      if (!pair) {
        const face = new THREE.MeshBasicMaterial({
          color: colour, depthTest: false, depthWrite: false, transparent: true, opacity: 0.95,
          side: THREE.DoubleSide,
        });
        pair = [face, bore(face)];
        rowMats.set(colour, pair);
      }
      return pair;
    };
    const drills = new Map<string, import('three').Group>();
    // WHICH MARK IS ON SCREEN, as the short string the tick compares against. Empty for none.
    let drawnMark = '';
    // THE HARDWARE SLIDING INTO ITS HOLES (see holeFit), and which drawing of the marks it
    // belongs to: a mesh that finishes loading after the marks were redrawn is not added.
    const sliding: Sliding[] = [];
    let fitGen = 0;
    // AND THE WORLD'S OWN ORIGIN, marked in black. The arms used to stand here and say so;
    // they are off in the corner now (see the widget above), and the point they were drawn
    // from would otherwise be a place on screen with nothing at it — while every position on
    // this page, every seat and every camera centre, is measured from exactly that point.
    //
    // A SMALL CROSS AND NOT A SECOND GIZMO. It says "here", which is all that is left to say
    // once the directions are named elsewhere: no arrowheads, no words.
    //
    // EACH BAR IN ITS OWN AXIS'S COLOUR — the widget's own red, green and blue, from
    // AXIS_COLOURS, so the mark at the origin and the arms in the corner name the directions
    // in one language. It was black, which said "here" and nothing else and left the reader
    // to carry the corner's colours across the picture themselves. Thin with it: a bar that
    // is a hair wide is a direction, and the fat black one read as a solid built at the
    // origin rather than a mark on it.
    const originMats = [AXIS_COLOURS.x, AXIS_COLOURS.y, AXIS_COLOURS.z].map((color) => (
      new THREE.MeshBasicMaterial({
        color, depthTest: false, depthWrite: false, transparent: true, opacity: 0.9,
      })
    ));
    const originCross = new THREE.Group();
    {
      // Sized off the whole assembly, like the parts' crosses are sized off their parts: one
      // absolute length would be a speck beside a chair and a cage round a barrel nut.
      const reach = Math.max(...[...bounds.values()].map((b) => {
        const v = b.getSize(new THREE.Vector3());
        return Math.max(v.x, v.y, v.z);
      }), 0) || 10;
      const arm = reach * 0.04;
      const thick = arm * CROSS_THICK;
      for (const along of [0, 1, 2]) {
        const bar = new THREE.Mesh(
          new THREE.BoxGeometry(
            along === 0 ? arm * 2 : thick,
            along === 1 ? arm * 2 : thick,
            along === 2 ? arm * 2 : thick,
          ),
          originMats[along],
        );
        bar.renderOrder = DRAW_ORDER.worldCross;
        originCross.add(bar);
      }
      // With the arms, from the mount too (see `gizmo` below and AssemblyOpts.gizmo).
      originCross.visible = gizmoShown;
      annot.add(originCross);
    }
    step('outline masks');
    for (const { instanceId, holder } of outlinable) {
      // AN EMPTY GROUP FOR A HOLE THAT MAY BE TYPED. Hung on the holder, so
      // it wears the part's pose without a line of per-frame work; what goes in it is rebuilt
      // on every keystroke, which is two small geometries and cheaper than keeping a ring of
      // the wrong size around to rescale.
      const drilled = new THREE.Group();
      holder.add(drilled);
      drills.set(instanceId, drilled);
    }

    // EVERY BOX PUT WHERE ITS PART IS. Called after anything moves a part — a new t, or a
    // hand dragging one — because a box wears its part's pose and there is no other moment
    // that knows it has changed.
    //
    // NOTHING IS RE-MEASURED HERE, and that is the point of the change: a part's size was
    // taken once, from its own mesh, and turning it cannot alter it. What this does is copy a
    // pose. The box that used to be rebuilt sixty times a second was rebuilt because it was
    // measuring the wrong thing — the room the parts were taking up, which does change.
    //
    // Laid out even while switched off: `show` has no box to work from, and lines left
    // un-updated behind a hidden group come back where the part used to be.
    const span = new THREE.Vector3();
    const pieceShift = new THREE.Vector3();
    const remeasure = () => {
      // THE PICTURE'S OWN SIZE, which is what every label in it is sized against — the
      // biggest visible part, standing in for the shot the pair is framed at. Sized by their
      // own boxes instead, a screw's numbers would be a tenth the height of the seat's beside
      // it, and the two are read from one distance.
      let widest = 0;
      for (const [id, obj] of objects) {
        const local = bounds.get(id);
        if (!obj.visible || !local) continue;
        local.getSize(span);
        widest = Math.max(widest, span.x, span.y, span.z);
      }
      for (const [id, dims] of extents) {
        const obj = objects.get(id);
        const local = bounds.get(id);
        if (!obj || !local) continue;
        dims.group.position.copy(obj.position);
        dims.group.quaternion.copy(obj.quaternion);
        // NOT WHILE ITS MESHES ARE SPREAD, words and all: the box is the part assembled, and round
        // an exploded one it would measure a thing that is not there.
        const whole = obj.visible && !apart.get(id)?.e;
        dims.show(whole && ruled.has(id));
        dims.showFaces(whole && labelled.has(id));
        dims.showBox(opts.boxSwitch ? whole && boxed.has(id) : null);
        dims.update(local, widest, intended.get(id) ?? null, !inUnits.has(id), endsOf.get(id) ?? null);
      }
      // AND THE BOXES OF SINGLE MESHES somebody asked for — see boxPieces. Worn through their
      // part's pose like the part's own box, and gone with their mesh when it is hidden.
      for (const [key, dims] of pieceExtents) {
        const [id, name] = key.split('\u001f');
        const obj = objects.get(id);
        const piece = piecesOf.get(id)?.find((p) => p.name === name);
        if (!obj || !piece?.box) continue;
        dims.group.position.copy(obj.position);
        dims.group.quaternion.copy(obj.quaternion);
        // The box on its name's switch; its numbers on the PART's ruler, which is the ruler for
        // every box on it; no face words, one set per mesh burying the model.
        const on = obj.visible && piece.obj.visible && !!boxedPieces.get(id)?.has(name);
        dims.showBox(on);
        dims.show(on && ruled.has(id));
        dims.showFaces(false);
        // ITS SECOND ROW from the part this mesh is — "?" on every axis nothing printed, and on a
        // mesh no part matches, rather than no row: a size that cannot be checked says so.
        const wanted = opts.wantedWords ? (opts.pieceWanted?.(name) ?? UNKNOWN_SIZE) : null;
        // Where the mesh is, spread or not.
        const e = apart.get(id)?.e ?? 0;
        const at = piece.shift && piece.at
          ? piece.at.copy(piece.box).translate(pieceShift.copy(piece.shift).multiplyScalar(e))
          : piece.box;
        dims.update(at, widest, wanted, !inUnits.has(id), null);
      }
      // AND THE DIMENSION LINES, through the same frame the boxes are drawn in: the file point
      // through the mesh's own scale and centring, reflected with a mirrored hand, moved with its
      // mesh when exploded, then worn through the part's pose.
      for (const [id, lines] of dimLines) {
        const obj = objects.get(id);
        const mesh = meshOf.get(id);
        if (!obj || !mesh || !obj.visible) {
          lines.update(() => false, widest);
          continue;
        }
        mesh.updateMatrix();
        const e = apart.get(id)?.e ?? 0;
        lines.update((out, point, group) => {
          const piece = piecesOf.get(id)?.find((p) => p.name === group);
          if (piece && !piece.obj.visible) return false;
          out.set(point[0] / 1000, point[1] / 1000, point[2] / 1000).applyMatrix4(mesh.matrix);
          if (obj.scale.x < 0) out.x = -out.x;
          if (piece?.shift && e) out.addScaledVector(piece.shift, e);
          out.applyQuaternion(obj.quaternion).add(obj.position);
          return true;
        }, widest);
      }
    };

    // THE PARTS SOMEBODY HAS TAKEN OUT OF THE PICTURE, and the last poses everything was
    // drawn at — so putting one back is the same pass as drawing it, rather than a second
    // way of deciding where things are.
    const hidden = new Set<string>();
    let placed: Placement[] = [];

    const apply = (placements: Placement[]) => {
      placed = placements;
      for (const obj of objects.values()) obj.visible = false;
      for (const p of placements) {
        const obj = objects.get(p.instance.id);
        if (!obj) continue;
        obj.visible = !hidden.has(p.instance.id);
        obj.position.set(p.pose.pos[0], p.pose.pos[1], p.pose.pos[2]);
        obj.quaternion.set(p.pose.rot[0], p.pose.rot[1], p.pose.rot[2], p.pose.rot[3]);
        // `updateMatrix` by hand: three does it at render time, which is after remeasure
        // needs it.
        obj.updateMatrix();
        // AND ITS OWN MESHES THAT ARE STILL ON THEIR WAY HOME (see Instance.inner): the washer of
        // a prepared screw, sliding on before the screw goes in. In the frame the piece's meshes
        // are listed in, which is their parent's.
        const own = piecesOf.get(p.instance.id);
        if (own && p.instance.inner) {
          const off = new Map((p.inner ?? []).map((m) => [m.index, m.offset]));
          own.forEach((piece, i) => {
            if (!piece.home || !p.instance.inner!.some((m) => m.index === i)) return;
            const o = off.get(i) ?? [0, 0, 0];
            piece.obj.position.set(piece.home.x + o[0], piece.home.y + o[1], piece.home.z + o[2]);
          });
        }
      }
      remeasure();
    };

    // FRAMED OVER BOTH ENDS OF THE STEP AT ONCE, so the shot holds everything the step will
    // ever show and the camera never has to move once it is playing.
    // AND THE DISTANCE THE SAME SHOT NEEDS WITH THE ORIGIN IN THE MIDDLE, which is a
    // different number and not a smaller one. The framing above holds the origin off in a
    // corner and fits a sphere around everything; centred on the origin instead, the far side
    // of the assembly is further away than that sphere's radius — up to twice it — so reusing
    // the opening distance would frame the shot home goes to about a body it no longer fits.
    let openedCentred = 1;

    // HOW FAR BACK THE SQUARE-ON SHOT HAS TO STAND, fitted to the actual silhouette.
    //
    // It used to fit a SPHERE — the distance from the origin to the furthest corner — to the
    // vertical field, and then widen it by 1.6 for the labels. Three multipliers stacking: a
    // sphere around a 735 × 482 × 135 chair is mostly empty air at the corners; centred on
    // the origin its radius is the whole height rather than half of it; and fitting to the
    // vertical field throws away the width of a viewport that is two and a half times as wide
    // as it is tall. Home ended up nearly three times further out than it needed to be.
    //
    // So: take the eight corners of the box, put each one in the camera's own frame at home,
    // and ask how far back the camera must stand for that corner to be inside BOTH fields.
    // The answer is the largest of the sixteen. A corner behind the origin needs less
    // distance, not more, which is why its depth is part of the sum rather than ignored.
    // A FIFTH OF THE PICTURE AROUND THE ACTION, which is the rule stated as a number: every
    // part is inside the frame at both ends of the movement, and the action is as close as it
    // can be while that holds. The room is for the dimension lines and their labels, which
    // stand outside the box and carry a number at the middle of each.
    //
    // Fitted to the union of t=0 and t=1 (see the loop in `frame`), so a part that starts six
    // centimetres out is framed from where it starts and not from where it lands.
    const CENTRED_MARGIN = 1.2;
    // FROM WHEREVER THE SHOT STANDS, and not from home alone. This took no arguments and read
    // the home directions off `worldAxis` — which was true while the only shot was home, and
    // became a lie the day the call started answering an angle: `look` reused this distance at
    // an orientation it was never measured for, and a screw that fits square-on runs off the
    // side of the picture seen from 60° round. The box does not change; how much of the
    // picture it fills does.
    //
    // `aim` is what the camera is centred on, so a shot held off the origin is fitted about
    // the point it actually looks at.
    const fitCentred = (
      box: import('three').Box3,
      eye: import('three').Vector3,
      upv: import('three').Vector3,
      aim?: import('three').Vector3,
      // Another set of points than the one on screen (see widthAt).
      points: import('three').Vector3[] | null = outline,
    ): number => {
      // The third direction — across the picture — is what those two imply.
      const across = new THREE.Vector3().crossVectors(eye, upv).normalize();
      const tanY = Math.tan((camera.fov * Math.PI) / 360);
      const tanX = tanY * Math.min(camera.aspect || 1, opts.framedAt ?? Infinity);
      if (!(tanY > 0) || !(tanX > 0)) return openedCentred;

      const corner = new THREE.Vector3();
      let need = 0;
      const fit = (p: import('three').Vector3) => {
        corner.copy(p);
        if (aim) corner.sub(aim);
        // How far the point already is towards the camera, and how far off centre.
        const depth = corner.dot(eye);
        // The margin widens the OFFSET and not the depth: it is room around the picture's
        // edges, which is where the dimension lines and their labels stand.
        need = Math.max(
          need,
          depth + (CENTRED_MARGIN * Math.abs(corner.dot(upv))) / tanY,
          depth + (CENTRED_MARGIN * Math.abs(corner.dot(across))) / tanX,
        );
      };
      // THE PARTS' OWN POINTS, where they have been taken (see outline), and the box's eight
      // corners only before then. Seen at an angle, a box's corners stand well outside the wood,
      // and the picture was fitted to air: the margin read wider than it was (Louis 2026-10-28).
      if (points?.length) points.forEach(fit);
      else {
        const p = new THREE.Vector3();
        for (const x of [box.min.x, box.max.x]) {
          for (const y of [box.min.y, box.max.y]) {
            for (const z of [box.min.z, box.max.z]) fit(p.set(x, y, z));
          }
        }
      }
      return need > 0 ? need : openedCentred;
    };

    // THE PARTS' OUTLINE, AS POINTS: a sample of every mesh's vertices at both ends of the
    // movement, in the world as `frame` measures it (pivot at rest). What a shot is fitted to,
    // and what an unaimed shot is centred on. Taken by `frame`, null before.
    let outline: import('three').Vector3[] | null = null;
    // A few thousand in all, which is plenty to find an edge and cheap enough to walk per frame.
    const OUTLINE_POINTS = 6000;
    const takeOutline = (into: import('three').Vector3[]) => {
      const meshes: import('three').Mesh[] = [];
      // THE PARTS STANDING, as their own meshes: not a part this assembly does not place, and not
      // the marks hung on a holder beside its mesh.
      const standing = new Set(placed.map((p) => p.instance.id));
      for (const [id, { body }] of bodies) {
        if (!standing.has(id)) continue;
        body.traverse((o) => {
          const m = o as import('three').Mesh;
          if (m.isMesh && m.geometry?.attributes?.position) meshes.push(m);
        });
      }
      const each = Math.max(1, Math.floor(OUTLINE_POINTS / 2 / Math.max(1, meshes.length)));
      for (const m of meshes) {
        m.updateWorldMatrix(true, false);
        const pos = m.geometry.attributes.position;
        const step = Math.max(1, Math.ceil(pos.count / each));
        for (let i = 0; i < pos.count; i += step) {
          into.push(new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(m.matrixWorld));
        }
      }
    };

    // THE MIDDLE OF THE PARTS AS SEEN FROM `eye`: halfway between their outermost points across
    // the picture and up it, and halfway through them in depth. Where a shot aimed at nothing in
    // particular looks (see CamAt.auto), so the margin is the same on both sides. It used to look
    // at the origin, and only the side reaching furthest from it got the margin.
    const outlineMiddle = (eye: import('three').Vector3, upv: import('three').Vector3) => {
      const mid = new THREE.Vector3();
      if (!outline?.length) return mid;
      const across = new THREE.Vector3().crossVectors(eye, upv).normalize();
      const lo = [Infinity, Infinity, Infinity];
      const hi = [-Infinity, -Infinity, -Infinity];
      const axes = [across, upv, eye];
      for (const p of outline) {
        for (let k = 0; k < 3; k += 1) {
          const v = p.dot(axes[k]);
          if (v < lo[k]) lo[k] = v;
          if (v > hi[k]) hi[k] = v;
        }
      }
      for (let k = 0; k < 3; k += 1) mid.addScaledVector(axes[k], (lo[k] + hi[k]) / 2);
      return mid;
    };

    // THE HOME VIEW'S OWN TWO DIRECTIONS, which is what `fitCentred` used to assume: back
    // along the assembly's −Y, up its Z.
    const homeEye = () => worldAxis('y').clone().multiplyScalar(-1).normalize();
    const homeUp = () => worldAxis('z').clone().normalize();

    // AND THE BOX THE LAST FRAMING WAS FITTED TO, kept so a later shot can be fitted to the
    // same thing from somewhere else. Null until something has been framed, which is the case
    // `look` falls back to home's distance for.
    let framed: import('three').Box3 | null = null;
    // AND THE PARTS' OWN BOX, BEFORE THE ORIGIN IS FOLDED INTO IT. `framed` is the SHOT — it
    // is stretched to keep the gizmo on screen, so on a mesh whose origin sits off its body it
    // covers a good deal of fresh air. Everything about where the camera may be is about the
    // WOOD, and the two stopped being the same box the moment the origin was added to one.
    let solid: import('three').Box3 | null = null;
    // SQUARE ON, ORIGIN IN THE MIDDLE, at the distance that fits the reach from it.
    //
    // Standing on the assembly's −Y looking down +Y, with X across the picture and Z up it —
    // which is what the gizmo draws when its own arms have no angle to show, the green one
    // foreshortened to a dot because it is pointing at you. It is the view with no rotation
    // in it, which is what makes it a REFERENCE rather than a place the camera happens to
    // have been: an orbit has no natural rest, and "roughly where I started" is not one.
    //
    // Everything the corner reads is measured from here. The angles are how far off this the
    // shot is turned, the zoom is a multiple of this distance, and the position is how far
    // the target has slid from this origin — so at home all six read zero and ×1.00, and any
    // other reading is a statement about how far you have gone.
    //
    // Its own distance, not the sphere's: the assembly is pulled back to the origin as well
    // as the camera being squared up, so what has to fit is the reach FROM the origin rather
    // than a sphere around the lot. See fitCentred.
    //
    // Written once and called twice — by the opening frame and by the house — because they
    // are the same shot. Two copies of it are two shots that agree until one is edited.
    const placeHome = () => {
      pivot.position.set(0, 0, 0);
      pivot.updateMatrixWorld(true);
      const dist = openedCentred || 1;
      camera.up.copy(worldAxis('z'));
      // AT THE MIDDLE OF THE PARTS, not the origin (see outlineMiddle).
      controls.target.copy(outlineMiddle(homeEye(), homeUp()));
      camera.position.copy(worldAxis('y')).multiplyScalar(-dist).add(controls.target);
      camera.lookAt(controls.target);
      controls.update();
    };

    // WHETHER THIS VIEWER HAS OPENED YET. The shared angle is taken once, on the first
    // framing, and never again: a re-frame is a step changing or a part moving, and having
    // the camera jump back to somebody's stored angle in the middle of that would be a
    // viewer that fights being turned.
    let opened = false;

    const frame = (asm: Assembly, step: number) => {
      // MEASURED WITH THE PIVOT AT REST, so the box comes out in the assembly's own
      // coordinates — where the origin is [0,0,0] and not "wherever the last shot put it".
      // It used to be measured before that reset, so each re-frame carried the previous
      // one's offset into the arithmetic of the next.
      pivot.position.set(0, 0, 0);
      pivot.updateMatrixWorld(true);
      const bounds = new THREE.Box3();
      const points: import('three').Vector3[] = [];
      for (const t of [0, 1]) {
        apply(poseAt(asm, step, t));
        root.updateMatrixWorld(true);
        bounds.expandByObject(root);
        takeOutline(points);
      }
      outline = points;
      if (bounds.isEmpty()) return;
      // Taken here, one line before the origin joins it.
      solid = bounds.clone();
      // AND THE ORIGIN IS PART OF THE SHOT. The gizmo stands there and every number on the
      // page is measured from it, so a frame fitted to the parts alone would push it out of
      // sight the moment somebody drags a part away from it.
      bounds.expandByPoint(new THREE.Vector3());
      const radius = bounds.getBoundingSphere(new THREE.Sphere()).radius || 1;
      // A WIDER SHOT THAN THE PARTS NEED. The measurement lines stand a gap outside the box
      // and carry a label at the middle of each — a number cut in half by the edge of the
      // viewport is not a measurement — and the origin is held off-centre besides, which
      // costs the picture room on the side it is held towards.
      // STILL THE SPHERE, and only for the clipping planes. It is the generous distance —
      // wide enough for the measurement labels standing outside the box — so near and far cut
      // nothing that home's tighter framing brings closer.
      const dist = (radius / Math.sin((camera.fov * Math.PI) / 360)) * 1.6;
      framed = bounds.clone();
      // The part may have moved, so the support's points have. Dropped rather than rebuilt:
      // the next frame that needs them takes them.
      keepPts = null;
      keptDir = null;
      openedCentred = fitCentred(bounds, homeEye(), homeUp(), outlineMiddle(homeEye(), homeUp()));
      camera.near = dist / 200;
      camera.far = dist * 20;
      camera.updateProjectionMatrix();
      // AND NO FURTHER BACK THAN FOUR TIMES THAT: the wheel had no limit, and past the far plane
      // the model was cut away whole — nothing left to zoom back in on (Louis 2026-10-23).
      controls.maxDistance = dist * 4;
      placeHome();
      // AND THEN THE ANGLE THE PART WAS LAST LEFT AT, if anybody has turned one.
      //
      // This scene WROTE that angle and never read it, which is exactly what "expand the mesh
      // and it snaps back to the front" is: turn the part in the panel, open the modal, and
      // the modal — another mounting of this same scene — framed it from the fixed default
      // while the store held the angle you had just chosen. meshScene has read it since it
      // was written; this one only ever contributed to it.
      //
      // THE DIRECTION ONLY, and the distance left as placeHome computed it. The panel is a
      // few hundred pixels and the modal is a screen, and one distance cannot serve both —
      // the same reason the store holds no distance to begin with.
      //
      // Home is left alone deliberately: it means the shot the viewer was designed to open
      // on, and a Home that went to wherever you last dragged would have no way back.
      if (!opened && opts.sharedView) {
        opened = true;
        const stored = getMeshView();
        if (stored) {
          const away = camera.position.distanceTo(controls.target) || 1;
          const dir = new THREE.Vector3(...stored.dir);
          if (dir.lengthSq() > 1e-12) {
            camera.position.copy(controls.target).add(dir.normalize().multiplyScalar(away));
            camera.lookAt(controls.target);
            controls.update();
          }
        }
      }
    };

    const controls = new OrbitControls(camera, surface);
    step('outline rims');
    controls.enableDamping = true;
    // THE DRAG IS OURS (below), and the wheel zooms toward the point under the cursor rather than
    // into the middle.
    controls.enablePan = false;
    controls.zoomToCursor = true;
    if (opts.scrollsUpDown) {
      controls.enableZoom = false;
      // After the controls, which set it to none.
      surface.style.touchAction = 'pan-y';
    }

    // POINTING AT A PART, AND PUSHING IT ABOUT.
    //
    // Two gestures on one canvas, and the whole of the arrangement is which of them wins a
    // press: a press that lands on a part it may move takes the pointer from the orbit
    // controls, and every other press is the camera's as it always was. A press that moves
    // nowhere is a click, and a click is what selects — including on the background, which
    // is how a selection is dropped.
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const partAt = (ev: PointerEvent): string | null => {
      const rect = surface.getBoundingClientRect();
      pointer.set(
        ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        -((ev.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      // THE SELECTED PART FIRST, WHEREVER IT IS. It is outlined through everything that covers
      // it (see outlineMat), so the one thing on screen saying "it is here" was a mark nobody
      // could press: a ray aimed at a barrel nut inside a rail lands on the rail and answers
      // the rail, and the nut could be seen, could be talked about in the panel, and could not
      // be taken hold of.
      //
      // A selection is a stated intent to work on that part. So while one stands, its own
      // geometry takes the pointer ahead of anything nearer the camera — clicking anywhere in
      // its outline grabs it. The cost is that its footprint stops being a way through to
      // whatever is in front of it, which is the same trade every modelling tool makes and is
      // undone by clicking off the part.
      if (selected) {
        const chosen = objects.get(selected);
        if (chosen?.visible && raycaster.intersectObject(chosen, true).length) return selected;
      }
      const live = [...objects.values()].filter((o) => o.visible);
      const hit = raycaster.intersectObjects(live, true)[0];
      if (!hit) return null;
      // A .glb is a tree and the ray touched some node inside one, so walk up to the holder.
      for (let node: import('three').Object3D | null = hit.object; node; node = node.parent) {
        for (const [id, holder] of objects) if (holder === node) return id;
      }
      return null;
    };

    // A PRESS THAT WENT NOWHERE: four pixels of slack, because a click on a trackpad is never
    // perfectly still — and past that the press was an orbit or a drag.
    const pressedStill = (ev: PointerEvent) => !!pressedAt
      && Math.abs(ev.clientX - pressedAt.x) < 4 && Math.abs(ev.clientY - pressedAt.y) < 4;

    // AND WHICH FACE'S NAME IS UNDER THE POINTER, if any — the plates the bounding box writes
    // its six words on (see makeExtentDims). One raycast against six sprites per part, and
    // only where a caller asked to hear about them.
    //
    // ONLY THE ONES CARRYING AN ANSWER, which is what keeps this from costing anything on
    // every other mesh in the app: a sprite is hit-tested by its whole quad and six of those
    // are six invisible rectangles over the object. See ExtentDims.answered.
    // A DIMENSION'S NUMBER UNDER THE POINTER — see dimensionLines. Its part, and its name.
    const dimensionAt = (ev: PointerEvent): { id: string; name: string } | null => {
      if (!dimLines.size) return null;
      const rect = surface.getBoundingClientRect();
      pointer.set(
        ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        -((ev.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      for (const [id, lines] of dimLines) {
        const name = lines.pick(raycaster);
        if (name) return { id, name };
      }
      return null;
    };

    const faceAt = (
      ev: PointerEvent,
    ): { instanceId: string; face: string } | null => {
      if (!opts.onPickFace) return null;
      const rect = surface.getBoundingClientRect();
      pointer.set(
        ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        -((ev.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      for (const [id, dims] of extents) {
        if (!dims.group.visible) continue;
        for (const { face, sprite } of dims.faceSprites) {
          // A plate that is switched off is not a plate you can press: the box may still be
          // drawn for the measurements' sake, and six invisible rectangles over the object
          // would swallow the presses that select the part.
          if (!sprite.visible || !dims.answered(face)) continue;
          if (raycaster.intersectObject(sprite, false).length) {
            return { instanceId: id, face };
          }
        }
      }
      return null;
    };

    let pressedAt: { x: number; y: number } | null = null;

    const onDown = (ev: PointerEvent) => {
      pressedAt = { x: ev.clientX, y: ev.clientY };
    };

    const onUp = (ev: PointerEvent) => {
      if (pressedAt) {
        // A PRESS THAT WENT NOWHERE IS A CLICK. Four pixels of slack, because a click on a
        // trackpad is never perfectly still — and past that the press was an orbit, which
        // must not throw away what is selected.
        if (pressedStill(ev)) {
          // A FACE'S NAME FIRST, because it is a word floating over the object and a press on
          // it is aimed at the word rather than at whatever is behind it. The plates are drawn
          // over everything (depthTest off, see mutableLabel), so the part is always "behind"
          // one — and a press that selected the part instead would make the label unpressable
          // exactly where it sits, which is over the part.
          // AND A DIMENSION'S NUMBER BEFORE THAT, for the same reason: pressed, it says whose
          // number it is — or stops saying.
          const measured = dimensionAt(ev);
          const named = measured ? null : faceAt(ev);
          if (measured) {
            dimLines.get(measured.id)?.toggleName(measured.name);
            remeasure();
          } else if (named) opts.onPickFace?.(named.instanceId, named.face);
          else {
            const id = partAt(ev);
            opts.onPick?.(id ? { instanceId: id } : null);
          }
        }
      }
      pressedAt = null;
    };

    surface.addEventListener('pointerdown', onDown);
    surface.addEventListener('pointerup', onUp);

    // A DRAG ON THE PARTS TURNS THE PICTURE ABOUT THEIR MIDDLE, not about the point the shot is
    // aimed at, and a drag beside them slides the camera — in every viewer, as in the published
    // player, where it began.
    //
    // A clip's shot is aimed at the action, close in: the middle of a screw's way into a frame
    // 73 cm tall. Turned about that point the frame swings round the screw in a wide arc and
    // leaves the picture, which is somebody trying to see the other side of a chair and losing
    // the chair. Turned about its own middle it stays where it is and shows its other side.
    //
    // It replaced a switch in the view corner — the two headings, "looking at" and "camera
    // orientation", choosing which a drag on the background did. Where you take hold says it now.
    //
    // THE CAMERA IS CARRIED ROUND THE PARTS' MIDDLE, WHOLE: where it stands, which way it faces
    // and the point the orbit controls hold for it, all by the one rotation. So nothing jumps
    // when the drag starts — the picture is the one that was on screen — and the middle of the
    // parts stays on the pixel it was on. The controls' own turning is switched off for it,
    // since theirs is about the point they aim at; the wheel and the pinch are still theirs.
    //
    // LEVEL: a drag across turns the parts round the assembly's upright, a drag up and down tips
    // the view about the picture's level, as far as straight above or below and no further. It
    // was a free trackball about the screen's own axes, and mixed drags piled up a roll nobody
    // asked for (see standLevel).
    //
    // AND A DRAG THAT STARTS OFF THE PARTS MOVES THE CAMERA INSTEAD — sideways and up, facing the
    // way it faced. Taking hold of the thing turns the thing; taking hold of the room slides
    // the room. Geared at the depth of the parts' middle, so they keep up with the finger.
    let stopTurning: (() => void) | undefined;
    // NO SLIDE once the scene stands on a floor (see floorShadow): a drag turns and tips it like
    // any other, wherever it starts, and never slides the product off the middle of its frame. It
    // was a turntable too, with no tip; Louis wanted the tip (2026-10-23).
    let onFloor = false;
    // HANDS OFF UNTIL THE SCENE IS READY: a drag or a zoom made while the meshes were still
    // arriving was undone by the shot put on them when they landed, and the view jumped (Louis
    // 2026-10-23). Let go at 'ready', or by `release` where the caller asked to say when.
    let handsOff = true;
    controls.enabled = false;
    const letGo = () => { handsOff = false; controls.enabled = true; };
    {
      controls.enableRotate = false;
      const dom = surface;
      const held = new Map<number, { x: number; y: number }>();
      let about: import('three').Vector3 | null = null;
      let sliding = false;
      // Off each part's kept box (see `bounds`), stood where its holder stands: walking the
      // vertices of a tessellated screw at every press is not a way to find a middle.
      const middleOfParts = () => {
        const box = new THREE.Box3();
        const corner = new THREE.Vector3();
        for (const [id, holder] of objects) {
          const own = bounds.get(id);
          if (!holder.visible || !own) continue;
          holder.updateWorldMatrix(true, false);
          for (let i = 0; i < 8; i++) {
            box.expandByPoint(corner.set(
              i & 1 ? own.max.x : own.min.x, i & 2 ? own.max.y : own.min.y, i & 4 ? own.max.z : own.min.z,
            ).applyMatrix4(holder.matrixWorld));
          }
        }
        return box.isEmpty() ? null : box.getCenter(new THREE.Vector3());
      };
      const onTurnDown = (ev: PointerEvent) => {
        if (handsOff || (ev.pointerType === 'mouse' && ev.button !== 0)) return;
        held.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
        // One finger turns or slides; a second makes it a pinch, which is the controls'.
        about = held.size === 1 ? middleOfParts() : null;
        sliding = held.size === 1 && !onFloor && partAt(ev) === null;
      };
      // Whether the touch under way landed on the part (see onTouchStart below).
      let grabbed = false;
      const onTurnMove = (ev: PointerEvent) => {
        const was = held.get(ev.pointerId);
        if (!was) return;
        const dx = ev.clientX - was.x;
        const dy = ev.clientY - was.y;
        was.x = ev.clientX;
        was.y = ev.clientY;
        if (held.size !== 1 || !about || (!dx && !dy)) return;
        gliding = null;
        opts.onCameraTaken?.();
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
        if (sliding) {
          const facing = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
          const depth = Math.max(camera.near, about.clone().sub(camera.position).dot(facing));
          // What one pixel is worth at that depth, for this lens.
          const each = (2 * depth * Math.tan((camera.fov * Math.PI) / 360)) / (dom.clientHeight || 1);
          const lift = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
          const by = right.multiplyScalar(-dx * each).add(lift.multiplyScalar(dy * each));
          camera.position.add(by);
          controls.target.add(by);
          controls.update();
          return;
        }
        // A full turn per height of the picture dragged, which is the orbit's own gearing.
        // ACROSS TURNS ROUND THE UPRIGHT, UP AND DOWN TIPS ABOUT THE PICTURE'S LEVEL, and nothing
        // else: the camera stands level whatever the drag (see standLevel). Every side is still
        // reached — above, below, behind — and a drag back the way it came undoes it.
        const per = (2 * Math.PI) / (dom.clientHeight || 1);
        const up = worldAxis('z');
        right.addScaledVector(up, -right.dot(up)).normalize();
        // THE TIP STOPS AT DRAG_TIP, before the camera moves: past straight above it crossed to
        // the other side, stood level facing the other way — the model flipped — and a hand
        // hovering there crossed back and forth every frame (Louis 2026-10-23). About a level
        // axis square to the view, a tip of `a` moves the camera's height angle by exactly `a`,
        // so the part of the drag that would pass the stop is cut off and the rest kept.
        // (A mouse drags as anywhere: nothing it does scrolls the list.)
        let tipBy = opts.scrollsUpDown && !grabbed && ev.pointerType === 'touch' ? 0 : -dy * per;
        const off = camera.position.clone().sub(controls.target);
        const heightOf = (v: import('three').Vector3) => Math.asin(Math.max(-1, Math.min(1, v.dot(up) / (v.length() || 1))));
        const tipWas = heightOf(off);
        // WHICH WAY A TIP OF +1 MOVES THE HEIGHT, from a hair of one. The new height is then the
        // old one plus the step, ADDED and not read back off the moved camera: read back, a step
        // past straight above came out under the stop (112° reads as 68°, on the other side),
        // so a fast drag crossed over, the view flipped, and every frame after crossed back.
        const way = Math.sign(heightOf(off.clone().applyAxisAngle(right, 1e-3)) - tipWas) || 1;
        const now = tipWas + way * tipBy;
        // Inside the stops, or no further out than it already stands (a shot may open past them).
        const stopped = Math.max(Math.min(-DRAG_TIP, tipWas), Math.min(Math.max(DRAG_TIP, tipWas), now));
        tipBy = (stopped - tipWas) / way;
        const turn = new THREE.Quaternion().setFromAxisAngle(up, -dx * per)
          .multiply(new THREE.Quaternion().setFromAxisAngle(right, tipBy));
        for (const at of [camera.position, controls.target]) at.sub(about).applyQuaternion(turn).add(about);
        standLevel();
        controls.update();
        if (trace) {
          const c = camState();
          const d = (r: number) => fmt((r * 180) / Math.PI);
          trace.say(`drag dx=${dx} dy=${dy} tip0=${d(tipWas)} want=${d(now)} by=${d(tipBy)} | tip=${fmt(c.tip)} az=${fmt(c.az)} top=${c.top.toFixed(2)}`);
          lastCam = `${fmt(c.tip)} ${fmt(c.az)} ${c.top.toFixed(2)}`;
          draggedThisFrame = true;
        }
      };
      const onTurnUp = (ev: PointerEvent) => { held.delete(ev.pointerId); };
      // The wheel and the pinch are the controls' own, and they say when one begins.
      const onZoom = () => opts.onCameraTaken?.();
      controls.addEventListener('start', onZoom);
      // A FINGER LANDING ON THE PART TAKES IT, in a list that scrolls (Louis 2026-10-23): the page
      // does not scroll under that touch and the drag turns and tips the part as anywhere else. A
      // finger landing beside it scrolls the page as before. Decided at the touch's start, the
      // one moment a browser lets a page keep a gesture from scrolling.
      const onTouchStart = (e: TouchEvent) => {
        const t = e.touches[0];
        grabbed = e.touches.length === 1 && !!t
          && partAt({ clientX: t.clientX, clientY: t.clientY } as PointerEvent) !== null;
        if (grabbed) e.preventDefault();
      };
      if (opts.scrollsUpDown) dom.addEventListener('touchstart', onTouchStart, { passive: false });
      dom.addEventListener('pointerdown', onTurnDown);
      dom.addEventListener('pointermove', onTurnMove);
      dom.addEventListener('pointerup', onTurnUp);
      dom.addEventListener('pointercancel', onTurnUp);
      stopTurning = () => {
        dom.removeEventListener('touchstart', onTouchStart);
        controls.removeEventListener('start', onZoom);
        dom.removeEventListener('pointerdown', onTurnDown);
        dom.removeEventListener('pointermove', onTurnMove);
        dom.removeEventListener('pointerup', onTurnUp);
        dom.removeEventListener('pointercancel', onTurnUp);
      };
    }

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = el;
      load?.say(`resize ${w}x${h} ${since()}`);
      if (!w || !h) return;
      camera.aspect = w / h;
      // The full shot drawn into the part of the picture under the headroom, at the same aspect,
      // centred across it; the window onto it spans the whole canvas.
      const room = opts.headroom ?? 0;
      if (room > 0 && room < h) {
        const fh = h - room;
        const fw = (w * fh) / h;
        camera.setViewOffset(fw, fh, -(w - fw) / 2, -room, w, h);
      } else camera.clearViewOffset();
      camera.updateProjectionMatrix();
      size.w = w;
      size.h = h;
      if (copyTo) {
        const px = renderer.getPixelRatio();
        surface.width = Math.floor(w * px);
        surface.height = Math.floor(h * px);
      } else renderer.setSize(w, h, false);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    resize();

    // A TURN THAT IS SLID RATHER THAN CUT. Half a turn of a nearly symmetrical screw applied
    // in one frame looks like nothing happened — the movement IS how you know which way it
    // went and that it went at all.
    //
    // The angle travels as an axis and an angle, never as two endpoints lerped: two opposite
    // directions have no midpoint, and lerping them passes through the origin where the
    // camera's own position is undefined. Exactly the half-turn this exists for.
    let gliding: {
      from: import('three').Vector3;
      // The camera's place turns and it stands level wherever that lands (see standLevel): a turn
      // about the axis it looks along is a roll, and there is none — at home, Y's turns move
      // nothing.
      axis: import('three').Vector3; angle: number; t0: number;
    } | null = null;
    const GLIDE_MS = 380;

    // WHICH WORLD AXIS AN ASSEMBLY AXIS IS. The root carries the Z-up-to-Y-up quarter turn,
    // so the assembly's Y is the world's −Z and its Z is the world's Y. Turning about the
    // letter on the button therefore means turning about this, or the view would swing about
    // an axis the gizmo does not draw.
    const worldAxis = (which: 'x' | 'y' | 'z') =>
      new THREE.Vector3(which === 'x' ? 1 : 0, which === 'y' ? 1 : 0, which === 'z' ? 1 : 0)
        .applyQuaternion(root.quaternion);

    // THE CAMERA STANDS LEVEL, ALWAYS: its up is the assembly's upright, so the horizon never
    // rolls. Every way the camera is moved ends here — a shot (`look`), a drag, a quarter turn,
    // a pose carried from the last clip. It used to carry a roll: a shot was a tip and then a
    // turn about the TIPPED upright, and the drag a free trackball whose mixed drags piled one
    // up, so uprights leaned and dragging back did not undo it (Louis 2026-10-23).
    // AND NEVER QUITE STRAIGHT DOWN OR UP, where "level" has no answer: half a degree short.
    const MAX_TIP = (89.5 * Math.PI) / 180;
    // THE CAMERA, IN NUMBERS, for the log (see camTracer): its height angle and its bearing round
    // the upright, both in degrees, and whether the top of the picture points up (`top`, 1 level,
    // below 0 upside down).
    const trace = serverLog('cam');
    const camState = () => {
      const up = worldAxis('z');
      const off = camera.position.clone().sub(controls.target);
      const away = off.length() || 1;
      const tip = (Math.asin(Math.max(-1, Math.min(1, off.dot(up) / away))) * 180) / Math.PI;
      const az = (Math.atan2(off.dot(worldAxis('x')), -off.dot(worldAxis('y'))) * 180) / Math.PI;
      const top = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion).dot(up);
      return { tip, az, top, away };
    };
    const fmt = (n: number) => n.toFixed(1);
    let lastCam = '';
    let draggedThisFrame = false;
    // AND A DRAG STOPS SHORTER, at 85°: closer in, a hair of movement swings the view round the
    // pole, and the picture reads as unsteady well before it would flip.
    const DRAG_TIP = (85 * Math.PI) / 180;
    const standLevel = () => {
      const up = worldAxis('z');
      const off = camera.position.clone().sub(controls.target);
      const away = off.length();
      if (!(away > 0)) return;
      const tip = Math.asin(Math.max(-1, Math.min(1, off.dot(up) / away)));
      if (Math.abs(tip) > MAX_TIP) {
        const flat = off.clone().addScaledVector(up, -off.dot(up));
        // Straight overhead: no side to lean towards, so away from where the picture's top points.
        if (flat.lengthSq() < 1e-12) {
          const top = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
          flat.copy(top.addScaledVector(up, -top.dot(up))).negate();
        }
        flat.normalize();
        const k = Math.sign(tip) * MAX_TIP;
        off.copy(flat.multiplyScalar(Math.cos(k) * away)).addScaledVector(up, Math.sin(k) * away);
        camera.position.copy(controls.target).add(off);
      }
      camera.up.copy(up);
      camera.lookAt(controls.target);
    };

    // THE SHOT WITH NO ANGLE IN IT, as a quaternion, built once: on the assembly's −Y looking
    // down +Y with Z up — the same view `home` goes to, and the origin every reported angle is
    // measured from. Position is irrelevant to an orientation, so a unit distance will do.
    const squareOn = new THREE.Quaternion();
    {
      // A CAMERA AND NOT AN Object3D, which is the whole of the bug this replaces.
      // `Object3D.lookAt` aims the object's +Z at the target; `Camera.lookAt` aims its −Z,
      // three branching on `isCamera` inside the same method name. A plain object standing
      // where the camera stands therefore faces the opposite way, and every angle was
      // reported against a reference half a turn out — home read −180, 0, −180, which is
      // 0, 180, 0 said the twisted way, and both of them mean "180° from square".
      const probe = new THREE.PerspectiveCamera();
      probe.up.copy(worldAxis('z'));
      probe.position.copy(worldAxis('y')).multiplyScalar(-1);
      probe.lookAt(0, 0, 0);
      squareOn.copy(probe.quaternion);
    }
    const relQuat = new THREE.Quaternion();
    const relEuler = new THREE.Euler();
    let viewSent: [number, number, number] | null = null;
    let zoomSent = 0;
    let centreSent: [number, number, number] | null = null;
    const centreScratch = new THREE.Vector3();
    // REPORTED ON THE WHOLE DEGREE AND NO OFTENER. This runs in the render loop and the other
    // end of it is React: an orbit is sixty frames a second, and a state update per frame to
    // move a readout by a hundredth of a degree is sixty renders nobody asked for. A degree
    // is also the precision the readout shows, so anything finer is invisible as well as
    // expensive.
    // AND THE ANGLE ITSELF, into the one every mesh in the app is turned to.
    //
    // The thumbnails under this viewer follow that shared angle and this viewer never wrote
    // to it, so the strip sat at its own default while the mesh above it was square on —
    // two pictures of one part disagreeing about which way it faces, which is exactly what
    // meshView exists to prevent (read its own note: "the thumbnails still agree with each
    // other and with the viewer above them"). Only mountMesh was holding up that promise.
    //
    // IN WORLD SPACE, and it has to stay that way. Both scenes now put the same quarter turn
    // on their root — a mesh stands up the same way wherever it is drawn — so their worlds
    // mean the same thing and a direction carries across untouched.
    //
    // It was converted into the file's frame here for exactly one commit, while mountMesh was
    // still Y-up, and then that was fixed as well: two corrections for one mismatch, leaving
    // every thumbnail a quarter turn off the viewer above it. One frame, stated once.
    // THE CAMERA STOPS AT THE SURFACE, on a viewer whose part is a solid.
    //
    // Zoom far enough and the near plane cuts the near wall away and you are inside the wood,
    // looking at the inside of the far wall — which is a picture of something that cannot
    // happen. A stencil cap over the near plane would paint the view solid and is the right
    // tool for a cross-section somebody asked for; here there is no cut to cap, only a camera
    // somewhere it cannot be, and the honest fix is to keep it out.
    //
    // A DISTANCE PER DIRECTION AND NOT ONE NUMBER. A bounding sphere would stop the camera a
    // half-length away from a rail — you could never look closely at anything — and the
    // inradius would let it in through the ends. So the limit is where the view direction
    // leaves the part ITSELF, measured against the mesh and falling back to its box.
    //
    // Set as `minDistance` rather than by moving the camera, so OrbitControls enforces it
    // through the zoom itself and the wheel simply stops. Fighting it frame by frame after
    // the fact is how a viewer comes to feel like it is resisting the mouse.
    const keepScratch = new THREE.Vector3();
    const keepRay = new THREE.Raycaster();
    // THE PART'S POINTS IN WORLD SPACE, thinned and taken once.
    //
    // A support has to be taken over the geometry, and half a million vertices every frame is
    // not a support, it is a stall. Every ninth or so, capped, which for a reconstruction —
    // where the vertices are spread evenly over a smooth surface — lands within a fraction of
    // a millimetre of the true extent. The margin the answer is multiplied by afterwards is
    // larger than the error.
    let keepPts: Float32Array | null = null;
    const keepPoints = (): Float32Array => {
      if (keepPts) return keepPts;
      const want = 20000;
      const at = new THREE.Vector3();
      const all: number[] = [];
      for (const { mesh } of outlinable) {
        mesh.updateMatrixWorld(true);
        // TRAVERSED, because what is held there is the loaded FILE and not a mesh — an
        // Object3D with the geometry somewhere under it. Reading `.geometry` off the group
        // finds undefined, collects nothing, and leaves the support at zero, which this
        // function then reads as "no constraint" and the camera flies straight through the
        // part. Every other user of these does `intersectObject(mesh, true)`; the `true` is
        // this same fact, said by the raycaster.
        mesh.traverse((o) => {
          const pos = (o as import('three').Mesh).geometry?.attributes?.position;
          if (!pos) return;
          const step = Math.max(1, Math.floor(pos.count / want));
          for (let i = 0; i < pos.count; i += step) {
            at.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(o.matrixWorld);
            all.push(at.x, at.y, at.z);
          }
        });
      }
      // AND A BOX IS BETTER THAN NOTHING. Where no geometry can be read at all, the framed
      // box's own corners stand in: conservative, always available, and — unlike having no
      // points — never silently equivalent to having no limit. The failure this guards
      // against has now happened twice.
      if (!all.length && framed) {
        for (const x of [framed.min.x, framed.max.x]) {
          for (const y of [framed.min.y, framed.max.y]) {
            for (const z of [framed.min.z, framed.max.z]) all.push(x, y, z);
          }
        }
      }
      keepPts = new Float32Array(all);
      return keepPts;
    };
    // The direction the surface was last measured along, and what it measured. Kept because
    // the measurement is a raycast over half a million triangles and the operation that
    // needs it — zooming — does not change the direction at all.
    let keptDir: import('three').Vector3 | null = null;
    let keptAt = 0;
    let keptWhen = 0;

    // Where the part's own SURFACE lies along this direction, or null if it cannot be found.
    //
    // THE LAST HIT AND NOT THE FIRST. A ray leaving the middle of a part with a socket in it
    // exits the socket wall, crosses the cavity and exits again; the camera is only truly
    // outside beyond the furthest of those. Taking the first would leave it parked in the
    // hollow, which is the case this whole clamp exists for.
    const surfaceAlong = (from: import('three').Vector3, dir: import('three').Vector3) => {
      keepRay.set(from, dir);
      keepRay.near = 0;
      keepRay.far = Infinity;
      let far = 0;
      for (const { mesh } of outlinable) {
        const hits = keepRay.intersectObject(mesh, true);
        for (const h of hits) far = Math.max(far, h.distance);
      }
      return far > 0 ? far : null;
    };

    // AND THE POINT BEING LOOKED AT STAYS ON THE PART.
    //
    // THE HOLE THE WHOLE CLAMP WAS FALLING THROUGH, and it is not a matter of degree: the
    // limit below is a distance from the TARGET, so it says nothing at all about a camera
    // orbiting a target that is somewhere else. Pan a few millimetres off the end of the rail,
    // zoom right in on that empty spot — nothing is in the way, so nothing stops it — and then
    // turn. The camera sweeps a small sphere about a point beside the part and passes straight
    // through the wood on its way round, and every frame of that is a legal position for a
    // camera looking at where it is looking.
    //
    // Guarding it by refusing to act when the target is off the part was the same bug written
    // as a condition: it made the one case that needed the limit the case that switched it
    // off. What actually has no meaning in a viewer of ONE part is looking at the air beside
    // it, so that is what is refused, and the limit is then always in force.
    //
    // THE PARTS' BOX AND NOT THE SHOT'S. The shot is stretched to hold the origin, which on
    // this rail is several millimetres off the end of the wood — and those few millimetres are
    // exactly the ones this was panned into.
    //
    // The camera comes along with it, so nothing on screen moves: what stops is the pan, at
    // the edge of the part, which is as far as there is anything to look at.
    // AND ONLY WHERE THERE IS AN INSIDE TO PROTECT ANYBODY FROM. The rule exists because a
    // camera that enters a textured surface comes out looking at the shell from within —
    // inverted normals, black or blown out — which reads as a broken part rather than as "you
    // are inside it". A wireframe has no inside: it is lines, they look the same from either
    // side, and getting in among them is what the mode is FOR. Somebody who switched to it
    // did so to count triangles round a hole nine millimetres across.
    //
    // Read every frame rather than latched, so switching back to textured pushes the camera
    // out on the next tick — the rule reasserts itself where it applies instead of being
    // remembered.
    const walled = () => opts.keepOut && drawn !== 'wireframe';
    const holdScratch = new THREE.Vector3();
    const holdTarget = () => {
      if (!walled() || !solid) return;
      const t = controls.target;
      holdScratch.copy(t).clamp(solid.min, solid.max);
      if (holdScratch.equals(t)) return;
      camera.position.add(holdScratch.sub(t));
      t.add(holdScratch);
    };

    const keepOut = () => {
      // AND THE LIMIT IS LIFTED, not merely left unset. This function is the only thing that
      // writes `minDistance`, so returning early would leave standing whatever the last
      // textured frame computed — and the wheel would go on stopping at the surface in a
      // wireframe, which is the whole of what this was gating.
      if (!walled() || !solid) {
        controls.minDistance = 0;
        return;
      }
      const t = controls.target;
      // Belt and braces with holdTarget above, which runs first and leaves this true. A
      // support measured from a point outside the part cannot be used the way it is used
      // below: the camera may be on EITHER side of the wood then, and pushing it past every
      // vertex would fling it through to the far side rather than hold it where it is.
      if (!solid.containsPoint(t)) return;
      const dir = keepScratch.copy(camera.position).sub(t);
      if (dir.lengthSq() < 1e-12) return;
      dir.normalize();

      // HOW FAR THE PART REACHES ALONG THIS DIRECTION, measured from the target: the furthest
      // any of its points gets. Past that the camera is beyond every vertex there is that
      // way, so it is outside the part's convex hull and therefore outside the part.
      //
      // NOT THE BOX, WHICH IS WHY THIS WAS NOT WORKING. Exiting an axis-aligned box is three
      // divisions and looks equivalent, and it has a failure this does not: it needs the
      // target to be properly INSIDE the box. The framing deliberately expands the box to
      // include the origin — the gizmo stands there and every number is measured from it —
      // so on a mesh whose own origin is at a corner rather than its middle, the target ends
      // up ON the boundary. Every exit distance in the direction away from the part then
      // comes out at or near zero, the limit collapses, and the camera is free to fly
      // straight in. Which is what it did.
      //
      // A support like this cannot fail that way: it asks where the geometry ACTUALLY is,
      // and where the target sits relative to a box does not enter into it.
      let out = 0;
      const pts = keepPoints();
      for (let i = 0; i < pts.length; i += 3) {
        const along = (pts[i] - t.x) * dir.x + (pts[i + 1] - t.y) * dir.y
          + (pts[i + 2] - t.z) * dir.z;
        if (along > out) out = along;
      }
      // NOTHING IN THE WAY IS A LIMIT OF NOUGHT, SAID OUT LOUD. The part lies entirely behind
      // the target this way, so anywhere along here is outside it and the camera is free. It
      // used to return, which left whatever the last direction had computed standing — a
      // number about a direction nobody is looking in any more, and the one kind of stale
      // value that can be too SMALL.
      if (!(out > 0)) {
        controls.minDistance = 0;
        return;
      }

      // AND THE SURFACE ITSELF, WHEN IT IS WORTH ASKING FOR.
      //
      // Measured: a raycast over the side rail's half a million triangles is 27 ms — most of
      // two frames — so it cannot run every frame and does not have to run most of them at
      // all. Two gates keep it to almost none.
      //
      // FIRST, ONLY WHEN THE CAMERA IS CLOSING IN. Beyond twice the box's limit the clamp
      // could not bind whatever the surface says, so there is nothing to learn. Normal
      // viewing therefore costs three divisions and no casts.
      //
      // SECOND, ONLY WHEN THE VIEW HAS TURNED, and at most five times a second. Zooming is
      // the gesture that would put the camera inside a part, and zooming does not change the
      // direction — so one measurement holds for the whole of it, which is exactly the case
      // that matters. Between casts the last value stands; a stale one during a fast orbit
      // can be slightly short, and the worst that does is allow a hair too close for a
      // fraction of a second.
      const now = performance.now();
      const near = camera.position.distanceTo(t) < out * 2;
      const turned = !keptDir || keptDir.dot(dir) < 0.99985;
      if (near && turned && now - keptWhen > 200) {
        keptWhen = now;
        const found = surfaceAlong(t, dir);
        keptDir = dir.clone();
        keptAt = found ?? 0;
      }
      // AND IT IS USED OVER A WIDER ANGLE THAN IT IS REFRESHED OVER, which is not sloppiness
      // but the lesser of two errors. Dropping a measurement the moment the view moves off it
      // falls back to the box — which is FURTHER out — so the camera would jump backwards in
      // the middle of an orbit. Keeping it a few degrees longer can only allow a hair too
      // close, which nobody sees. Re-measured at one degree, honoured to eight.
      const stands = keptDir && keptDir.dot(dir) > 0.99;
      // Only ever a REFINEMENT of the box: the surface lies inside its own box by
      // definition, so this can only let the camera closer, which is why it is measured.
      if (keptAt > 0 && stands) out = Math.min(out, keptAt);

      // AND THE NEAR PLANE, WHICH IS THE HALF THAT WAS MISSING.
      //
      // Nobody sees from the camera; they see from the near plane, which sits `camera.near`
      // in front of it. So the camera can be legitimately outside the wood while the plane it
      // looks through is still buried in it, and everything between the two is clipped away —
      // which is a view of the inside of a solid part, arrived at from a camera that never
      // entered it. Measured on the side rail: the framing gives near = dist / 200 ~ 3.9
      // against a half-thickness of 12.5, so the plane reached a third of the way in. Exactly
      // the sliver that was visible, and exactly why it did not depend on the zoom factor.
      //
      // ADDITIVE, AND THE OLD FACTOR IS GONE. `out * 1.02` is the wrong shape for this: a
      // multiplicative margin scales with the part — generous on a 400 mm rail, nothing at
      // all on an 8 mm dowel — while what it has to cover is a fixed distance in front of the
      // camera. It only ever looked right because it was not the term that mattered.
      //
      // The percent that remains is for the support itself, which reads every ninth vertex
      // and so falls a little short of the true extent; the half-near on top keeps the plane
      // off the surface rather than grazing it, which flickers.
      controls.minDistance = out * 1.01 + camera.near * 1.5;
    };

    const shareScratch = new THREE.Vector3();
    // AND ONLY WHEN IT HAS ACTUALLY MOVED. This is called from the render loop, which runs
    // whether anything changed or not, and every call re-aims every thumbnail on the page
    // and re-renders it. A dot product against the last one sent is what keeps a still
    // viewer from driving a strip of canvases at sixty frames a second — the same reason
    // the readout below it reports on the whole degree and no oftener.
    let dirSent: [number, number, number] | null = null;
    const shareView = (persist: boolean) => {
      if (!opts.sharedView) return;
      const dir = shareScratch.copy(camera.position).sub(controls.target);
      if (dir.lengthSq() < 1e-12) return;
      dir.normalize();
      // A ten-thousandth of the unit sphere, which is far finer than a pixel of movement on
      // a 96-pixel tile and coarse enough that floating-point noise on a still camera does
      // not read as a turn.
      const moved = !dirSent
        || dir.x * dirSent[0] + dir.y * dirSent[1] + dir.z * dirSent[2] < 0.9999;
      if (!moved && !persist) return;
      dirSent = [dir.x, dir.y, dir.z];
      setMeshView({ dir: dirSent }, persist);
    };

    // WHAT ZOOM 1 IS FROM WHERE THE CAMERA STANDS NOW: the distance that fits the framed parts
    // seen from this side and centred on this point — the same fit `look` puts a shot at.
    //
    // The readout divided by the SQUARE-ON distance instead, and so did `zoomTo`, while `look`
    // fitted the angle it was given: a shot recorded at zoom 1, 25° round and aimed off the
    // middle, stood further back than home and read 0.8 in the corner. Typed back in, 0.8 put
    // the camera further off again and read 0.64. One reference for all three, and a number read
    // here is the number that puts the camera where it was read.
    const fitEye = new THREE.Vector3();
    const fitUp = new THREE.Vector3();
    const fitHere = (): number => {
      if (!framed) return openedCentred;
      fitEye.subVectors(camera.position, controls.target);
      if (fitEye.lengthSq() < 1e-12) return openedCentred;
      fitEye.normalize();
      fitUp.set(0, 1, 0).applyQuaternion(camera.quaternion).normalize();
      return fitCentred(framed, fitEye, fitUp, controls.target);
    };

    // BETWEEN ZOOM 1 AND ZOOM_MOST (Louis 2026-10-28). Never further back than the fit: the wheel
    // stood the camera at ×0.53, a small chair in a field of cream, and no shot is ever wider. Nor
    // closer than a hundredth of it. Put back along the line it looks down, every frame, so a turn
    // that changes the fit is held too.
    const ZOOM_MOST = 100;
    // UNLESS THE SHOT ON SCREEN IS WIDER (Louis 2026-10-28): the parts screen stands a washer at
    // half its card (see TutorialKit's SMALLEST_DRAWN), which is a shot under ×1, and the floor
    // made every part fill its card. The floor is the widest of ×1 and the shot `look` last put.
    let zoomLeast = 1;
    const zoomHeld = (zoom: number) => Math.min(ZOOM_MOST, Math.max(zoomLeast, zoom));
    const holdZoom = () => {
      if (!framed) return;
      const line = new THREE.Vector3().subVectors(camera.position, controls.target);
      const away = line.length();
      const fit = fitHere();
      if (!(away > 0) || !(fit > 0)) return;
      const most = fit / zoomLeast;
      const to = away > most * 1.0001 ? most : away < (fit / ZOOM_MOST) * 0.9999 ? fit / ZOOM_MOST : away;
      if (to !== away) camera.position.copy(controls.target).add(line.multiplyScalar(to / away));
    };

    const reportView = () => {
      shareView(false);
      if (!opts.onView) return;
      relQuat.copy(squareOn).invert().multiply(camera.quaternion);
      relEuler.setFromQuaternion(relQuat, 'XYZ');
      const deg: [number, number, number] = [
        Math.round((relEuler.x * 180) / Math.PI),
        Math.round((relEuler.y * 180) / Math.PI),
        Math.round((relEuler.z * 180) / Math.PI),
      ];
      // −0 prints as "-0", which is a minus sign appearing and disappearing on a number that
      // has not changed.
      for (let i = 0; i < 3; i += 1) if (Object.is(deg[i], -0)) deg[i] = 0;
      // Two hundredths of a turn is the precision the readout prints, so anything finer is
      // invisible as well as a render. A wheel-zoom moves this without moving a single
      // degree, which is why it is its own test rather than a passenger on the angles'.
      const dist = camera.position.distanceTo(controls.target) || 1;
      const zoom = Math.round(((fitHere() || dist) / dist) * 100) / 100;
      // WHERE THE SHOT IS CENTRED, out of world space and into the assembly's — `root` holds
      // the Z-up-to-Y-up quarter turn and `pivot` holds however far the framing has slid the
      // assembly across the picture, and worldToLocal undoes both in one step. Tenths of a
      // centimetre, which is the precision printed.
      const local = root.worldToLocal(centreScratch.copy(controls.target));
      const centre: [number, number, number] = [
        Math.round(local.x) / 10, Math.round(local.y) / 10, Math.round(local.z) / 10,
      ];
      for (let i = 0; i < 3; i += 1) if (Object.is(centre[i], -0)) centre[i] = 0;

      const sameAngles = viewSent
        && deg[0] === viewSent[0] && deg[1] === viewSent[1] && deg[2] === viewSent[2];
      const sameCentre = centreSent
        && centre[0] === centreSent[0] && centre[1] === centreSent[1] && centre[2] === centreSent[2];
      if (sameAngles && sameCentre && zoom === zoomSent) return;
      viewSent = deg;
      zoomSent = zoom;
      centreSent = centre;
      opts.onView({ deg, zoom, centre });
    };

    // WRITTEN DOWN WHEN THE GESTURE SETTLES, and not on every frame of it. The angle is
    // followed live — that is what makes a strip of thumbnails turn WITH the mouse — but
    // saving it is a synchronous disk touch, and a drag is one decision rather than three
    // hundred. See meshView, which draws the same line.
    const onSettled = () => shareView(true);
    controls.addEventListener('end', onSettled);

    let raf = 0;
    const tick = () => {
      // THE MESHES SLIDING APART OR BACK TOGETHER — see explode.
      for (const [id, s] of apart) {
        if (s.e === s.to) continue;
        const k = Math.min(1, (performance.now() - s.t0) / EXPLODE_MS);
        s.e = k >= 1 ? s.to : s.from + (s.to - s.from) * smoothstep(k);
        spread(id, s.e);
        remeasure();
      }
      if (gliding) {
        const k = Math.min(1, (performance.now() - gliding.t0) / GLIDE_MS);
        // Smoothstep, so it leaves and arrives at rest. A linear turn stops dead at the end
        // and reads as a jump backwards on the last frame.
        const e = k * k * (3 - 2 * k);
        camera.position.copy(gliding.from).applyAxisAngle(gliding.axis, gliding.angle * e);
        standLevel();
        if (k >= 1) gliding = null;
      }
      const now = performance.now();
      for (const s of sliding) slide(s, now);
      // THE MARKS, if this viewer was given a way to ask for them. Compared as one short
      // string — the whole list against the last — so an unchanged answer costs no geometry
      // at all, and a list that gained a row rebuilds every mark rather than working out
      // which one moved. There are never more than a handful.
      if (opts.holeMarks) {
        const want = opts.holeMarks();
        // The colour is part of the answer, so it is part of the key: a mark relit in another
        // row's colour is a different picture and has to be rebuilt. Same argument as the
        // pieces below, where it is written out.
        // AND THE RINGS, which is the one thing here that is not a number about a place.
        //
        // Everything else in this key is a fact about where a mark sits, and for a long time
        // that was all a mark was. A step-two answer carries the borders its cut opened AND
        // the capsule it was cut with — so a row going from "capsule" to "capsule and four
        // borders" changes not one field of this key, and the rings sat in state, switched on,
        // with the scene certain it had nothing to redraw.
        //
        // Their shape and not their contents: how many rings and how long each is, plus one
        // coordinate to tell two cuts of the same shape apart. A checksum over some thousands
        // of floats, sixty times a second, to answer a question their lengths already answer.
        const ringKey = (m: HoleMark) => (m.rings ?? [])
          .map((r) => `${r.length}@${r[0] ?? ''}`).join(',');
        const key = want
          .map((m) => `${m.on ?? ''}|${m.face}|${m.u}|${m.v}|${m.diameter}|${m.depth}|${m.colour ?? ''}`
            + `|${m.ball ?? ''}|${ringKey(m)}|${m.fit?.src ?? ''}`)
          .join(';');
        if (key !== drawnMark) {
          drawnMark = key;
          fitGen += 1;
          sliding.length = 0;
          for (const id of drills.keys()) markHole(id, want);
        }
      }
      // The target first: the limit below is measured from it, so it has to be somewhere
      // measuring from means something before anything is measured.
      holdTarget();
      keepOut();
      controls.update();
      holdZoom();
      // AND WHICH FACE NAMES THE PART IS STANDING IN FRONT OF, which changes as the shot turns
      // and not as the parts move — so it is asked here rather than in the placement pass
      // above. See ExtentDims.facing: it walks six dot products and only touches the labels
      // when one of them has crossed.
      for (const dims of extents.values()) dims.facing(camera.position);
      // Every frame, and cheap: it is a distance and three logarithms, and it no-ops unless
      // the answer has actually changed rung. Here rather than on the controls' change event
      // because the camera also moves under the framing, the home button and the glide.
      pickGizmoStep();
      reportView();
      placeHud();
      fitShell();
      holdLabels(scene, camera, viewHeight());
      // A CAMERA THAT MOVED WITHOUT A DRAG STEP saying so — damping, a zoom, a shot, a turn.
      if (trace) {
        const c = camState();
        const now = `${fmt(c.tip)} ${fmt(c.az)} ${c.top.toFixed(2)}`;
        if (now !== lastCam && !draggedThisFrame) {
          trace.say(`frame tip=${fmt(c.tip)} az=${fmt(c.az)} top=${c.top.toFixed(2)} away=${c.away.toFixed(3)}${gliding ? ' gliding' : ''}`);
        }
        lastCam = now;
        draggedThisFrame = false;
      }
      render();
      if (!drawnOnce && objects.size) { drawnOnce = true; load?.say(`first frame with the parts ${since()}`); }
      raf = requestAnimationFrame(tick);
    };
    step('camera, controls, drag, resize');
    tick();
    step('first tick');

    function markHole(id: string, all: HoleMark[]): void {
        const marks = all.filter((m) => !m.on || m.on === id);
        const group = drills.get(id);
        if (!group) return;
        for (const old of [...group.children]) {
          group.remove(old);
          (old as import('three').Mesh).geometry?.dispose();
        }
        const part = outlinable.find((o) => o.instanceId === id);
        const size = part?.size;
        if (!marks.length || !size || !part) { group.visible = false; return; }

        // THE BORDERS THEMSELVES, straight from the numbers the cut handed back. Nothing to
        // search for and nothing to intersect: these ARE the edges that were opened, in the
        // frame this scene draws, so they go on as they come.
        // OUT OF THE FILE'S FRAME AND INTO THIS ONE. The rings arrive as coordinates in the
        // GLB's own space, which is the space `part.mesh` holds — and that mesh is RECENTRED
        // under its holder when the scene is built (`mesh.position.sub(box.getCenter(…))`), so
        // the two frames differ by at least that translation. Everything else in this group is
        // placed from `pointOnFace`, which is the holder's space by construction and never had
        // to know. Drawn raw, the rings land wherever the file happened to put the part.
        part.holder.updateMatrixWorld(true);
        const fromFile = part.holder.matrixWorld.clone().invert().multiply(part.mesh.matrixWorld);
        const atPoint = new THREE.Vector3();

        // Reused down the loops below rather than made per segment: a border is some hundreds
        // of edges and this runs whenever a row is lit.
        const segA = new THREE.Vector3();
        const segB = new THREE.Vector3();
        const along = new THREE.Vector3();
        const turn = new THREE.Quaternion();
        const stand = new THREE.Vector3(0, 1, 0);
        const put1 = new THREE.Matrix4();
        const fat = new THREE.Vector3();
        const ringBox = new THREE.Box3();
        const ringSize = new THREE.Vector3();

        for (const mark of marks) {
          const rings = (mark.rings ?? []).filter((ring) => ring.length >= 6);
          if (!rings.length) continue;
          // THE POINTS FIRST, IN THIS SCENE'S FRAME, AND HOW BIG THE THING IS — because how
          // thick to draw it is a question about the bore, and the bore is only known once
          // its borders have been read.
          //
          // THE WIDEST BORDER OF THE MARK, and not this one, and not all of them together.
          // A through hole leaves its two mouths tens of millimetres apart, so the pair
          // measured as one gives a rod sized by how THICK the board is; and a border can
          // come back as a stray fragment of a dozen edges, which measured alone would be a
          // thread beside its neighbours. The biggest ring is the bore, and every rod on the
          // mark is drawn to it.
          let widest = 0;
          const points = rings.map((ring) => {
            const put = new Float32Array(ring.length);
            ringBox.makeEmpty();
            for (let i = 0; i + 2 < ring.length; i += 3) {
              atPoint.set(ring[i], ring[i + 1], ring[i + 2]).applyMatrix4(fromFile);
              put[i] = atPoint.x; put[i + 1] = atPoint.y; put[i + 2] = atPoint.z;
              ringBox.expandByPoint(atPoint);
            }
            ringBox.getSize(ringSize);
            widest = Math.max(widest, ringSize.x, ringSize.y, ringSize.z);
            return put;
          });
          const rad = widest * RING_FAT;
          if (!(rad > 0)) continue;

          points.forEach((put, n) => {
            // ONE ROD PER SEGMENT, all of a ring's in ONE object. The cut sends its border as
            // PAIRS of points rather than an ordered walk — a vertex where three border edges
            // meet is exactly the malformed case worth seeing, and not a reason for the
            // picture to fail — so there is nothing to sweep a tube along and every segment
            // is its own little cylinder. Instanced: one draw call and one geometry for the
            // several hundred of them a bore leaves.
            const count = Math.floor(put.length / 6);
            const rod = new THREE.InstancedMesh(
              // Its own geometry per ring, because markHole disposes what it removes — a
              // shared one would be disposed by the first ring taken off the part and gone
              // for every ring drawn after.
              new THREE.CylinderGeometry(rad, rad, 1, 6, 1),
              ringMats[n % ringMats.length],
              count,
            );
            for (let i = 0; i < count; i += 1) {
              segA.set(put[i * 6], put[i * 6 + 1], put[i * 6 + 2]);
              segB.set(put[i * 6 + 3], put[i * 6 + 4], put[i * 6 + 5]);
              along.subVectors(segB, segA);
              const len = along.length();
              if (len <= 0) { rod.setMatrixAt(i, put1.makeScale(0, 0, 0)); continue; }
              // The cylinder stands up the Y axis at unit height, so it is turned onto the
              // segment and stretched to its length — the radius stays as the geometry has it.
              turn.setFromUnitVectors(stand, along.divideScalar(len));
              rod.setMatrixAt(i, put1.compose(
                segA.addScaledVector(along, len / 2), turn, fat.set(1, len, 1),
              ));
            }
            rod.instanceMatrix.needsUpdate = true;
            rod.raycast = () => {};
            rod.renderOrder = DRAW_ORDER.cross - 1;
            group.add(rod);
          });
        }

        // EVERY CAPSULE ON SCREEN, gathered as the marks are laid out and read once at the end:
        // the faces are found in ONE pass over the mesh however many balls are down, where a
        // pass per mark would walk half a million triangles four times to answer four
        // questions about the same triangles.
        const bites: { a: import('three').Vector3; b: import('three').Vector3; r: number }[] = [];

        for (const mark of marks) {
          const box = pointOnFace(mark, { x: size.x, y: size.y, z: size.z });
          // A mark this part cannot place — a face whose size nobody measured — is skipped
          // rather than taking the others down with it.
          if (!box) continue;
          const n = faceNormal(mark.face);

          // A MARK THAT IS ONLY ITS RINGS. Step two's answer carries the borders its cut left
          // and nothing else — no width, no ball, no mouth to sit under. Everything below
          // this places things at `at`, the point on the surface where the bore comes out,
          // and that point is worked out by firing rays down the face's normal.
          //
          // THERE IS NO WOOD THERE ANY MORE. The cut has just removed it, so the rays find
          // nothing, the mark falls back to the bounding box's plane — a place in the air —
          // and draws itself amber to say "nothing under this spot", which is true and is not
          // an answer to any question being asked. The rings are drawn from the server's own
          // coordinates and need none of it.
          //
          // So it is skipped, rays and all: eight of them over half a million triangles, to
          // place a cross beside a picture that already says where it is.
          const ringsOnly = !!mark.rings?.length && !mark.ball && !(mark.diameter > 0);

          // ON THE SURFACE, NOT ON THE BOX. The face a hole is named on is a face of the
          // bounding box — a plane through the part's furthest point that way — and a part is
          // only rarely a box: a rail with a tenon proud at one end has its whole side face
          // defined by that tenon, with the board itself sitting several millimetres behind.
          // A ring drawn on the plane would float in front of the part.
          //
          // SO THE SURFACE IS FOUND ROUND THE MOUTH, AND NOT DOWN THE MIDDLE.
          //
          // One ray down the axis answers "where would a drill first touch", which is the
          // right question for a hole that does not exist yet and the wrong one for every
          // hole this table reports: at the centre of a hole that is ALREADY THERE, the first
          // material along the axis is that hole's own floor. Every mark sank one bore-length
          // into the part, the mouth ring landing where the floor is and the floor ring a
          // bore-length beyond the wood.
          //
          // A ring of rays at the mouth's own radius answers the question actually being
          // asked — where does the face begin here — because the mark is a circle, not a
          // point, and the material around a hole is what the circle sits on. The SHALLOWEST
          // of them wins: a probe that falls down the bore, or into a neighbouring one, reads
          // deep and loses to the wood beside it.
          const reach = Math.max(size.x, size.y, size.z) * 2;
          const r = Math.max(0, mark.diameter) / 2;
          // How much part there is along this face's normal — the box's own extent that way.
          const thickness = Math.abs(n[0]) * size.x + Math.abs(n[1]) * size.y + Math.abs(n[2]) * size.z;
          const aim = new THREE.Vector3(-n[0], -n[1], -n[2]);
          // THE BOX'S POINT IS THE FALLBACK, and the rays are wrapped so they can only ever be
          // an improvement on it. A mark that cannot be placed where the material is should
          // land on the plane it was named against — never nowhere, and never stuck where the
          // last one was, which is what an exception in here would look like from the outside:
          // a preview that has stopped following the numbers.
          let at = box;
          // THE READING'S OWN ANSWER FIRST, WHERE THERE IS ONE.
          //
          // A row is a reading of the mesh AS IT WAS. Re-deriving the mouth here measures the
          // mesh AS IT IS, and after any edit those are answers to different questions — fill
          // one hole and the mark on a hole nobody touched moves, because the rays now meet a
          // surface that has been rewritten under a table that has not. The row is not made
          // truer by a fresher measurement of something else; it is made wrong by one.
          //
          // Bounded like the probe below and for the same reason: a mouth in front of the face
          // or past the far side is not a surface, whatever said so.
          if (mark.surface != null && mark.surface >= 0 && mark.surface <= thickness) {
            const deep = -mark.surface;
            at = [box[0] + n[0] * deep, box[1] + n[1] * deep, box[2] + n[2] * deep];
          }
          // AND THE RAYS ONLY WHERE NOBODY HAS SAID — which is the drill form, where the hole
          // does not exist yet and nothing could have measured it.
          try {
            // Skipped where the reading already answered: eight rays over half a million
            // triangles is not a measurement worth taking twice, and the second one is the
            // one that can disagree.
            if (mark.surface == null && !ringsOnly) {
              part.holder.updateMatrixWorld(true);
              const dir = aim.clone()
                .applyQuaternion(part.holder.getWorldQuaternion(new THREE.Quaternion()))
                .normalize();
              // The two axes across this face. n is axis-aligned, so they are simply the other
              // two — and which of them is u and which is v does not matter to a circle.
              const across = [0, 1, 2].filter((i) => n[i] === 0);
              // How far in the material starts under one point of the face, or null where the
              // ray met nothing at all. Negative going in, and measured from the probe's own
              // start so every answer is comparable.
              const under = (offA: number, offB: number): number | null => {
                const p = [box[0], box[1], box[2]];
                p[across[0]] += offA;
                p[across[1]] += offB;
                const hit = new THREE.Raycaster(
                  part.holder.localToWorld(new THREE.Vector3(
                    p[0] + n[0] * reach, p[1] + n[1] * reach, p[2] + n[2] * reach,
                  )),
                  dir, 0, reach * 3,
                ).intersectObject(part.mesh, true)
                  .find((h) => (h.object as import('three').Mesh).isMesh);
                if (!hit) return null;
                const [hx, hy, hz] = part.holder.worldToLocal(hit.point.clone()).toArray();
                // HOW FAR IN, AND ONLY HOW FAR IN. The hit is put back onto the ray it was cast
                // along rather than used as it comes: this step answers where the MATERIAL
                // starts, and the place across the face was answered before any ray was fired.
                // Read whole, a hit off the ray by a hand's breadth moves the mark that far
                // across the part — which is what a wireframe's idea of a "hit" did here.
                return n[0] * (hx - p[0]) + n[1] * (hy - p[1]) + n[2] * (hz - p[2]);
              };
              // Just outside the bore, so the ring stands on wood rather than on air. With no
              // width typed yet there is no mouth to stand round, and the axis is all there is.
              const round = r > 0 ? r * 1.25 : 0;
              const found: number[] = [];
              for (let k = 0; round > 0 && k < 8; k += 1) {
                const turn = (k / 8) * Math.PI * 2;
                const deep = under(Math.cos(turn) * round, Math.sin(turn) * round);
                if (deep != null) found.push(deep);
              }
              if (!found.length) {
                const deep = under(0, 0);
                if (deep != null) found.push(deep);
              }
              // AND A PROBE MAY ONLY SAY HOW DEEP, WITHIN THE PART. The box is the part's own
              // extent, so a reading in front of the face or past its far side is not a surface
              // whatever the ray hit — and a mark placed on one floats in the air, red, as
              // though it had found wood there. Out of range it falls back to the box's plane,
              // which is drawn amber: "there is nothing under this spot" is a picture somebody
              // can act on, and a bore standing off the part is not.
              if (found.length) {
                const deep = Math.max(...found);
                if (deep <= 0 && -deep <= thickness) {
                  at = [box[0] + n[0] * deep, box[1] + n[1] * deep, box[2] + n[2] * deep];
                }
              }
            }
          } catch {
            // Left on the box's own plane, and drawn amber for it — see the two materials.
          }
          // AMBER FOR AIR WHATEVER ROW IT IS, then this row's colour, then the form's red.
          const [mat, boreMat] = at === box
            ? [drillAirMat, drillAirBoreMat]
            : mark.colour != null ? rowMat(mark.colour) : [drillMat, drillBoreMat];
          // Off the surface by a hair, in the direction the face looks. The mark ignores the
          // depth buffer, so this is not about z-fighting — it is so the ring reads as sitting
          // ON the face rather than sunk into it when the part is turned edge-on.
          const lift = Math.max(size.x, size.y, size.z) * 0.002;
          const spun = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, 1), new THREE.Vector3(n[0], n[1], n[2]),
          );
          // `down` sinks the piece into the part along the face's own normal, which is what
          // lets the bore be drawn as three things at three depths — its mouth, its wall and
          // its floor — off one placement rule.
          // `side` is which way the hair of lift goes: out of the near face for the pieces at
          // the mouth, and out of the FAR one for the pieces at the other end — where "out"
          // is the other way along the same normal. Lifted the near way, the far cross sinks
          // into the wood instead of standing off it, which is the one thing lift exists to
          // stop.
          const put = (mesh: import('three').Mesh, down = 0, side = 1) => {
            const off = lift * side - down;
            mesh.position.set(at[0] + n[0] * off, at[1] + n[1] * off, at[2] + n[2] * off);
            mesh.quaternion.copy(spun);
            mesh.raycast = () => {};
            mesh.renderOrder = DRAW_ORDER.cross;
            group.add(mesh);
          };
          // HOW FAR THE BORE GOES, and nothing drawn beyond what is known.
          //
          // A MEASURED DEPTH WHEREVER THERE IS ONE, through or blind alike: find_holes reports
          // the material a through hole actually crosses, which on a part whose box is thicker
          // than its board — a backrest 135 mm deep across a 32 mm panel — is the length of the
          // bore rather than the width of the air around it.
          //
          // The box's far side only where nobody has said, which is the drill form with
          // "through" ticked and no depth to type: there the length is not known and the honest
          // picture is a tube leaving the part.
          const sunk = n[0] * (at[0] - box[0]) + n[1] * (at[1] - box[1]) + n[2] * (at[2] - box[2]);
          const bore = Math.max(0, mark.depth ?? (mark.through ? thickness + sunk : 0));
          // THE HARDWARE, once its mesh is in. Placed from the same mouth and normal as the
          // bore, and only if the marks have not been redrawn while it loaded.
          if (mark.fit) {
            const { fit } = mark;
            const gen = fitGen;
            const mouth = at;
            const through = !!mark.through;
            void loadFit(loader, fit.src).then((source) => {
              if (!source || gen !== fitGen) return;
              const s = placeFit(THREE, source, fit, mouth, n, bore, through);
              if (!s) {
                console.warn('[holes] the hardware mesh has no size to scale it by', fit.src);
                return;
              }
              group.add(s.obj);
              sliding.push(s);
            });
          }
          // NOT WHERE THE HARDWARE IS SHOWN: the ring and the cross would sit over the very
          // thing being looked at.
          if (r > 0 && !mark.fit) {
            const thin = Math.max(r * 0.12, Math.max(size.x, size.y, size.z) * 0.004);
            const ring = () => new THREE.RingGeometry(Math.max(r - thin, r * 0.5), r, 48);
            // ITS MOUTH, ITS WALL AND ITS FLOOR. The ring alone was a hole drawn as a circle,
            // which is the one thing a hole is not: what somebody is checking against the part
            // is how deep it goes and whether it comes out, and neither is a fact about a
            // circle. The wall is faint and the two ends are not, so the bore reads as a length
            // with a start and a finish rather than as a red rod laid over the picture.
            put(new THREE.Mesh(ring(), mat));
            if (bore > 0) {
              put(new THREE.Mesh(
                new THREE.CylinderGeometry(r, r, bore, 48, 1, true).rotateX(Math.PI / 2),
                boreMat,
              ), bore / 2);
              put(new THREE.Mesh(ring(), mat), bore, -1);
            }
          }
          // AND THE BALL A FILL WOULD TAKE, where the mark is about one. A sphere at each
          // mouth, drawn faint so the wood inside it is still visible — the question it is
          // asked to answer is how much of the part goes, and a solid red blob answers it by
          // hiding exactly the thing being weighed.
          //
          // ALWAYS THE FORM'S RED, never the row's colour: every other mark here is a reading
          // of what the mesh HAS, and this one is what a press is about to remove. Red is what
          // this app means by that everywhere else.
          if (mark.ball && mark.ball > 0) {
            const half = mark.ball / 2;
            // The rod this capsule is: the mouth, and `bore` along the face's own normal into
            // the part. The same two points the domes and the tube below are placed at, so the
            // faces painted from it cannot disagree with the skin drawn over them.
            bites.push({
              a: new THREE.Vector3(at[0], at[1], at[2]),
              b: new THREE.Vector3(
                at[0] - n[0] * bore, at[1] - n[1] * bore, at[2] - n[2] * bore,
              ),
              r: half,
            });
            if (bore > 0) {
              // THE CAPSULE, AS ITS OWN SKIN AND NOTHING ELSE: a dome at each end facing away
              // from the other, and the open tube between them.
              //
              // WHOLE SPHERES PUT A SURFACE INSIDE THE SHAPE. The half of each ball that faces
              // the middle lies within the rod's own volume, so looking down the capsule went
              // through the rod's near wall, then a dome, then the wood, then another dome,
              // then the far wall — four translucent layers over a shape that has two, and the
              // mesh being weighed is what they fog. The silhouette is identical without them.
              //
              // `spun` turns +Z onto the face's normal and `down` sinks along it, so the mouth
              // end domes towards +Z and the far end towards −Z. A hemisphere is built round +Y
              // (theta 0 to a quarter turn), and a quarter turn about X lays it on one or the
              // other.
              const cap = (out: number) => new THREE.SphereGeometry(
                half, 32, 12, 0, Math.PI * 2, 0, Math.PI / 2,
              ).rotateX((out * Math.PI) / 2);
              put(new THREE.Mesh(cap(1), drillBoreMat));
              put(new THREE.Mesh(cap(-1), drillBoreMat), bore);
              put(new THREE.Mesh(
                new THREE.CylinderGeometry(half, half, bore, 48, 1, true).rotateX(Math.PI / 2),
                drillBoreMat,
              ), bore / 2);
            } else {
              // Nothing found at the far end, so there is no rod and the ball IS the skin.
              put(new THREE.Mesh(new THREE.SphereGeometry(half, 32, 24), drillBoreMat));
            }
          }
          // The centre mark, sized off the part and not off the hole: it says "here", and a
          // cross that shrank with the bore would vanish on the 3 mm one, which is the hole
          // whose place is hardest to see.
          //
          // AT BOTH ENDS OF A THROUGH HOLE, because both ends are somewhere on the part and a
          // hole that comes out has two places to check. One cross said where it was drilled
          // and left the far mouth marked by a ring alone — the fainter of the two things
          // this mark is made of, and the one that reads as a bore's outline rather than as a
          // position. It is also the end somebody working from the other side is looking at.
          if (!ringsOnly && !mark.fit) {
            const arm = Math.max(size.x, size.y, size.z) * 0.02 || 1;
            const bar = arm * 0.18;
            const ends: [number, number][] = [[0, 1]];
            if (mark.through && bore > 0) ends.push([bore, -1]);
            for (const [down, side] of ends) {
              for (const across of [0, 1]) {
                put(new THREE.Mesh(
                  new THREE.PlaneGeometry(across ? bar : arm * 2, across ? arm * 2 : bar),
                  mat,
                ), down, side);
              }
            }
          }
        }

        // AND THE FACES THOSE CAPSULES WOULD TAKE. The same test the cut makes: a triangle
        // goes if the point midway between its three corners lies within the rod.
        //
        // IN THE HOLDER'S FRAME, which is the frame the marks were just placed in — the
        // mesh may sit under a transform of its own inside the part, so each geometry's
        // vertices are carried up through it rather than read where they lie.
        if (bites.length) {
          part.holder.updateMatrixWorld(true);
          const toHolder = part.holder.matrixWorld.clone().invert();
          const local = new THREE.Matrix4();
          const va = new THREE.Vector3();
          const vb = new THREE.Vector3();
          const vc = new THREE.Vector3();
          const mid = new THREE.Vector3();
          const rel = new THREE.Vector3();
          const seg = new THREE.Vector3();
          const doomed = (p: import('three').Vector3): boolean => bites.some((bite) => {
            seg.subVectors(bite.b, bite.a);
            const len2 = seg.lengthSq();
            rel.subVectors(p, bite.a);
            // How far along the rod the nearest point is, clamped to its two ends — which is
            // what rounds the ends off into the domes the two mouths need.
            const along = len2 > 0 ? Math.max(0, Math.min(1, rel.dot(seg) / len2)) : 0;
            return rel.addScaledVector(seg, -along).length() <= bite.r;
          });
          const keep: number[] = [];
          part.mesh.traverse((child) => {
            const m = child as import('three').Mesh;
            if (!m.isMesh || !m.geometry) return;
            const pos = m.geometry.getAttribute('position');
            if (!pos) return;
            local.multiplyMatrices(toHolder, m.matrixWorld);
            const idx = m.geometry.getIndex();
            const count = idx ? idx.count : pos.count;
            for (let i = 0; i + 2 < count; i += 3) {
              const a = idx ? idx.getX(i) : i;
              const b = idx ? idx.getX(i + 1) : i + 1;
              const c = idx ? idx.getX(i + 2) : i + 2;
              va.fromBufferAttribute(pos, a).applyMatrix4(local);
              vb.fromBufferAttribute(pos, b).applyMatrix4(local);
              vc.fromBufferAttribute(pos, c).applyMatrix4(local);
              mid.copy(va).add(vb).add(vc).multiplyScalar(1 / 3);
              if (!doomed(mid)) continue;
              keep.push(va.x, va.y, va.z, vb.x, vb.y, vb.z, vc.x, vc.y, vc.z);
            }
          });
          if (keep.length) {
            const faces = new THREE.BufferGeometry();
            faces.setAttribute('position', new THREE.Float32BufferAttribute(keep, 3));
            const painted = new THREE.LineSegments(
              new THREE.WireframeGeometry(faces), drillCutMat,
            );
            // The faces themselves were only ever the way to their edges, and nothing in the
            // scene holds them: the wireframe copied what it needed.
            faces.dispose();
            painted.raycast = () => {};
            // Under the crosses and the rings, which are what say WHERE, and above the part's
            // own wires, which are what this is recolouring.
            painted.renderOrder = DRAW_ORDER.cross - 1;
            group.add(painted);
          }
        }
        group.visible = true;
    }

    // WHAT THE CLASH CHECK MEASURES, gathered from the meshes this mount already holds and
    // then kept: the geometries and their transforms do not change once the parts have
    // landed — only the holders move, and a measurement supplies its own poses.
    let measurable: ClashPart[] | null = null;
    // AND WHERE THEY ARE MEASURED: off this thread, by a worker given a copy of them at the first
    // question (see assemblyClashRemote). Ended with the scene.
    let measurer: ClashRemote | null = null;
    const measuring = () => {
      measurable ??= clashParts(THREE, bodies);
      return (measurer ??= clashRemote(measurable));
    };

    // THE OVERLAYS — see AssemblyView.overlays. Each file fetched once per mount; the answer to the
    // latest list is the only one drawn, a slower file for an earlier press being dropped.
    const overlayGroup = new THREE.Group();
    const overlayFiles = new Map<string, Promise<import('three').Object3D | null>>();
    // The copies hung on the model's own meshes, taken off before the next list is drawn.
    const overlayHung: import('three').Object3D[] = [];
    let overlayAsked = '';
    const overlays = (list: readonly ViewerOverlay[]) => {
      const key = list.map((o) => `${o.url}#${o.colour}#${(o.on ?? []).join(',')}`).join('|');
      overlayAsked = key;
      const host = meshOf.get('part');
      if (!host) return;
      if (overlayGroup.parent !== host) host.add(overlayGroup);
      void Promise.all(list.map((o) => {
        let file = overlayFiles.get(o.url);
        if (!file) {
          file = new Promise((resolve) => {
            loader.load(o.url, (gltf) => resolve(gltf.scene), undefined, () => resolve(null));
          });
          overlayFiles.set(o.url, file);
        }
        return file.then((solid) => (solid ? { solid, colour: o.colour, on: o.on ?? [] } : null));
      })).then((got) => {
        if (!alive || overlayAsked !== key) return;
        overlayGroup.clear();
        for (const hung of overlayHung.splice(0)) hung.removeFromParent();
        const pieces = piecesOf.get('part') ?? [];
        host.updateMatrixWorld(true);
        const toHost = new THREE.Matrix4().copy(host.matrixWorld).invert();
        for (const one of got) {
          if (!one) continue;
          one.solid.traverse((node) => {
            const mesh = node as import('three').Mesh;
            if (!mesh.isMesh) return;
            (mesh.material as import('three').Material).dispose?.();
            mesh.material = new THREE.MeshBasicMaterial({
              color: one.colour, transparent: true, opacity: 0.85, depthTest: false,
            });
            mesh.renderOrder = 999;
          });
          const owners = one.on.flatMap((name) => pieces.filter((p) => p.name === name));
          if (!owners.length) {
            overlayGroup.add(one.solid);
            continue;
          }
          // A COPY ON EACH, placed back where the solid is in the file's frame: the inverse of where
          // that mesh stands in the model's root when assembled — so the explode that moves the mesh
          // moves the copy with it, and nothing else does.
          for (const piece of owners) {
            const copy = one.solid.clone(true);
            const home = new THREE.Matrix4().compose(
              piece.home ?? piece.obj.position, piece.obj.quaternion, piece.obj.scale,
            );
            const inHost = toHost.clone().multiply(piece.obj.parent!.matrixWorld).multiply(home);
            inHost.invert().decompose(copy.position, copy.quaternion, copy.scale);
            piece.obj.add(copy);
            overlayHung.push(copy);
          }
        }
      });
    };

    // RENDERED ONCE MORE, THEN READ — see AssemblyView.shot.
    const grab = (): string | null => {
      holdLabels(scene, camera, viewHeight());
      // WITHOUT THE CLASH PAINT. A shot is a still, read as what a visitor will see, and a
      // visitor sees no collision reading — the red patches came out in every still of a
      // fitting a tenth of a millimetre tight. Hidden for this one render, and put back.
      const red = [...painted.values()].filter((p) => p.visible);
      red.forEach((p) => { p.visible = false; });
      render();
      red.forEach((p) => { p.visible = true; });
      // AN EMPTY FRAME IS NOT A PICTURE, and saying so is the whole of the difference
      // between a still that is missing and one that is white.
      //
      // A blank PNG filed against a gesture is worse than no file at all: the review would
      // draw it, it would look like a clip of nothing, and the thing that went wrong would
      // be invisible. Null instead, and the caller writes nothing — so a gesture whose
      // picture could not be taken shows as having none, which is a state somebody can see
      // and act on.
      //
      // THE ALPHA CHANNEL IS THE TEST. The renderer is built with `alpha: true` and the
      // white behind a clip is the card's own CSS, so a pixel nothing has drawn to is
      // transparent. Any pixel with alpha at all is a picture.
      const gl = renderer.getContext();
      const { width, height } = renderer.domElement;
      if (!width || !height) return null;
      const px = new Uint8Array(width * height * 4);
      gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, px);
      let drawn = false;
      for (let i = 3; i < px.length; i += 4) {
        if (px[i] !== 0) { drawn = true; break; }
      }
      if (!drawn) return null;
      try {
        return renderer.domElement.toDataURL('image/png');
      } catch {
        // A tainted canvas is the one way this throws, and it cannot happen here — every
        // texture comes from this origin. Caught anyway, because the caller's fallback is
        // simply to keep the live scene, and a thrown exception would take the card with it.
        return null;
      }
    };

    step('before ready (view, holes, shadow set up)');
    if (!opts.heldUntilReleased) letGo();
    load?.say(`ready ${since()}`);
    // WHERE THE CAMERA STANDS AND WHAT IT LOOKS AT, after the caller has framed its shot: three
    // of a page's part cards came up blank, drawn and ready, the part out of sight (Louis
    // 2026-10-23). The picture's size and headroom, the camera, its aim, the parts' box and how
    // far the camera is from it, read twice, once the shot is set and once things have settled.
    if (load) {
      const v = (p: import('three').Vector3) => `${p.x.toFixed(3)},${p.y.toFixed(3)},${p.z.toFixed(3)}`;
      const shotNow = (when: string) => {
        if (!alive) return;
        const box = new THREE.Box3();
        objects.forEach((o) => box.expandByObject(o));
        const size = box.getSize(new THREE.Vector3());
        load.say(`shot ${when}: canvas ${el.clientWidth}x${el.clientHeight} room ${opts.headroom ?? 0}`
          + ` cam ${v(camera.position)} aim ${v(controls.target)} box centre ${v(box.getCenter(new THREE.Vector3()))}`
          + ` size ${v(size)} away ${camera.position.distanceTo(controls.target).toFixed(3)}`
          + ` near ${camera.near.toPrecision(3)} far ${camera.far.toPrecision(3)} aspect ${camera.aspect.toFixed(3)}`);
      };
      setTimeout(() => shotNow('+0.5 s'), 500);
      setTimeout(() => shotNow('+3 s'), 3000);
    }
    if (objects.size) opts.onState?.('ready');
    opts.onReady?.({
      show: (asm, step, t) => { apply(poseAt(asm, step, t)); paletteNow(); },
      clash: (asm, step, moments) => measuring().profile(asm, step, moments),
      clashFaces: (asm, step, t) => measuring().marks(asm, step, t),
      hiddenFrom: (asm, step, eyes, cams) => measuring().hidden(asm, step, eyes, cams),
      overlapsAmong: (asm, pairs) => measuring().overlaps(asm, pairs),
      thickness: (probes) => {
        const part = outlinable[0];
        if (!part) return probes.map(() => null);
        part.holder.updateMatrixWorld(true);
        const size = part.size;
        const reach = Math.max(size.x, size.y, size.z) * 2;
        const turn = part.holder.getWorldQuaternion(new THREE.Quaternion());
        return probes.map((probe) => {
          const box = pointOnFace(probe, { x: size.x, y: size.y, z: size.z });
          if (!box) return null;
          const n = faceNormal(probe.face);
          const dir = new THREE.Vector3(-n[0], -n[1], -n[2])
            .applyQuaternion(turn).normalize();
          // The two axes across this face, as the mark code names them: n is axis-aligned, so
          // they are simply the other two, and which is u and which is v is nothing to a ring.
          const across = [0, 1, 2].filter((i) => n[i] === 0);
          // FIRST HIT TO LAST, along the axis. Not the first two: a part with a rebate or an
          // existing bore in the way is several slabs along one ray, and what a screw crosses
          // is the whole of it. `intersectObject` answers in order, so the ends are the ends.
          const span = (offA: number, offB: number): number | null => {
            const at = [box[0], box[1], box[2]];
            at[across[0]] += offA;
            at[across[1]] += offB;
            const from = part.holder.localToWorld(new THREE.Vector3(
              at[0] + n[0] * reach, at[1] + n[1] * reach, at[2] + n[2] * reach,
            ));
            const hits = new THREE.Raycaster(from, dir, 0, reach * 3)
              .intersectObject(part.mesh, true)
              .filter((h) => (h.object as import('three').Mesh).isMesh);
            if (hits.length < 2) return null;
            const a = part.holder.worldToLocal(hits[0].point.clone()).toArray();
            const b = part.holder.worldToLocal(hits[hits.length - 1].point.clone()).toArray();
            return Math.abs(n[0] * (a[0] - b[0]) + n[1] * (a[1] - b[1]) + n[2] * (a[2] - b[2]));
          };
          // A RING JUST OUTSIDE THE BORE, AND THE MEDIAN OF IT — the same shape the mark's own
          // probe uses and for the same reason: a ray down the middle of a hole that is
          // already drilled measures the air in it. The median rather than the shallowest,
          // because here every reading is wood and the outlier is a probe that clipped an
          // edge or fell into a neighbouring bore.
          const round = Math.max(0, probe.diameter) / 2 * 1.25;
          const found: number[] = [];
          for (let k = 0; k < 8; k += 1) {
            const a = (k / 8) * Math.PI * 2;
            const got = round > 0
              ? span(Math.cos(a) * round, Math.sin(a) * round)
              : span(0, 0);
            if (got != null && got > 0) found.push(got);
          }
          if (!found.length) return null;
          found.sort((x, y) => x - y);
          return found[Math.floor(found.length / 2)];
        });
      },
      clashMarks: (marks) => {
        for (const [id, paint] of painted) {
          const faces = marks?.[id];
          // The measurement's own copy of this part — the geometry those face indices count
          // in. Absent before anything has been measured, which is also when nothing can
          // have been marked.
          const src = measurable?.find((p) => p.id === id);
          if (!faces?.length || !src) { paint.visible = false; paint.geometry.setDrawRange(0, 0); continue; }
          // GROWN FOR A PATCH BIGGER THAN IT WAS BUILT FOR — a picture standing still asks for the
          // whole of one (see STANDING_MARKS); a movement never does.
          if (faces.length * 18 > (paint.geometry.attributes.position.array as Float32Array).length) {
            paint.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(faces.length * 18), 3));
          }
          const out = paint.geometry.attributes.position as import('three').BufferAttribute;
          const write = out.array as Float32Array;
          let at = 0;
          // Three edges per face, each edge two points: the triangle drawn as its own little
          // wireframe, in the same shape as the wireframe it sits inside.
          for (let f = 0; f < faces.length && (f + 1) * 18 <= write.length; f += 1) {
            // In whichever of the part's meshes the face is (see ClashMarks).
            const v = [0, 1, 2].map((k) => faceVertex(src, faces[f], k));
            if (v.some((c) => !c)) continue;
            for (const [from, to] of [[0, 1], [1, 2], [2, 0]]) {
              const p = v[from]!.g.attributes.position;
              const q = v[to]!.g.attributes.position;
              write[at] = p.getX(v[from]!.v); write[at + 1] = p.getY(v[from]!.v); write[at + 2] = p.getZ(v[from]!.v);
              write[at + 3] = q.getX(v[to]!.v); write[at + 4] = q.getY(v[to]!.v); write[at + 5] = q.getZ(v[to]!.v);
              at += 6;
            }
          }
          out.needsUpdate = true;
          paint.geometry.setDrawRange(0, at / 3);
          paint.visible = true;
        }
      },
      select: (id) => { selected = id; showShells(); },
      highlight: (ids) => { lit = new Set(ids); showShells(); },
      hide: (ids) => {
        hidden.clear();
        for (const id of ids) hidden.add(id);
        // Re-drawn from the poses already worked out: hiding a part changes what is on
        // screen and nothing about where anything is.
        apply(placed);
      },
      hidePieces: (id, names) => {
        const off = new Set(names);
        piecesOff.set(id, off);
        for (const piece of piecesOf.get(id) ?? []) {
          piece.obj.visible = !off.has(piece.name);
          // And its copies in the outline, so a hidden mesh leaves no orange behind.
          if (piece.twins) piece.twins.mask.visible = piece.twins.rim.visible = piece.obj.visible;
        }
        // A hidden mesh takes its box with it.
        remeasure();
      },
      explode: (id, on, animate = true) => {
        if (!piecesOf.has(id)) return;
        const to = on ? 1 : 0;
        const was = apart.get(id)?.e ?? 0;
        apart.set(id, { e: animate ? was : to, from: was, to, t0: performance.now() });
        if (!animate) {
          spread(id, to);
          remeasure();
        }
      },
      dimensionLines: (id, spec) => {
        let lines = dimLines.get(id);
        if (!lines) {
          lines = makeDimensionLines(THREE);
          annot.add(lines.group);
          dimLines.set(id, lines);
        }
        lines.set(spec);
        remeasure();
      },
      boxPieces: (id, names) => {
        boxedPieces.set(id, new Set(names));
        if (opts.extentWords && opts.faceWords) {
          for (const name of names) {
            const key = pieceKey(id, name);
            if (pieceExtents.has(key) || !piecesOf.get(id)?.some((p) => p.name === name)) continue;
            const dims = makeExtentDims(THREE, opts.extentWords, opts.faceWords, opts.wantedWords);
            annot.add(dims.group);
            pieceExtents.set(key, dims);
          }
        }
        remeasure();
      },
      frame,
      // RENDERED ONCE MORE, THEN READ. Explicitly rather than trusting whatever the loop last
      // left: a caller asks for this the moment a scene reports ready, and "ready" is about the
      // meshes having landed rather than about a frame having been drawn with them in it.
      shot: grab,
      renderWidth: (px) => {
        renderer.setPixelRatio(px && el.clientWidth ? px / el.clientWidth : Math.min(window.devicePixelRatio, 2));
        resize();
      },
      drawInto: (ctx, w, h) => {
        render();
        ctx.drawImage(surface, 0, 0, w, h);
      },
      draw: (mode) => { drawn = mode; draw.apply(mode); showShells(); },
      overlays,
      turn: (which, degrees) => {
        gliding = {
          from: camera.position.clone(),
          axis: worldAxis(which),
          angle: (degrees * Math.PI) / 180,
          t0: performance.now(),
        };
      },
      // Written into the box, and then the boxes are laid out again — which is what paints
      // the plates. `remeasure` is what every other change to them goes through and it runs
      // when something MOVES, which this does not: an answer landing is the one way a label's
      // words change while the part stands still, so nothing else would ever redraw it.
      // A RING WHERE THE HOLE WOULD BE, and a cross at its centre.
      //
      // BOTH, because they answer different halves and one of them arrives first: the place is
      // typed before the width, so a preview that drew only the circle would be nothing at all
      // through the two fields that decide WHERE. The cross is the point; the ring is how much
      // of the face goes.
      //
      // Measured off the part's own box, which is what the u and the v are measured across
      // (see partFaces.pointOnFace, the one place that arithmetic lives — the same function
      // the Phase 5 contact is placed by and the same one the drill route's Python twin
      // mirrors).
      said: (id, faces) => {
        extents.get(id)?.said(faces);
        remeasure();
      },
      light: (mode) => lighting.apply(mode),
      brightness: (by) => lighting.brighten(by),
      release: letGo,
      floorShadow: () => {
        if (shadowFloor) return;
        const box = new THREE.Box3();
        objects.forEach((o) => { if (o.visible) box.expandByObject(o); });
        if (box.isEmpty()) return;
        const centre = box.getCenter(new THREE.Vector3());
        const reach = box.getSize(new THREE.Vector3()).length();
        renderer.shadowMap.enabled = true;
        // VSM, BLURRED: a soft-edged shadow like a photograph's, where PCF's was a hard-edged grey
        // cut-out.
        renderer.shadowMap.type = THREE.VSMShadowMap;
        objects.forEach((o) => o.traverse((m) => { if ((m as import('three').Mesh).isMesh) m.castShadow = true; }));
        // ITS OWN LIGHT, AT NO INTENSITY: it casts the shadow and lights nothing, so the picture
        // is the set's as before. From above and behind, so it falls towards the viewer as a
        // product photograph has it.
        const sun = new THREE.DirectionalLight(0xffffff, 0);
        sun.target.position.copy(centre);
        // A drag turns and tips it and never slides it (see `onFloor`).
        onFloor = true;
        // And the wheel zooms on the product, not on whatever is under the cursor: a zoom aimed
        // at the empty floor beside it took the view off the product with it.
        controls.zoomToCursor = false;
        // AIMED AT ITS MIDDLE, the camera moved with the aim so the angle is the shot's: the shot
        // was aimed at the last gesture's middle, and a zoom goes towards the aim, so the product
        // slid off-centre as it grew (Louis 2026-10-23).
        const shift = centre.clone().sub(controls.target);
        controls.target.add(shift);
        camera.position.add(shift);
        standLevel();
        controls.update();
        // AND THE LIGHT STAYS IN THE ROOM (Louis 2026-10-28): put once behind the product, to the
        // right and above, as the opening shot sees it, and left there. It used to go round with
        // the camera, so the shadow kept its place on screen as the product turned, and the floor
        // looked as if it turned with it. Now the shadow sweeps round as the view does.
        const toward = new THREE.Vector3().subVectors(controls.target, camera.position).setY(0).normalize();
        const right = new THREE.Vector3().crossVectors(toward, new THREE.Vector3(0, 1, 0)).normalize();
        sun.position.copy(centre)
          .addScaledVector(right.multiplyScalar(0.5).add(toward.multiplyScalar(0.6)).setY(1).normalize(), reach * 2);
        sun.castShadow = true;
        sun.shadow.mapSize.set(1024, 1024);
        sun.shadow.radius = 12;
        sun.shadow.blurSamples = 16;
        // Fitted to the product and no wider, so the map's pixels are spent on it.
        const half = reach * 0.75;
        Object.assign(sun.shadow.camera, {
          left: -half, right: half, top: half, bottom: -half, near: reach, far: reach * 3,
        });
        sun.shadow.camera.updateProjectionMatrix();
        // Nothing but the shadow: the floor itself is the frame's own background.
        // NO WIDER THAN THE SHADOW'S OWN SQUARE (`half`): eight times the product, it ran past the
        // edge of the map, and a blurred map leaves its edge on the floor as a grey line, seen
        // where a small part is framed from far enough back (Louis 2026-10-28).
        const floor = new THREE.Mesh(
          new THREE.PlaneGeometry(half * 1.9, half * 1.9).rotateX(-Math.PI / 2),
          new THREE.ShadowMaterial({ opacity: 0.22 }),
        );
        floor.position.set(centre.x, box.min.y, centre.z);
        floor.receiveShadow = true;
        scene.add(sun, sun.target, floor);
        shadowFloor = { sun, floor };
      },
      viewpoint: () => ({
        at: [camera.position.x, camera.position.y, camera.position.z],
        to: [controls.target.x, controls.target.y, controls.target.z],
      }),
      aimAt: (put) => {
        camera.position.set(put.at[0], put.at[1], put.at[2]);
        controls.target.set(put.to[0], put.to[1], put.to[2]);
        controls.update();
        camera.updateProjectionMatrix();
      },
      placed: () => {
        const out: Placed[] = [];
        root.updateMatrixWorld(true);
        const toRoot = root.matrixWorld.clone().invert();
        for (const [instanceId, { holder, body }] of bodies) {
          if (!holder.visible) continue;
          const made = madeOf.get(instanceId);
          (made ?? [body]).forEach((one, i) => {
            if (!one.visible) return;
            one.updateWorldMatrix(true, false);
            // World, then back out of the root: what is left is the part's place in the
            // assembly, in the assembly's own axes and the scene's millimetres.
            out.push({
              instanceId,
              piece: made ? i : null,
              m: toRoot.clone().multiply(one.matrixWorld).toArray(),
            });
          });
        }
        return out;
      },
      gizmo: (on) => {
        gizmoShown = on;
        if (gizmo) gizmo.group.visible = on;
        // AND THE MARK AT THE ORIGIN GOES WITH THE ARMS. They are one statement in two pieces
        // — the arms say which way each axis runs, the cross says where all three meet — and
        // the cross was answerable by nothing at all: switched off, the gizmo left a small
        // red-green-blue cross standing in the middle of the parts, which reads as a mark on
        // the PART it happens to be over rather than as the world's origin.
        originCross.visible = on;
      },
      // Through each group's own switch and not by reaching for `group.visible`, which is
      // rewritten by every `remeasure` — the lines used to come back the moment the step was
      // played. See ExtentDims.show. Applied by `remeasure`, so a box switched back on is
      // put where its part is in the same pass that shows it.
      measures: (on) => {
        ruled.clear();
        for (const id of on) ruled.add(id);
        remeasure();
      },
      faces: (on) => {
        labelled.clear();
        for (const id of on) labelled.add(id);
        remeasure();
      },
      boxes: (on) => {
        boxed.clear();
        for (const id of on) boxed.add(id);
        remeasure();
      },
      // THE SAME SHOT THE VIEWER OPENED ON — see placeHome, which is now the only thing that
      // aims this camera on its own. A glide in flight is dropped rather than allowed to go
      // on turning a camera that has just been put somewhere.
      home: () => {
        gliding = null;
        placeHome();
      },
      cameraPose: () => {
        root.updateWorldMatrix(true, false);
        const inv = root.matrixWorld.clone().invert();
        const eye = camera.position.clone().applyMatrix4(inv);
        const up = camera.position.clone().add(camera.up).applyMatrix4(inv).sub(eye).normalize();
        const target = controls.target.clone().applyMatrix4(inv);
        return { eye: eye.toArray() as Vec3T, target: target.toArray() as Vec3T, up: up.toArray() as Vec3T };
      },
      // Where it stands and what it looks at; its up is the upright's (see standLevel).
      placeCamera: ({ eye, target }) => {
        gliding = null;
        root.updateWorldMatrix(true, false);
        camera.position.copy(root.localToWorld(new THREE.Vector3(...eye)));
        controls.target.copy(root.localToWorld(new THREE.Vector3(...target)));
        standLevel();
        controls.update();
      },
      carryPose: ({ eye, target, up }, from, to) => {
        const m = new THREE.Matrix4().fromArray(to).multiply(new THREE.Matrix4().fromArray(from).invert());
        const e = new THREE.Vector3(...eye).applyMatrix4(m);
        const u = new THREE.Vector3(eye[0] + up[0], eye[1] + up[1], eye[2] + up[2]).applyMatrix4(m).sub(e).normalize();
        return { eye: e.toArray() as Vec3T, target: new THREE.Vector3(...target).applyMatrix4(m).toArray() as Vec3T, up: u.toArray() as Vec3T };
      },
      meshFrames: () => {
        root.updateWorldMatrix(true, true);
        const toRoot = root.matrixWorld.clone().invert();
        return meshByUrl.map(({ url, inst, obj }) => {
          let shown = true;
          for (let o: import('three').Object3D | null = obj; o && o !== root; o = o.parent) shown &&= o.visible;
          return { url, inst, frame: toRoot.clone().multiply(obj.matrixWorld).elements.slice(), shown };
        });
      },
      meshFrame: (url, among) => {
        const hit = among.map((inst) => meshByUrl.find((m) => m.inst === inst && m.url === url)).find(Boolean);
        if (!hit) return null;
        root.updateWorldMatrix(true, false);
        hit.obj.updateWorldMatrix(true, false);
        return root.matrixWorld.clone().invert().multiply(hit.obj.matrixWorld).elements.slice();
      },
      widthAt: (asm, step, eye, aim) => {
        // THE OUTLINE OF THAT ASSEMBLY, at both ends, put back as it stood: read in one tick, so
        // nothing of it is ever drawn.
        const before = placed;
        const points: import('three').Vector3[] = [];
        for (const t of [0, 1]) {
          apply(poseAt(asm, step, t));
          root.updateMatrixWorld(true);
          takeOutline(points);
        }
        apply(before);
        root.updateMatrixWorld(true);
        if (!points.length) return null;
        // From that side and aimed there, as `look` will stand it.
        const eyeW = new THREE.Vector3(eye[0], eye[1], eye[2]).transformDirection(root.matrixWorld).normalize();
        const aimW = root.localToWorld(new THREE.Vector3(aim[0], aim[1], aim[2]));
        const dist = fitCentred(new THREE.Box3(), eyeW, worldAxis('z'), aimW, points);
        const tanX = Math.tan((camera.fov * Math.PI) / 360) * (camera.aspect || 1);
        return dist > 0 ? 2 * dist * tanX : null;
      },
      look: ({ centre, deg, zoom, auto }) => {
        gliding = null;
        // HOME FIRST, so everything below is measured from the same place the numbers were
        // measured against: the pivot's offset and the current distance are both part of what
        // "turned 30° at ×2" means, and half-applying a shot is a different shot.
        placeHome();
        const aim = root.localToWorld(new THREE.Vector3(centre[0], centre[1], centre[2]));
        const turned = squareOn.clone().multiply(
          new THREE.Quaternion().setFromEuler(new THREE.Euler(
            (deg[0] * Math.PI) / 180,
            (deg[1] * Math.PI) / 180,
            (deg[2] * Math.PI) / 180,
            'XYZ',
          )),
        );
        // A camera faces its own −Z, so it stands a distance along +Z of where it looks. The
        // angles say WHERE FROM and nothing more: it stands level (see standLevel), so whatever
        // roll three angles can spell is dropped.
        const eye = new THREE.Vector3(0, 0, 1).applyQuaternion(turned).normalize();
        const upv = worldAxis('z');
        // AN UNAIMED SHOT LOOKS AT THE MIDDLE OF THE PARTS, and one on its way to or from one at
        // that share of it (see CamAt.auto). Seen from where the camera is going to stand, which
        // is what makes the margin even on both sides of the picture.
        if (auto && auto > 0) {
          const across = new THREE.Vector3().crossVectors(eye, upv).normalize();
          const up = new THREE.Vector3().crossVectors(across, eye).normalize();
          aim.addScaledVector(outlineMiddle(eye, up), auto);
        }
        // AND FITTED FOR THIS ANGLE, not for home's. `openedCentred` is the distance the
        // SQUARE-ON shot needs, and a body that fits the frame from the front is a body seen
        // corner-first from anywhere else — longer across the picture, by up to its own
        // diagonal. Reusing it was what put a screw's tip off the side of the picture at t=0
        // on the first shot the call ever chose for itself. Home's number is still the
        // fallback, for a viewer that has framed nothing to measure.
        const fitted = framed ? fitCentred(framed, eye, upv, aim) : openedCentred;
        // Between ×1 and ×100 (see holdZoom), or this shot where it is wider than ×1.
        zoomLeast = Math.min(1, zoom > 0 ? zoom : 1);
        const dist = (fitted || 1) / zoomHeld(zoom > 0 ? zoom : 1);
        camera.position.copy(aim).add(eye.clone().multiplyScalar(dist));
        controls.target.copy(aim);
        standLevel();
        controls.update();
      },
      // Straight along the view axis, so nothing but the distance changes: no angle to
      // re-derive from three rounded degrees, no centre to hand back. `fitHere` is the
      // reference `reportView` divides by, which is what makes this exact.
      zoomTo: (zoom) => {
        gliding = null;
        const line = new THREE.Vector3().subVectors(camera.position, controls.target);
        const away = line.length();
        if (!(away > 0) || !(zoom > 0)) return;
        camera.position.copy(controls.target)
          .add(line.multiplyScalar((fitHere() || away) / zoomHeld(zoom) / away));
        controls.update();
      },
    });

    cleanup = () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      surface.removeEventListener('pointerdown', onDown);
      surface.removeEventListener('pointerup', onUp);
      stopTurning?.();
      controls.removeEventListener('end', onSettled);
      controls.dispose();
      gizmo?.dispose();
      extents.forEach((e) => e.dispose());
      pieceExtents.forEach((e) => e.dispose());
      dimLines.forEach((l) => l.dispose());
      outlineMat.dispose();
      // The mark's four, by name, because the traversal below only reaches a material that is
      // ON something: a viewer closed without a hole ever being marked never built the meshes
      // that carry these, and they would go out with the scene unreleased.
      [drillMat, drillAirMat, drillBoreMat, drillAirBoreMat].forEach((m) => m.dispose());
      rowMats.forEach(([face, wall]) => { face.dispose(); wall.dispose(); });
      originMats.forEach((m) => m.dispose());
      originCross.traverse((o) => (o as import('three').Mesh).geometry?.dispose());
      // Every geometry and material the loader built, walked from the objects themselves —
      // a GLB brings however many of each it feels like, and counting on two is how a
      // viewer leaks a texture per open.
      root.traverse((o) => {
        const m = o as import('three').Mesh;
        m.geometry?.dispose?.();
        const mat = m.material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
        else mat?.dispose?.();
      });
      if (shadowFloor) {
        shadowFloor.sun.shadow.dispose();
        shadowFloor.floor.geometry.dispose();
        (shadowFloor.floor.material as import('three').Material).dispose();
      }
      scene.environment = null;
      lighting.dispose();
      measurer?.end();
      trace?.stop();
      // The context back to the browser, not merely the buffers — see releaseContext. A shared
      // one goes back when its last view does (see sharedRenderer).
      giveBack();
      surface.remove();
    };
  });

  return () => {
    alive = false;
    // In the load log as it happens: a page killed on the next screen shows whether this one had
    // let go of its context yet.
    load?.say(`released ${since()}`);
    cleanup?.();
    load?.stop();
  };
}
